import React, { useState, useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { Browser } from '@capacitor/browser';
import { Book, Upload, DollarSign, User, LogOut, Search, Award, Star, Mail, Lock, AlertCircle, CreditCard, CheckCircle, FileText, Download, Trash2, X, Plus, Menu, Play, Pause, Video, Edit, ClipboardList, Settings, Home, GraduationCap, Gift, BarChart3, Eye, Bookmark, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import jsPDF from 'jspdf';

// Firebase Configuration
const firebaseConfig = {
  apiKey: "AIzaSyAy-Hez-e1m8Z_JZiNIxFwz_q-SSHdIdjc",
  authDomain: "ceu-platform.firebaseapp.com",
  projectId: "ceu-platform",
  storageBucket: "ceu-platform.firebasestorage.app",
  messagingSenderId: "734423804205",
  appId: "1:734423804205:web:8fa498f12a1812f6cf4ba4"
};

// Firebase Auth, Firestore, Storage
let auth, db, storage;
let initialized = false;

// Maps lowercase IAP productId (e.g. 'course_abc123') to original Firestore document ID
const iapCourseMap = {};

// The active store platform: App Store on iOS, Google Play everywhere else.
const getIapPlatform = () =>
  window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'ios'
    ? window.CdvPurchase.Platform.APPLE_APPSTORE
    : window.CdvPurchase.Platform.GOOGLE_PLAY;

async function initializeFirebase() {
  if (initialized) return;
  
  const { initializeApp } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-app.js');
  const { getAuth, initializeAuth, indexedDBLocalPersistence, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInWithCredential, GoogleAuthProvider, OAuthProvider, sendPasswordResetEmail, deleteUser, reauthenticateWithCredential, EmailAuthProvider } =
    await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-auth.js');
  const { getFirestore, collection, doc, setDoc, getDoc, getDocs, query, where, addDoc, updateDoc, deleteDoc, onSnapshot } = 
    await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-firestore.js');
  const { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } = 
    await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-storage.js');
  const { getFunctions, httpsCallable} = 
    await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-functions.js');
  const { getAnalytics } = 
    await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-analytics.js');
  const { initializePerformance } = 
    await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-performance.js');
  const app = initializeApp(firebaseConfig);
  // On iOS, getAuth() hangs in WKWebView under the capacitor:// scheme (onAuthStateChanged
  // never fires its initial event) — initializeAuth with indexedDB persistence is the
  // documented fix. Android/web keep getAuth() so existing sessions aren't invalidated.
  let isIOS = false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    isIOS = Capacitor.getPlatform() === 'ios';
  } catch (e) {}
  auth = isIOS ? initializeAuth(app, { persistence: indexedDBLocalPersistence }) : getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  const functions = getFunctions(app);
  const analytics = getAnalytics(app);
  const performance = initializePerformance(app);
  
  initialized = true;
  
  window.firebaseAuth = {
    createUser: (email, password) => createUserWithEmailAndPassword(auth, email, password),
    signIn: (email, password) => signInWithEmailAndPassword(auth, email, password),
    signOut: () => signOut(auth),
    onAuthChange: (callback) => onAuthStateChanged(auth, callback),
    signInWithCredential: (credential) => signInWithCredential(auth, credential),
    googleCredential: (idToken) => GoogleAuthProvider.credential(idToken),
    appleCredential: (idToken, rawNonce) => new OAuthProvider('apple.com').credential({ idToken, rawNonce }),
    sendPasswordReset: (email) => sendPasswordResetEmail(auth, email),
    deleteCurrentUser: () => deleteUser(auth.currentUser),
    reauthWithPassword: (password) => reauthenticateWithCredential(auth.currentUser, EmailAuthProvider.credential(auth.currentUser.email, password)),
    reauthWithGoogle: async (idToken) => reauthenticateWithCredential(auth.currentUser, GoogleAuthProvider.credential(idToken)),
    getCurrentUser: () => auth.currentUser
  };
  
  window.firebaseDB = {
    setDoc: (path, data) => setDoc(doc(db, path), data),
    getDoc: (path) => getDoc(doc(db, path)),
    getDocs: (collectionPath) => getDocs(collection(db, collectionPath)),
    addDoc: (collectionPath, data) => addDoc(collection(db, collectionPath), data),
    updateDoc: (path, data) => updateDoc(doc(db, path), data),
    deleteDoc: (path) => deleteDoc(doc(db, path)),
    query: (collectionPath, ...queryConstraints) => {
      const q = query(collection(db, collectionPath), ...queryConstraints);
      return getDocs(q);
    },
    where: where,
    onSnapshot: (path, callback) => onSnapshot(doc(db, path), callback)
  };
  
  window.firebaseStorage = {
    uploadFile: (path, file, onProgress) => {
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) onProgress(progress);
        },
        (error) => console.error('Upload error:', error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          return downloadURL;
        }
      );
      
      return uploadTask;
    },
    getDownloadURL: (path) => getDownloadURL(ref(storage, path)),
    deleteFile: (path) => deleteObject(ref(storage, path))
  };
window.firebaseFunctions = {
    call: (functionName, data) => {
      const callable = httpsCallable(functions, functionName);
      return callable(data);
    }
  };
}

// Error Boundary for Crashlytics
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details (Crashlytics auto-captures console.error)
    console.error('🚨 App Error Caught:', error);
    console.error('📍 Error Info:', errorInfo);
    console.error('📚 Component Stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI when app crashes
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="text-center">
              <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Oops! Something went wrong
              </h2>
              <p className="text-gray-600 mb-6">
                We've been notified and are working on a fix. Please refresh the page to continue.
              </p>

              <button
                onClick={() => window.location.reload()}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ALL_US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];
const ALL_STATE_CODES = ALL_US_STATES.map(s => s.code);

function CEUPlatform() {
  const [currentScreen, setCurrentScreen] = useState('login');
const [authChecking, setAuthChecking] = useState(true);
  const [studentTab, setStudentTab] = useState('browse');
  const [providerTab, setProviderTab] = useState('dashboard');
  const [adminTab, setAdminTab] = useState('dashboard');
const [adminReviewTab, setAdminReviewTab] = useState('courses'); // courses or providers
const [adminFinanceTab, setAdminFinanceTab] = useState('payouts'); // payouts, refunds, 1099, reconciliation
  const [reconciliationSourceFilter, setReconciliationSourceFilter] = useState('all');
  const [reconciliationDateFrom, setReconciliationDateFrom] = useState('');
  const [reconciliationDateTo, setReconciliationDateTo] = useState('');
  const [payoutDateFilter, setPayoutDateFilter] = useState('all');
  const [payoutSearch, setPayoutSearch] = useState('');
const [payoutSourceFilter, setPayoutSourceFilter] = useState('all');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('ACH');
  const [payoutReference, setPayoutReference] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [selected1099Year, setSelected1099Year] = useState(new Date().getFullYear());
  const [adminDataLoading, setAdminDataLoading] = useState(false);
  const [adminCourseSearch, setAdminCourseSearch] = useState('');
  const [adminCourseProfessionFilter, setAdminCourseProfessionFilter] = useState('all');
  const [adminCourseStatusFilter, setAdminCourseStatusFilter] = useState('all');
  const [adminCourseSort, setAdminCourseSort] = useState('recent');
  const [adminProviderSearch, setAdminProviderSearch] = useState('');
  const [adminProviderProfessionFilter, setAdminProviderProfessionFilter] = useState('all');
  const [adminProviderStatusFilter, setAdminProviderStatusFilter] = useState('all');
  const [adminProviderSort, setAdminProviderSort] = useState('recent');
  const [adminStudentSearch, setAdminStudentSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [adminStudentStatusFilter, setAdminStudentStatusFilter] = useState('all');
  const [showSuspendProviderModal, setShowSuspendProviderModal] = useState(false);
  const [suspendProviderReason, setSuspendProviderReason] = useState('');
  const [suspendingProvider, setSuspendingProvider] = useState(false);
  const [showReinstateProviderModal, setShowReinstateProviderModal] = useState(false);
  const [showBanStudentModal, setShowBanStudentModal] = useState(false);
  const [showUnbanStudentModal, setShowUnbanStudentModal] = useState(false);
  const [banStudentReason, setBanStudentReason] = useState('');
  const [banningStudent, setBanningStudent] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpForm, setHelpForm] = useState({ name: '', email: '', message: '' });
  const [toasts, setToasts] = useState([]);


// ===================================================================
// TOAST NOTIFICATION SYSTEM
// ===================================================================
const showToast = (message, type = 'info') => {
  const id = Date.now();
  const newToast = { id, message, type };
  setToasts(prev => {
    const updated = [...prev, newToast];
    // Keep only the last 3 toasts to prevent crowding
    return updated.slice(-3);
  });
  
  // Auto-remove after 5 seconds
  setTimeout(() => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, 5000);
};

const removeToast = (id) => {
  setToasts(prev => prev.filter(t => t.id !== id));
};


// ===================================================================
// ===================================================================
// HELP FORM HANDLER
// ===================================================================
const handleHelpSubmit = async () => {
  if (!helpForm.name || !helpForm.email || !helpForm.message) {
    showToast('Please fill in all fields', 'error');
    return;
  }
  
  try {
    // Save to Firestore
    await window.firebaseDB.addDoc('helpRequests', {
      ...helpForm,
      userId: currentUser?.uid,
      userType: currentUser?.userType,
      createdAt: new Date(),
      status: 'pending'
    });

    // Notify admin via email
    try {
      await window.firebaseDB.addDoc('mail', {
        to: adminSettings?.notificationEmail || 'support@renewceu.com',
        message: {
          subject: '📬 New Support Request - RenewCEU',
          text: `A student has submitted a support request:\n\nName: ${helpForm.name}\nEmail: ${helpForm.email}\nMessage: ${helpForm.message}\n\nLog in to the admin dashboard to view and respond.`
        }
      });
    } catch (emailError) {
      console.error('Email notification failed:', emailError);
    }

    showToast('Help request submitted successfully!', 'success');
    setShowHelpModal(false);
    setHelpForm({ name: '', email: '', message: '' });
  } catch (error) {
    console.error('Error submitting help request:', error);
    showToast('Failed to submit help request', 'error');
  }
};

// YOUTUBE PLAYER COMPONENT
// ===================================================================
const YouTubePlayer = ({ video, course, onClose }) => {
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const saveIntervalRef = useRef(null);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    if (!window.YT || !video.youtubeVideoId) return;

    const initPlayer = () => {
      playerInstanceRef.current = new window.YT.Player(playerRef.current, {
        videoId: video.youtubeVideoId,
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          fs: 1,
          playsinline: 1
        },
        events: {
          onReady: async (event) => {
            // Load saved position
            const savedPosition = await loadVideoProgress(video.id, course.id);
            if (savedPosition > 0) {
              event.target.seekTo(savedPosition, true);
            }
            
            // Start auto-save interval (every 10 seconds)
            saveIntervalRef.current = setInterval(async () => {
              if (playerInstanceRef.current && playerInstanceRef.current.getPlayerState() === 1) {
                const currentTime = playerInstanceRef.current.getCurrentTime();
                const duration = playerInstanceRef.current.getDuration();
                await saveVideoProgress(video.id, course.id, currentTime, duration);
              }
            }, 10000);
          },
          onStateChange: async (event) => {
            // Paused (2) or Ended (0)
            if (event.data === 2 || event.data === 0) {
              const currentTime = playerInstanceRef.current.getCurrentTime();
              const duration = playerInstanceRef.current.getDuration();
              await saveVideoProgress(video.id, course.id, currentTime, duration);
              
              // If ended
if (event.data === 0) {
  const key = `${course.id}_${video.id}`;
  if (!watchedVideos[key] && !hasCompleted) {
    setHasCompleted(true);
    await handleVideoEnded(video.id, course.id);
  }
}
            }
          }
        }
      });
    };

    if (window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
      }
    };
  }, [video.youtubeVideoId]);

  return (
    <div>
      <div ref={playerRef} className="w-full aspect-video"></div>
      <button
        onClick={onClose}
        className="mt-3 text-white text-sm hover:underline"
      >
        Close Video
      </button>
    </div>
  );
};

  // ===================================================================
// YOUTUBE PLAYER API LOADER
// ===================================================================
useEffect(() => {
  // Check if already loaded
  if (window.YT) return;
  
  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  const firstScriptTag = document.getElementsByTagName('script')[0];
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  
  window.onYouTubeIframeAPIReady = () => {
  };
}, []);

  // ===================================================================
// ANALYTICS TRACKING SYSTEM
// ===================================================================
const trackEvent = async (eventData) => {
  if (!window.firebaseDB) return;
  
  try {
    const event = {
      ...eventData,
      timestamp: new Date().toISOString(),
      sessionId: sessionStorage.getItem('sessionId') || generateSessionId()
    };
    
    await window.firebaseDB.addDoc('analytics_events', event);
     
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

const generateSessionId = () => {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('sessionId', sessionId);
  return sessionId;
};
  const [loading, setLoading] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [profession, setProfession] = useState('massage-therapy');
  const [providerNumber, setProviderNumber] = useState('');
  const [providerExpiration, setProviderExpiration] = useState('');
  const [governingBody, setGoverningBody] = useState(''); // Generic accrediting body field
  const [credentialNotes, setCredentialNotes] = useState(''); // Additional credentials/notes
  const [licenseNumber, setLicenseNumber] = useState('');
  const [userType, setUserType] = useState('student');
  const [studentProfession, setStudentProfession] = useState('');
  const [studentCustomProfession, setStudentCustomProfession] = useState('');
  const [licenseExpirationDate, setLicenseExpirationDate] = useState('');
  const [licenseEditDate, setLicenseEditDate] = useState('');
  const [error, setError] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfession, setSelectedProfession] = useState('all');
  const [ceuFilter, setCeuFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [ceuOnlyFilter, setCeuOnlyFilter] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [hasUserSorted, setHasUserSorted] = useState(false);
  const [selectedState, setSelectedState] = useState('all');
  
  const [courses, setCourses] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [showProfessionManager, setShowProfessionManager] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [showW9Form, setShowW9Form] = useState(false);
  const [adminSettings, setAdminSettings] = useState({
    notificationEmail: '',
    defaultCommission: 20,
    appStoreCutRate: 15,
    providerEnrollmentOpen: true,
    platformName: 'RenewCEU',
    emailFrequency: 'daily',
    notifyNewCEUCourse: true,
    notifyNewProvider: true,
    notifyEducationalCourse: false,
    notifyCoursePurchase: true,
    notifyRefundRequest: true,
    termsOfServiceURL: '',
    privacyPolicyURL: '',
    refundPolicyURL: '',
    contactSupportEmail: '',
    certificateExpirationYears: 0,
    certificateSignatureURL: '',
    certificateLogoURL: ''
  });
  const [analyticsData, setAnalyticsData] = useState({
    totalUsers: 0,
    mau: 0,
    dau: 0,
    totalCourses: 0,
    totalEnrollments: 0,
    totalRevenue: 0,
    topCourses: [],
    recentActivity: [],
    conversionRate: 0
  });
  const [allStudents, setAllStudents] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [allEnrollments, setAllEnrollments] = useState([]);
  // pendingProviders state removed - providers are now immediately active
  const [pendingCourses, setPendingCourses] = useState([]);
  const [inspectingCourse, setInspectingCourse] = useState(null);
  const [inspectingCertSettings, setInspectingCertSettings] = useState(undefined); // undefined = loading, null = none found
  const [inspectingExpandedVideo, setInspectingExpandedVideo] = useState(null);
  const [editingCommission, setEditingCommission] = useState(false);
  const [commissionInput, setCommissionInput] = useState('');
  const [savingCommission, setSavingCommission] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvingCourse, setApprovingCourse] = useState(null);
  const [approvalCommission, setApprovalCommission] = useState(20);
  const [earningsViewMonth, setEarningsViewMonth] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  const [expandedPayoutBatch, setExpandedPayoutBatch] = useState(null);
  const [previewingQuiz, setPreviewingQuiz] = useState(null);
  const [takingQuizAsAdmin, setTakingQuizAsAdmin] = useState(null);
  const [adminQuizAnswers, setAdminQuizAnswers] = useState({});
  const [adminQuizResults, setAdminQuizResults] = useState(null);
  const [rejectingCourse, setRejectingCourse] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [allTransactions, setAllTransactions] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [providerCourses, setProviderCourses] = useState([]);
  const [editingCourseId, setEditingCourseId] = useState(null); // For inline editing
  const [expandedCourseId, setExpandedCourseId] = useState(null); // For accordion collapse/expand
  
  // Marketplace Banner states
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [showProviderSettings, setShowProviderSettings] = useState(false);
  const [updatingCredentials, setUpdatingCredentials] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [newCourse, setNewCourse] = useState({
    title: '',
    profession: 'Massage Therapist',
    hours: '',
    price: '',
    description: '',
    isCEU: true,
    courseType: 'ceu',
    featuredImage: '',
    approvalEntries: [{ governingBody: '', approvalNumber: '', expirationDate: '' }],
    approvedStates: ALL_STATE_CODES,
  });
    
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [courseFiles, setCourseFiles] = useState({});
  
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
const [addingYoutubeVideo, setAddingYoutubeVideo] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');
  const [courseVideos, setCourseVideos] = useState({});
  const [watchedVideos, setWatchedVideos] = useState({});
  const [selectedCourseForDetail, setSelectedCourseForDetail] = useState(null);
const [showCourseDetailModal, setShowCourseDetailModal] = useState(false);
// State for viewing enrolled course modal
  const [viewingEnrolledCourse, setViewingEnrolledCourse] = useState(null);
  const [expandedVideoId, setExpandedVideoId] = useState(null);
const [bookmarkedCourses, setBookmarkedCourses] = useState([]);
const [allCourses, setAllCourses] = useState([]);
 const [purchasingCourse, setPurchasingCourse] = useState(false);
const [pendingPurchaseCourse, setPendingPurchaseCourse] = useState(null);
const [purchaseAckChecked, setPurchaseAckChecked] = useState(false);
const [iapReady, setIapReady] = useState(false);
const materialFileInputRef = useRef(null);
const signatureFileInputRef = useRef(null);
const iapInitializedRef = useRef(false);
// True while an account is being created (email signup or Google/Apple onboarding).
// createUser()/signInWithCredential() sign the user in BEFORE the Firestore user doc
// is written, which fires onAuthChange with a doc-less user. This ref tells the
// onAuthChange "no doc" branch to stand down so it doesn't sign the user out mid-signup.
const signupInProgress = useRef(false);
// videoWatchTime is intentionally frozen as an empty object — retained only for the
// read inside saveVideoProgress; no setter exists.
const [videoWatchTime] = useState({});
  
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [courseQuizzes, setCourseQuizzes] = useState({});
  const [showQuizBuilder, setShowQuizBuilder] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState({
    passingScore: 70,
    quizFormat: 'all-on-page',
    questions: []
  });
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question: '',
    hasImage: false,
    imageFile: null,
    imageURL: null,
    options: ['', ''],
    correctAnswer: 0
  });
  const [quizImageUploadProgress, setQuizImageUploadProgress] = useState(0);
  const [uploadingQuizImage, setUploadingQuizImage] = useState(false);
  
  const [takingQuiz, setTakingQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [materialProgress, setMaterialProgress] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [studentQuizAttempts, setStudentQuizAttempts] = useState({});
  const [showRetakeWarning, setShowRetakeWarning] = useState(false);
  
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [showCertificateSettings, setShowCertificateSettings] = useState(null);
  const [courseCredentials, setCourseCredentials] = useState({});
  const [courseSignatures, setCourseSignatures] = useState({});
  const [signatureFile, setSignatureFile] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [signatureUploadProgress, setSignatureUploadProgress] = useState(0);
  const [certificates, setCertificates] = useState({});
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState(null);
  // Rating System States
const [showRatingPrompt, setShowRatingPrompt] = useState(false);
const [ratingCourseId, setRatingCourseId] = useState(null);
const [selectedRating, setSelectedRating] = useState(0);
const [hoverRating, setHoverRating] = useState(0);
const [submittingRating, setSubmittingRating] = useState(false);
const [userRatings, setUserRatings] = useState({});
const [ratingPromptCounts, setRatingPromptCounts] = useState({});
const [showManualRating, setShowManualRating] = useState(false);
const [manualRatingCourseId, setManualRatingCourseId] = useState(null);
const [organizations, setOrganizations] = useState([]);
const [showCreateOrgCourse, setShowCreateOrgCourse] = useState(false);
const [showGenerateCodes, setShowGenerateCodes] = useState(false);
const [showRedeemModal, setShowRedeemModal] = useState(false);
const [redemptionCodeInput, setRedemptionCodeInput] = useState('');
const [redeeming, setRedeeming] = useState(false);
const [codeGeneration, setCodeGeneration] = useState({
  courseId: '',
  prefix: '',
  quantity: 50,
  expiresAt: '',
  discountType: 'free',
  discountValue: 100
});
const [orgCourseForm, setOrgCourseForm] = useState({
  title: '',
  description: '',
  organizationId: '',
  hours: 0,
  isCEU: true
});
const [creatingOrgCourse, setCreatingOrgCourse] = useState(false);
const [showOrgManager, setShowOrgManager] = useState(false);
const [selectedOrg, setSelectedOrg] = useState(null);
const [orgDetailTab, setOrgDetailTab] = useState('codes');
const [orgCodes, setOrgCodes] = useState([]);
const [orgCourses, setOrgCourses] = useState([]);
const [selectedCode, setSelectedCode] = useState(null);
const [selectedCodeRedemptions, setSelectedCodeRedemptions] = useState([]);
const [loadingCodeRedemptions, setLoadingCodeRedemptions] = useState(false);
const [expandedBatches, setExpandedBatches] = useState({});
const [editingOrgForm, setEditingOrgForm] = useState({ name: '', contactName: '', contactEmail: '', status: 'active' });
const [savingOrgSettings, setSavingOrgSettings] = useState(false);
const [newOrgForm, setNewOrgForm] = useState({ name: '', contactEmail: '', contactName: '' });
const [creatingOrg, setCreatingOrg] = useState(false);
  // Admin Management
const [allAdmins, setAllAdmins] = useState([]);
const [showAdminManager, setShowAdminManager] = useState(false);
const [newAdminEmail, setNewAdminEmail] = useState('');
const [newAdminName, setNewAdminName] = useState('');

const [invitingAdmin, setInvitingAdmin] = useState(false);

// Activity Log
const [activityLog, setActivityLog] = useState([]);
const [showActivityLog, setShowActivityLog] = useState(false);
const [showAddNoteModal, setShowAddNoteModal] = useState(false);
const [generalNote, setGeneralNote] = useState('');
const [generalNoteTitle, setGeneralNoteTitle] = useState('');
const [expandedLogId, setExpandedLogId] = useState(null);
const [resolvingLogId, setResolvingLogId] = useState(null);
// Transaction filters
const [filterProvider, setFilterProvider] = useState('all');
const [filterCourse, setFilterCourse] = useState('all');
const [filterStatus, setFilterStatus] = useState('all');
const [filterSource, setFilterSource] = useState('all');
const [filterDateFrom, setFilterDateFrom] = useState('');
const [filterDateTo, setFilterDateTo] = useState('');

// Payout checkboxes
const [selectedTransactions, setSelectedTransactions] = useState([]);

// Global Search
const [showGlobalSearch, setShowGlobalSearch] = useState(false);

// Email System
const [showEmailComposer, setShowEmailComposer] = useState(false);

  const [notificationPermission, setNotificationPermission] = useState('default');
  
  const demoCourses = [];
  // ===================================================================
  // ANDROID BACK BUTTON HANDLER
  // ===================================================================
  useEffect(() => {
    let listenerHandle;
    
    const setupListener = async () => {
      listenerHandle = await CapacitorApp.addListener('backButton', () => {
        if (pendingPurchaseCourse) {
          setPendingPurchaseCourse(null);
          setPurchaseAckChecked(false);
          return;
        }
        if (showDeleteAccountModal) {
          setShowDeleteAccountModal(false);
          setDeleteAccountPassword('');
          return;
        }
        if (currentScreen === 'google-account-type') {
          setPendingGoogleUser(null);
          setCurrentScreen('login');
          return;
        }
        // Priority 1: Close expanded videos FIRST
        if (inspectingExpandedVideo) {
          setInspectingExpandedVideo(null);
          return;
        }
        if (expandedVideoId) {
          setExpandedVideoId(null);
          return;
        }
        
        // Priority 2: Close modals
        if (inspectingCourse) {
          setInspectingCourse(null);
          return;
        }
        if (viewingEnrolledCourse) {
          setViewingEnrolledCourse(null);
          return;
        }
        if (showCourseDetailModal) {
          setShowCourseDetailModal(false);
          setSelectedCourseForDetail(null);
          return;
        }
        if (showCreateCourse) {
          setShowCreateCourse(false);
          setEditingCourse(null);
          return;
        }
        if (showProviderSettings) {
          setShowProviderSettings(false);
          return;
        }
        if (showHelpModal) {
          setShowHelpModal(false);
          return;
        }
        if (showRedeemModal) {
          setShowRedeemModal(false);
          return;
        }
        if (showQuizBuilder) {
          setShowQuizBuilder(null);
          return;
        }
        if (showCertificateSettings) {
          setShowCertificateSettings(null);
          return;
        }
        if (showRetakeWarning) {
          setShowRetakeWarning(false);
          return;
        }
        if (showEditWarning) {
          setShowEditWarning(false);
          return;
        }
        if (showRatingPrompt) {
          setShowRatingPrompt(false);
          return;
        }
        if (showManualRating) {
          setShowManualRating(false);
          return;
        }
        if (showCreateOrgCourse) {
          setShowCreateOrgCourse(false);
          return;
        }
        if (showGenerateCodes) {
          setShowGenerateCodes(false);
          return;
        }
        if (showOrgManager) {
          setShowOrgManager(false);
          return;
        }
        if (showAdminManager) {
          setShowAdminManager(false);
          return;
        }
        if (showActivityLog) {
          setShowActivityLog(false);
          return;
        }
        if (showAddNoteModal) {
          setShowAddNoteModal(false);
          return;
        }
        if (showProfessionManager) {
          setShowProfessionManager(false);
          return;
        }
        if (showAdminSettings) {
          setShowAdminSettings(false);
          return;
        }
        if (showW9Form) {
          setShowW9Form(false);
          return;
        }
        if (showGlobalSearch) {
          setShowGlobalSearch(false);
          return;
        }
        if (showEmailComposer) {
          setShowEmailComposer(false);
          return;
        }
        // Priority 2: Navigate back from sub-screens to main screen
        if (currentUser?.userType === 'student') {
          if (studentTab !== 'browse') {
            setStudentTab('browse');
            setHasUserSorted(false);
            return;
          }
        }
        
        if (currentUser?.userType === 'provider') {
          if (providerTab !== 'dashboard') {
            setProviderTab('dashboard');
            return;
          }
        }
        
        if (currentUser?.userType === 'admin') {
          if (adminTab !== 'dashboard') {
            setAdminTab('dashboard');
            return;
          }
        }
        
        // Priority 3: Exit app if on main screen
        CapacitorApp.exitApp();
      });
    };
    
    setupListener();
    
    // Cleanup
    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [
    inspectingCourse,
    viewingEnrolledCourse,
    expandedVideoId,
    inspectingExpandedVideo,
    showCourseDetailModal,
    showCreateCourse,
    showProviderSettings,
    showHelpModal,
    showRedeemModal,
    showQuizBuilder,
    showCertificateSettings,
    showRetakeWarning,
    showEditWarning,
    showRatingPrompt,
    showManualRating,
    showCreateOrgCourse,
    showGenerateCodes,
    showOrgManager,
    showAdminManager,
    showActivityLog,
    showAddNoteModal,
    showProfessionManager,
    showAdminSettings,
    showW9Form,
    showGlobalSearch,
    showEmailComposer,
    studentTab,
    providerTab,
    adminTab,
    currentUser
  ]);
  
  // ===================================================================
  // PUSH NOTIFICATION FUNCTIONS (CAPACITOR NATIVE)
  // ===================================================================
  
  const requestNotificationPermission = async () => {
    try {
      // Import Capacitor Firebase Messaging
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      
      // Request permission
      const result = await FirebaseMessaging.requestPermissions();
      
      if (result.receive === 'granted') {
        setNotificationPermission('granted');
        return true;
      } else {
        setNotificationPermission('denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setNotificationPermission('denied');
      return false;
    }
  };
  
  const registerDeviceToken = async (userId) => {
    try {
      // Import Capacitor Firebase Messaging
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      
      // Request permission first
      const hasPermission = await requestNotificationPermission();
      if (!hasPermission) {
        showToast('Notification permission denied', 'error');
        return;
      }
      
      // Get FCM token
      const result = await FirebaseMessaging.getToken();
      const token = result.token;
      
      if (token) {
        // Detect platform
        const { Capacitor } = await import('@capacitor/core');
        const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
        
        // Store token in Firestore
        const tokenDoc = {
          userId: userId,
          token: token,
          createdAt: new Date().toISOString(),
          platform: platform,
          lastUpdated: new Date().toISOString()
        };
        
        await window.firebaseDB.setDoc(`device_tokens/${userId}`, tokenDoc);
        showToast('Push notifications enabled', 'success');
      }
    } catch (error) {
      console.error('Error registering device token:', error);
      showToast('Failed to enable push notifications', 'error');
    }
  };
  
  // Initialize FCM when user logs in
  useEffect(() => {
    if (currentUser?.uid) {
      // Set up foreground notification listener
      const setupNotificationListener = async () => {
        try {
          const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
          
          // Listen for foreground messages
          await FirebaseMessaging.addListener('notificationReceived', (notification) => {
            // Show toast for foreground messages
            if (notification.notification) {
              const title = notification.notification.title || 'Notification';
              const body = notification.notification.body || '';
              showToast(`${title}: ${body}`, 'info');
            }
          });
          
        } catch (error) {
        }
      };
      
      setupNotificationListener();
    }
  }, [currentUser?.uid]);
  
  useEffect(() => {
    const init = async () => {
      await initializeFirebase();

      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { StatusBar } = await import('@capacitor/status-bar');
          await StatusBar.setOverlaysWebView({ overlay: false });
        }
      } catch (e) {}

      loadProfessions();

      if (window.firebaseAuth) {
        window.firebaseAuth.onAuthChange(async (user) => {
          if (user) {
            const userDocRef = await window.firebaseDB.getDoc(`users/${user.uid}`);
            if (userDocRef.exists()) {
              const userData = userDocRef.data();

              if (userData.status === 'suspended' || userData.status === 'banned') {
                await window.firebaseAuth.signOut();
                showToast('Your account has been suspended. Please contact support at renewceuapp@gmail.com.', 'error');
                setCurrentScreen('login');
              } else {
                setCurrentUser({
                  uid: user.uid,
                  email: user.email,
                  ...userData
                });

                // Track login (auto-login via auth state)
                await window.firebaseDB.updateDoc(`users/${user.uid}`, {
                  lastLoginAt: new Date().toISOString(),
                  lastActiveAt: new Date().toISOString(),
                  loginCount: (userData.loginCount || 0) + 1
                });

                const screenToSet = userData.userType === 'student' ? 'student-browse' :
                                    userData.userType === 'admin' ? 'admin-dashboard' :
                                    'provider-dashboard';

                setCurrentScreen(screenToSet);

                if (userData.userType === 'student') {
                  loadEnrolledCourses(user.uid);
                  loadWatchedVideos(user.uid);
                  loadBookmarks();
                  await loadAllCourses();
                } else if (userData.userType === 'provider') {
                  loadProviderCourses(user.uid);
                } else if (userData.userType === 'admin') {
                  setAdminDataLoading(true);
                  try {
                    const fetchedCourses = await loadAllCourses();
                    await loadAllUsers();
                    const fetchedTransactions = await loadAllTransactions();
                    await loadAdminSettings();
                    const fetchedEnrollments = await loadAllEnrollments();
                    await loadAllAdmins();
                    await loadAllProviders();
                    await loadActivityLog();
                    await loadAnalyticsData(fetchedCourses, fetchedEnrollments, fetchedTransactions);
                    await loadOrganizations();
                  } catch (error) {
                    console.error('❌ ERROR loading admin data from auth listener:', error);
                  } finally {
                    setAdminDataLoading(false);
                  }
                }
              }
            } else if (!signupInProgress.current) {
              // Firebase Auth succeeded but no Firestore user doc exists for this UID.
              // Sign out cleanly — we don't know what account type they should be, and
              // onboarding is only for Google/Apple Sign In.
              //
              // Guarded by signupInProgress: during signup/social onboarding the user is
              // signed in before their doc is written, so a missing doc is expected and
              // must NOT trigger a sign-out. Do not convert this to an early `return` —
              // setAuthChecking(false) below must always run.
              await window.firebaseAuth.signOut();
              setCurrentUser(null);
              showToast('Account not found. Please sign up or contact support.', 'error');
              setCurrentScreen('login');
            }
          } else {
          setCurrentUser(null);
        }
        
        // Auth check complete - stop showing splash screen
        setAuthChecking(false);
      });
    }
  };
  
  init();
}, []);

 
useEffect(() => {
  if (currentUser?.licenseExpirationDate) {
    setLicenseEditDate(currentUser.licenseExpirationDate.split('T')[0]);
  }
}, [currentUser?.licenseExpirationDate]);

useEffect(() => {
  if (selectedOrg && orgDetailTab === 'codes') {
    loadOrgCodes(selectedOrg.id);
  }
}, [selectedOrg, orgDetailTab]);

useEffect(() => {
  if (selectedOrg && orgDetailTab === 'courses') {
    loadOrgCourses(selectedOrg.id);
  }
}, [selectedOrg, orgDetailTab]);

useEffect(() => {
  if (selectedOrg && orgDetailTab === 'settings') {
    setEditingOrgForm({
      name: selectedOrg.name,
      contactName: selectedOrg.contactName || '',
      contactEmail: selectedOrg.contactEmail || '',
      status: selectedOrg.status || 'active'
    });
  }
}, [selectedOrg, orgDetailTab]);

// Fetch certificate settings for the admin course inspection modal (read-only, raw doc)
useEffect(() => {
  if (!inspectingCourse?.id) {
    setInspectingCertSettings(undefined);
    return;
  }
  let cancelled = false;
  setInspectingCertSettings(undefined); // loading
  (async () => {
    try {
      if (!window.firebaseDB) return;
      const snapshot = await window.firebaseDB.query(
        'course_certificate_settings',
        window.firebaseDB.where('courseId', '==', inspectingCourse.id)
      );
      if (cancelled) return;
      setInspectingCertSettings(snapshot.docs.length > 0 ? snapshot.docs[0].data() : null);
    } catch (error) {
      console.error('❌ Error loading certificate settings for inspection:', error);
      if (!cancelled) setInspectingCertSettings(null);
    }
  })();
  return () => { cancelled = true; };
}, [inspectingCourse?.id]);

// Initialize Cordova IAP
useEffect(() => {
  if (typeof window.CdvPurchase === 'undefined') return;
  if (!allCourses || allCourses.length === 0) return;

  const store = window.CdvPurchase.store;

  // Register listeners and initialize only once
  if (!iapInitializedRef.current) {
    iapInitializedRef.current = true;

    store.when().approved(async (transaction) => {
      await handleApprovedPurchase(transaction);
      transaction.finish();
    });

    store.when().finished((transaction) => {});

    allCourses.forEach(course => {
      if (course.price > 0) {
        const productId = `course_${course.id.toLowerCase()}`;
        iapCourseMap[productId] = course.id;
        store.register({
          id: productId,
          type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
          platform: getIapPlatform()
        });
      }
    });

    store.initialize([getIapPlatform()]).then(() => {
      store.update().then(() => {
        setIapReady(true);
      });
    });
  } else {
    // Store already initialized — register any new courses and refresh
    allCourses.forEach(course => {
      if (course.price > 0) {
        const productId = `course_${course.id.toLowerCase()}`;
        iapCourseMap[productId] = course.id;
        store.register({
          id: productId,
          type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
          platform: getIapPlatform()
        });
      }
    });

    store.update();
  }
}, [allCourses]);

const handleApprovedPurchase = async (transaction) => {
  try {
    // Refetch live user from Firebase auth — avoids stale closure on currentUser state
    const liveUser = auth.currentUser;
    if (!liveUser) {
      console.error('handleApprovedPurchase: no authenticated user');
      return;
    }

    const productId = transaction.products[0].id;
    const originalCourseId = iapCourseMap[productId];

    if (!originalCourseId) {
      console.error('handleApprovedPurchase: productId not found in iapCourseMap:', productId);
      return;
    }

    // Fetch course directly from Firestore using original document ID
    const courseDoc = await window.firebaseDB.getDoc(`courses/${originalCourseId}`);
    if (!courseDoc.exists()) {
      console.error('Course not found for courseId:', originalCourseId);
      return;
    }
    const course = { id: originalCourseId, ...courseDoc.data() };

    // Check if already enrolled — skip write if so (guards against duplicate approved events)
    const existingEnrollments = await window.firebaseDB.query(
      'enrollments',
      window.firebaseDB.where('userId', '==', liveUser.uid),
      window.firebaseDB.where('courseId', '==', originalCourseId)
    );
    if (!existingEnrollments.empty) {
      await loadEnrolledCourses(liveUser.uid);
      setPurchasingCourse(false);
      return;
    }

    // Check if this transactionId was already used by any user — guards against
    // CdvPurchase re-delivering a previous user's NON_CONSUMABLE on a new login
    if (transaction.transactionId) {
      const existingByTransaction = await window.firebaseDB.query(
        'enrollments',
        window.firebaseDB.where('transactionId', '==', transaction.transactionId)
      );
      if (!existingByTransaction.empty) {
        setPurchasingCourse(false);
        return;
      }
    }

    // Get current app store cut rate from settings (default 15%)
    const appStoreCutRate = adminSettings.appStoreCutRate || 15;

    // Calculate amounts step by step
    const grossAmount = course.price;
    const appStoreCutAmount = grossAmount * (appStoreCutRate / 100);
    const netRevenue = grossAmount - appStoreCutAmount;

    const platformCommissionRate = course.commission !== undefined && course.commission !== null ? course.commission : 20;
    const platformCommission = netRevenue * (platformCommissionRate / 100);
    const providerEarnings = netRevenue - platformCommission;

    // Detect payment source (Apple or Google)
    const paymentSource = transaction.platform === 'android-playstore' ? 'google' : 'apple';

    // Create enrollment
    await window.firebaseDB.addDoc('enrollments', {
      userId: liveUser.uid,
      courseId: course.id,
      courseTitle: course.title,
      enrolledAt: new Date().toISOString(),
      paymentMethod: 'iap',
      transactionId: transaction.transactionId,
      price: course.price,
      paid: true,
      completed: false,
      progress: 0
    });

    // Increment student count on the course document
    await window.firebaseDB.updateDoc(`courses/${course.id}`, {
      students: (course.students || 0) + 1
    });

    // Track transaction for provider payout
    await window.firebaseDB.addDoc('transactions', {
      courseId: course.id,
      courseTitle: course.title,
      studentId: liveUser.uid,
      studentName: currentUser?.fullName || liveUser.displayName || '',
      providerId: course.providerId,
      providerName: course.provider,

      // Amounts
      amount: grossAmount,
      appStoreCutRate: appStoreCutRate,
      appStoreCutAmount: appStoreCutAmount,
      netRevenue: netRevenue,

      // Commission
      commission: platformCommissionRate,
      platformCommission: platformCommission,
      providerEarnings: providerEarnings,

      // Payment details
      paymentMethod: 'iap',
      paymentSource: paymentSource,
      transactionId: transaction.transactionId,
      appStoreTransactionId: transaction.transactionId,

      // Status tracking
      status: 'pending',
      providerPaid: false,
      providerPaidDate: null,
      processorCleared: false,
      processorClearedAt: null,

      // Timestamps
      purchaseDate: new Date().toISOString(),

      // Refund tracking
      refunded: false,
      refundSource: null
    });

    showToast('Purchase successful! You are now enrolled.', 'success');
    await loadEnrolledCourses(liveUser.uid);
    setPurchasingCourse(false);

  } catch (error) {
    console.error('Error processing purchase:', error);
    showToast('Purchase completed but enrollment failed. Please contact support.', 'error');
    setPurchasingCourse(false);
  }
};

const handlePurchaseCourse = async (course) => {
  if (!iapReady) {
    showToast('Payment system not ready. Please try again in a moment.', 'error');
    return;
  }
  if (typeof window.CdvPurchase === 'undefined') {
    showToast('In-app purchases are only available in the mobile app.', 'error');
    return;
  }

  try {
    const productId = `course_${course.id.toLowerCase()}`;
    const store = window.CdvPurchase.store;
    const product = store.get(productId, getIapPlatform());

    if (!product) {
      showToast('Product not found: ' + productId, 'error');
      return;
    }

    const offer = product.offers[0];
    setPendingPurchaseCourse({ course, offer });
    setPurchaseAckChecked(false);

  } catch (error) {
    console.error('Purchase error:', error);
    showToast('Purchase failed: ' + error.message, 'error');
  }
};

const confirmPurchase = async () => {
  if (!pendingPurchaseCourse) return;
  const { offer } = pendingPurchaseCourse;
  setPendingPurchaseCourse(null);
  setPurchaseAckChecked(false);
  setShowCourseDetailModal(false);
  setPurchasingCourse(true);
  try {
    await window.CdvPurchase.store.order(offer);
  } catch (error) {
    console.error('Purchase error:', error);
    showToast('Purchase failed: ' + error.message, 'error');
    setPurchasingCourse(false);
  }
};
  
  const loadEnrolledCourses = async (userId) => {
    try {
      const enrollmentsRef = await window.firebaseDB.query(
        'enrollments',
        window.firebaseDB.where('userId', '==', userId)
      );
      
      const enrollmentDocs = enrollmentsRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEnrolledCourses(enrollmentDocs);
      
      for (const enrollment of enrollmentDocs) {
        await loadCourseFiles(enrollment.courseId);
        await loadCourseVideos(enrollment.courseId);
        await loadCourseQuiz(enrollment.courseId);
        await loadMaterialProgress(userId, enrollment.courseId);
        await loadCourseCertificateSettings(enrollment.courseId);
      }
         
      await loadStudentQuizAttempts(userId);
      await loadUserRatings(userId);
await loadRatingPromptCounts(userId);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const initializeDefaultProfessions = async () => {
    const defaultProfessions = [
      {
        id: 'massage-therapy',
        name: 'Massage Therapy',
        active: true,
        accreditingBody: 'NCBTMB',
        providerNumberLabel: 'NCBTMB Provider Number',
        courseApprovalLabel: 'NCBTMB Course Approval Number',
        creditLabel: 'CEU Hours',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      },
      {
        id: 'personal-training',
        name: 'Personal Training',
        active: true,
        accreditingBody: 'NASM/ACE/ACSM/NSCA',
        providerNumberLabel: 'Certification Number',
        courseApprovalLabel: 'Course Approval Number',
        creditLabel: 'CECs',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      },
      {
        id: 'yoga',
        name: 'Yoga Instruction',
        active: true,
        accreditingBody: 'Yoga Alliance',
        providerNumberLabel: 'RYT Number',
        courseApprovalLabel: 'YACEP Course Number',
        creditLabel: 'Contact Hours',
        requiresProviderNumber: true,
        requiresCourseApproval: false,
        requiresExpiration: true
      },
      {
        id: 'acupuncture',
        name: 'Acupuncture',
        active: false,
        accreditingBody: 'NCCAOM',
        providerNumberLabel: 'NCCAOM Provider Number',
        courseApprovalLabel: 'PDA Approval Number',
        creditLabel: 'PDA Points',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      },
      {
        id: 'physical-therapy',
        name: 'Physical Therapy',
        active: false,
        accreditingBody: 'State Boards',
        providerNumberLabel: 'PT License Number',
        courseApprovalLabel: 'State Course Approval',
        creditLabel: 'Contact Hours',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      },
      {
        id: 'nursing',
        name: 'Nursing (RN/LPN)',
        active: false,
        accreditingBody: 'State Boards',
        providerNumberLabel: 'Nursing License Number',
        courseApprovalLabel: 'Course ID',
        creditLabel: 'Contact Hours',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      },
      {
        id: 'chiropractic',
        name: 'Chiropractic',
        active: false,
        accreditingBody: 'State Boards',
        providerNumberLabel: 'Chiropractic License',
        courseApprovalLabel: 'State Course Approval',
        creditLabel: 'CE Hours',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      },
      {
        id: 'occupational-therapy',
        name: 'Occupational Therapy',
        active: false,
        accreditingBody: 'NBCOT',
        providerNumberLabel: 'NBCOT Number',
        courseApprovalLabel: 'Course Approval Number',
        creditLabel: 'CEU Hours',
        requiresProviderNumber: true,
        requiresCourseApproval: true,
        requiresExpiration: true
      }
    ];
    
    try {
      for (const profession of defaultProfessions) {
        await window.firebaseDB.setDoc(`professions/${profession.id}`, profession);
      }
    } catch (error) {
      console.error('Error initializing professions:', error);
    }
  };

  const loadProviderCourses = async (providerId) => {
    try {
      const coursesRef = await window.firebaseDB.query(
        'courses',
        window.firebaseDB.where('providerId', '==', providerId)
      );
      
      const courseDocs = coursesRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setProviderCourses(courseDocs);
      setCourses(courseDocs);
      
      for (const course of courseDocs) {
        await loadCourseFiles(course.id);
        await loadCourseVideos(course.id);
        await loadCourseQuiz(course.id);
        await loadCourseCertificateSettings(course.id);
      }
    } catch (error) {
      console.error('Error loading provider courses:', error);
    }
  };

  const loadProfessions = async () => {
    try {
      const professionsSnapshot = await window.firebaseDB.getDocs('professions');
      const professionsData = professionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (professionsData.length === 0) {
        await initializeDefaultProfessions();
        const newSnapshot = await window.firebaseDB.getDocs('professions');
        const newData = newSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProfessions(newData);
      } else {
        setProfessions(professionsData);
      }
    } catch (error) {
      console.error('Error loading professions:', error);
    }
  };

  const loadAllCourses = async () => {
    try {
      if (!window.firebaseDB) {
        console.error('❌ window.firebaseDB is undefined!');
        return;
      }

      const coursesRef = await window.firebaseDB.getDocs('courses');

      const courseDocs = coursesRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const pendingCEUCourses = courseDocs.filter(c => c.isCEU && c.status === 'pending');

      setCourses(courseDocs);
      setAllCourses(courseDocs);
      setPendingCourses(pendingCEUCourses);
      return courseDocs;
    } catch (error) {
      console.error('❌ ERROR in loadAllCourses():', error);
      console.error('❌ Error stack:', error.stack);
      return [];
    }
  };
  const loadAnalyticsData = async (coursesData = allCourses, enrollmentsData = allEnrollments, transactionsData = allTransactions) => {
  try {
    
    // Calculate date ranges
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const todayStart = new Date(now.setHours(0, 0, 0, 0));
    
    // Get all users for MAU/DAU calculation
    const usersRef = await window.firebaseDB.getDocs('users');
    const allUsersData = usersRef.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Calculate MAU (users active in last 30 days)
    const mau = allUsersData.filter(user => {
      if (!user.lastActiveAt) return false;
      const lastActive = new Date(user.lastActiveAt);
      return lastActive >= thirtyDaysAgo;
    }).length;
    
    // Calculate DAU (users active today)
    const dau = allUsersData.filter(user => {
      if (!user.lastActiveAt) return false;
      const lastActive = new Date(user.lastActiveAt);
      return lastActive >= todayStart;
    }).length;
    
    // Load analytics events
    const eventsRef = await window.firebaseDB.getDocs('analytics_events');
    const events = eventsRef.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Get course view counts
    const courseViews = {};
    events.filter(e => e.type === 'course_viewed').forEach(event => {
      courseViews[event.courseId] = (courseViews[event.courseId] || 0) + 1;
    });
    
    // Calculate top courses by views
    const topCourses = coursesData
      .map(course => ({
        ...course,
        views: courseViews[course.id] || 0,
        enrollments: enrollmentsData.filter(e => e.courseId === course.id).length,
        bookmarks: 0 // Will add bookmark count later
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Calculate total revenue
    const totalRevenue = Math.round(
      transactionsData
        .filter(t => !t.refunded)
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
      * 100) / 100;

    // Get recent activity (last 20 events)
    const recentActivity = events
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 20);

    // Calculate conversion rate
    const totalViews = Object.values(courseViews).reduce((sum, count) => sum + count, 0);
    const conversionRate = totalViews > 0 ? (enrollmentsData.length / totalViews * 100) : 0;

    setAnalyticsData({
      totalUsers: allUsersData.length,
      mau,
      dau,
      totalCourses: coursesData.length,
      totalEnrollments: enrollmentsData.length,
      totalRevenue,
      topCourses,
      recentActivity,
      conversionRate
    });
    
    
  } catch (error) {
    console.error('Error loading analytics:', error);
  }
};
  const exportAnalyticsToCSV = () => {
  try {
    // Prepare CSV data
    const csvRows = [];
    
    // Header
    csvRows.push('RenewCEU Analytics Report');
    csvRows.push(`Generated: ${new Date().toLocaleString()}`);
    csvRows.push('');
    
    // Platform Metrics
    csvRows.push('PLATFORM METRICS');
    csvRows.push('Metric,Value');
    csvRows.push(`Total Users,${analyticsData.totalUsers}`);
    csvRows.push(`Monthly Active Users (MAU),${analyticsData.mau}`);
    csvRows.push(`Daily Active Users (DAU),${analyticsData.dau}`);
    csvRows.push(`Total Courses,${analyticsData.totalCourses}`);
    csvRows.push(`Total Enrollments,${analyticsData.totalEnrollments}`);
    csvRows.push(`Total Revenue,$${analyticsData.totalRevenue.toFixed(2)}`);
    csvRows.push(`Conversion Rate,${analyticsData.conversionRate.toFixed(2)}%`);
    csvRows.push('');
    
    // Top Courses
    csvRows.push('TOP PERFORMING COURSES');
    csvRows.push('Rank,Course Title,Provider,Views,Enrollments,Revenue,Conversion Rate');
    analyticsData.topCourses.forEach((course, index) => {
      const revenue = (course.price * course.enrollments).toFixed(2);
      const conversion = course.views > 0 ? ((course.enrollments / course.views) * 100).toFixed(1) : 0;
      csvRows.push(`${index + 1},"${course.title}","${course.provider}",${course.views},${course.enrollments},$${revenue},${conversion}%`);
    });
    csvRows.push('');
    
    // User Breakdown
    csvRows.push('USER BREAKDOWN');
    csvRows.push('User Type,Count');
    const students = allUsers.filter(u => u.userType === 'student').length;
    const providers = allUsers.filter(u => u.userType === 'provider').length;
    const admins = allUsers.filter(u => u.userType === 'admin').length;
    csvRows.push(`Students,${students}`);
    csvRows.push(`Providers,${providers}`);
    csvRows.push(`Admins,${admins}`);
    csvRows.push('');
    
    // Create CSV string
    const csvContent = csvRows.join('\n');
    
    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `renewceu-analytics-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('Analytics report downloaded!', 'success');
    
  } catch (error) {
    console.error('Error exporting analytics:', error);
    showToast('Failed to export analytics', 'error');
  }
};
  const loadAllUsers = async () => {
    try {
      if (!window.firebaseDB) {
        console.error('❌ window.firebaseDB is undefined!');
        return;
      }

      const usersRef = await window.firebaseDB.getDocs('users');

      const userDocs = usersRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const students = userDocs.filter(u => u.userType === 'student');
      const providers = userDocs.filter(u => u.userType === 'provider');
      setAllStudents(students);
      // Provider approval logic removed - providers immediately active
    } catch (error) {
      console.error('❌ ERROR in loadAllUsers():', error);
      console.error('❌ Error stack:', error.stack);
    }
  };
  
  const loadAllTransactions = async () => {
    try {
      const transactionsRef = await window.firebaseDB.getDocs('transactions');

      const transactionDocs = transactionsRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAllTransactions(transactionDocs);
      return transactionDocs;
    } catch (error) {
      console.error('❌ ERROR in loadAllTransactions():', error);
      return [];
    }
  };

  const loadAllEnrollments = async () => {
    try {
      const enrollmentsRef = await window.firebaseDB.getDocs('enrollments');

      const enrollmentDocs = enrollmentsRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAllEnrollments(enrollmentDocs);
      return enrollmentDocs;
    } catch (error) {
      console.error('❌ ERROR in loadAllEnrollments():', error);
      return [];
    }
  };

  const loadAdminSettings = async () => {
    try {
      const settingsDoc = await window.firebaseDB.getDoc('admin_settings/main');

      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setAdminSettings(prev => ({ ...prev, ...settings }));
      }
    } catch (error) {
      console.error('❌ ERROR in loadAdminSettings():', error);
    }
  };

const approveCourse = async (courseId, commission = 20) => {
    try {
      const course = pendingCourses.find(c => c.id === courseId);

      // A paid course cannot go live without a valid price — an approved course
      // with no/zero price would have no purchasable store product behind it.
      if (!course.price || Number(course.price) <= 0) {
        showToast('Cannot approve: course must have a price greater than $0.', 'error');
        return;
      }

      // Get current app store cut rate
      const appStoreCutRate = adminSettings.appStoreCutRate || 15;
      
      // Calculate with app store cut
      const grossAmount = course.price;
      const appStoreCutAmount = grossAmount * (appStoreCutRate / 100);
      const netRevenue = grossAmount - appStoreCutAmount;
      
      // Platform commission and provider earnings from NET revenue
      const platformCommission = netRevenue * (commission / 100);
      const providerEarnings = netRevenue - platformCommission;
      
      await window.firebaseDB.updateDoc(`courses/${courseId}`, {
        status: 'approved',
        commission: commission,
        approvedAt: new Date().toISOString(),
        lastApprovedPrice: course.price
      });
      
      await logActivity('Approved Course', `Approved "${course.title}" by ${course.provider} with ${commission}% commission (${appStoreCutRate}% app store cut)`, 'content');
      
      if (course && course.providerId) {
        const providerDoc = await window.firebaseDB.getDoc(`users/${course.providerId}`);
        if (providerDoc.exists()) {
          const providerEmail = providerDoc.data().email;
          await sendEmail(
            providerEmail,
            `✅ Course Approved: ${course.title}`,
            `Congratulations! Your course "${course.title}" has been approved and is now live on RenewCEU.\n\nCourse Price: $${course.price}\nApp Store Cut (${appStoreCutRate}%): -$${appStoreCutAmount.toFixed(2)}\nNet Revenue: $${netRevenue.toFixed(2)}\nPlatform Fee (${commission}%): -$${platformCommission.toFixed(2)}\n\nYou earn per sale: $${providerEarnings.toFixed(2)}\n\nStudents can now enroll and start learning!\n\nThank you for contributing to our platform.`
          );
        }
      }
      
      // Notify all admins that a course went live (fires on the transition to
      // approved, for any course regardless of isCEU).
      try {
        const adminsSnap = await window.firebaseDB.query('users', window.firebaseDB.where('userType', '==', 'admin'));
        const courseTypeLabel = course.isCEU ? 'CEU course' : 'course';
        for (const doc of adminsSnap.docs) {
          if (doc.data().email) {
            await sendEmail(
              doc.data().email,
              `✅ Course Approved & Live: ${course.title}`,
              `A ${courseTypeLabel} has been approved and is now live:\n\nTitle: ${course.title}\nProvider: ${course.provider}\nPrice: $${course.price}\n\nRemember to confirm the in-app purchase product exists in the store before students try to buy it.`
            );
          }
        }
      } catch (err) {
        console.error('Error sending admin course-approval notification:', err);
      }

      showToast('Course approved! Provider has been notified via email.', 'success');
      showToast('Remember to add the IAP SKU in Google Play Console before this course goes live.', 'info');
      await loadAllCourses();
    } catch (error) {
      showToast('Error approving course: ' + error.message, 'error');
      console.error(error);
    }
  };

  const rejectCourse = async (courseId, notes) => {
    try {
      const course = pendingCourses.find(c => c.id === courseId) || 
                     courses.find(c => c.id === courseId);
      
      await window.firebaseDB.updateDoc(`courses/${courseId}`, {
        status: 'rejected',
        rejectionNotes: notes || 'No specific reason provided',
        rejectedAt: new Date().toISOString()
      });

      // Rejected Course - ADD 'content':
await logActivity('Rejected Course', `Rejected "${course.title}" - Reason: ${notes || 'No reason provided'}`, 'content');	
      
      if (course && course.providerId) {
        const providerDoc = await window.firebaseDB.getDoc(`users/${course.providerId}`);
        if (providerDoc.exists()) {
          const providerEmail = providerDoc.data().email;
          await sendEmail(
            providerEmail,
            `🔴 Course Needs Revision: ${course.title}`,
            `Your course "${course.title}" requires some changes before it can be approved.\n\nReason:\n${notes || 'No specific reason provided'}\n\nPlease review the feedback and resubmit your course after making the necessary changes.\n\nYou can edit and resubmit from your dashboard.`
          );
        }
      }
      
      showToast('Course rejected. Provider has been notified via email.', 'success');
      setRejectingCourse(null);
      setRejectionNotes('');
      await loadAllCourses();
    } catch (error) {
      showToast('Error rejecting course: ' + error.message, 'error');
      console.error(error);
    }
  };
  
  const suspendProvider = async () => {
    if (!selectedProvider || !suspendProviderReason.trim()) return;
    setSuspendingProvider(true);
    try {
      const updates = {
        status: 'suspended',
        suspendedAt: new Date().toISOString(),
        suspensionReason: suspendProviderReason.trim()
      };
      await window.firebaseDB.updateDoc(`users/${selectedProvider.id}`, updates);
      await sendEmail(
        selectedProvider.email,
        'RenewCEU Account Suspended',
        'Your provider account has been suspended. Please contact renewceuapp@gmail.com to discuss next steps.'
      );
      const updated = { ...selectedProvider, ...updates };
      setAllProviders(prev => prev.map(p => p.id === selectedProvider.id ? updated : p));
      setSelectedProvider(updated);
      setShowSuspendProviderModal(false);
      setSuspendProviderReason('');
      showToast('Provider suspended.', 'success');
    } catch (error) {
      console.error('Error suspending provider:', error);
      showToast('Error suspending provider: ' + error.message, 'error');
    } finally {
      setSuspendingProvider(false);
    }
  };

  const reinstateProvider = async () => {
    if (!selectedProvider) return;
    try {
      await window.firebaseDB.updateDoc(`users/${selectedProvider.id}`, {
        status: 'active',
        reinstatedAt: new Date().toISOString(),
        suspendedAt: null,
        suspensionReason: null
      });
      await sendEmail(
        selectedProvider.email,
        'RenewCEU Account Reinstated',
        'Your provider account has been reinstated. You can log in and access your account normally.'
      );
      const updated = { ...selectedProvider, status: 'active', reinstatedAt: new Date().toISOString() };
      delete updated.suspendedAt;
      delete updated.suspensionReason;
      setAllProviders(prev => prev.map(p => p.id === selectedProvider.id ? updated : p));
      setSelectedProvider(updated);
      showToast('Provider reinstated.', 'success');
    } catch (error) {
      console.error('Error reinstating provider:', error);
      showToast('Error reinstating provider: ' + error.message, 'error');
    }
  };

  const banStudent = async () => {
    if (!selectedStudent || !banStudentReason.trim()) return;
    setBanningStudent(true);
    try {
      const updates = {
        status: 'banned',
        bannedAt: new Date().toISOString(),
        banReason: banStudentReason.trim()
      };
      await window.firebaseDB.updateDoc(`users/${selectedStudent.id}`, updates);
      const updated = { ...selectedStudent, ...updates };
      setAllStudents(prev => prev.map(s => s.id === selectedStudent.id ? updated : s));
      setSelectedStudent(updated);
      setShowBanStudentModal(false);
      setBanStudentReason('');
      showToast('Student banned.', 'success');
    } catch (error) {
      console.error('Error banning student:', error);
      showToast('Error banning student: ' + error.message, 'error');
    } finally {
      setBanningStudent(false);
    }
  };

  const unbanStudent = async () => {
    if (!selectedStudent) return;
    try {
      await window.firebaseDB.updateDoc(`users/${selectedStudent.id}`, {
        status: 'active',
        bannedAt: null,
        banReason: null
      });
      const updated = { ...selectedStudent, status: 'active' };
      delete updated.bannedAt;
      delete updated.banReason;
      setAllStudents(prev => prev.map(s => s.id === selectedStudent.id ? updated : s));
      setSelectedStudent(updated);
      showToast('Student unbanned.', 'success');
    } catch (error) {
      console.error('Error unbanning student:', error);
      showToast('Error unbanning student: ' + error.message, 'error');
    }
  };

  const loadCourseFiles = async (courseId) => {
    try {
      const filesRef = await window.firebaseDB.query(
        'course_files',
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      const fileDocs = filesRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCourseFiles(prev => ({
        ...prev,
        [courseId]: fileDocs
      }));
    } catch (error) {
      console.error('Error loading course files:', error);
    }
  };
  
  const loadCourseVideos = async (courseId) => {
    try {
      const videosRef = await window.firebaseDB.query(
        'course_videos',
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      const videoDocs = videosRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCourseVideos(prev => ({
        ...prev,
        [courseId]: videoDocs
      }));
    } catch (error) {
      console.error('Error loading course videos:', error);
    }
  };
  
  const loadWatchedVideos = async (userId) => {
    try {
      const watchedRef = await window.firebaseDB.query(
        'video_progress',
        window.firebaseDB.where('userId', '==', userId)
      );
      
      const watchedDocs = watchedRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const watchedMap = {};

      watchedDocs.forEach(doc => {
        const key = `${doc.courseId}_${doc.videoId}`;
        watchedMap[key] = doc.completed;
      });

      setWatchedVideos(watchedMap);
    } catch (error) {
      console.error('Error loading watched videos:', error);
    }
  };

  const handleSignup = async () => {
    setError('');
    
    if (!email || !password || !fullName) {
      setError('Please fill in all fields');
      return;
    }

    if (userType === 'student' && !studentProfession) {
      setError('Please select your profession');
      return;
    }

    if (userType === 'student' && studentProfession === 'other' && !studentCustomProfession.trim()) {
      setError('Please specify your profession');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // Confirm provider account type
    if (userType === 'provider') {
      const confirmed = confirm(
        '⚠️ Create Provider Account?\n\n' +
        'Provider accounts are for instructors who want to create and sell courses.\n\n' +
        'If you want to TAKE courses, please register as a Student instead.\n\n' +
        'Note: One email = one account type. You cannot switch later.\n\n' +
        'Continue creating Provider account?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    setLoading(true);
    // Set after validation (validation early-returns would otherwise leak this as true).
    // Released in finally, by which point the user doc has been written.
    signupInProgress.current = true;

    try {
      const userCredential = await window.firebaseAuth.createUser(email, password);
      const user = userCredential.user;
      
      // Check for a pending admin invitation before writing the user doc
      let resolvedUserType = userType;
      let adminInviteData = null;
      try {
        const inviteSnap = await window.firebaseDB.query(
          'admin_invitations',
          window.firebaseDB.where('email', '==', email),
          window.firebaseDB.where('status', '==', 'pending')
        );
        if (!inviteSnap.empty) {
          adminInviteData = { id: inviteSnap.docs[0].id, ...inviteSnap.docs[0].data() };
          resolvedUserType = 'admin';
        }
      } catch (err) {
        console.error('Error checking admin invitations:', err);
      }

      await window.firebaseDB.setDoc(`users/${user.uid}`, {
        email: email,
        fullName: fullName,
        userType: resolvedUserType,
        status: 'active',
        createdAt: new Date().toISOString(),
        ...(resolvedUserType === 'admin' && adminInviteData && {
          adminRole: adminInviteData.adminRole || null,
          ...(adminInviteData.isOwner === true && { isOwner: true })
        }),
        ...(resolvedUserType === 'student' && {
          profession: studentProfession === 'other' ? studentCustomProfession : studentProfession,
          licenseExpirationDate: licenseExpirationDate || null,
        }),
        ...(resolvedUserType === 'provider' && {
          profession: profession,
          providerNumber: providerNumber,
          providerExpiration: providerExpiration,
          licenseNumber: licenseNumber
        })
      });

      // Mark invitation as accepted
      if (adminInviteData) {
        try {
          await window.firebaseDB.updateDoc(`admin_invitations/${adminInviteData.id}`, {
            status: 'accepted',
            acceptedAt: new Date().toISOString(),
            acceptedByUid: user.uid
          });
        } catch (err) {
          console.error('Error updating admin invitation status:', err);
        }
      }

      if (resolvedUserType === 'provider' && adminSettings.notifyNewProvider) {
        try {
          const adminsSnap = await window.firebaseDB.query('users', window.firebaseDB.where('userType', '==', 'admin'));
          const adminEmails = [];
          for (const doc of adminsSnap.docs) {
            if (doc.data().email) {
              adminEmails.push(doc.data().email);
            }
          }
          for (const adminEmail of adminEmails) {
            await sendEmail(
              adminEmail,
              '✅ New Provider Registered',
              `A new provider has registered and is now active on the platform:\n\nName: ${fullName}\nEmail: ${email}\nRegistered: ${new Date().toLocaleString()}\n\nThey can log in and begin creating courses immediately.`
            );
          }
        } catch (err) {
          console.error('Error sending admin provider notification:', err);
        }
      }
      
      setCurrentUser({
        uid: user.uid,
        email: email,
        fullName: fullName,
        userType: resolvedUserType,
        ...(resolvedUserType === 'admin' && adminInviteData && {
          adminRole: adminInviteData.adminRole || null,
          ...(adminInviteData.isOwner === true && { isOwner: true })
        })
      });

      setCurrentScreen(
        resolvedUserType === 'student' ? 'student-browse' :
        resolvedUserType === 'admin' ? 'admin-dashboard' :
        'provider-dashboard'
      );

      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setFullName('');
      setLicenseExpirationDate('');
    } catch (error) {
      setError(error.message);
    } finally {
      signupInProgress.current = false;
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');

    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await window.firebaseAuth.signIn(email, password);
      const user = userCredential.user;

      const userDocRef = await window.firebaseDB.getDoc(`users/${user.uid}`);

      if (userDocRef.exists()) {
        const userData = userDocRef.data();
        if (userData.status === 'suspended' || userData.status === 'banned') {
          await window.firebaseAuth.signOut();
          showToast('Your account has been suspended. Please contact support at renewceuapp@gmail.com.', 'error');
          return;
        }
        setCurrentUser({
          uid: user.uid,
          email: user.email,
          ...userData
        });

        // Track login
        await window.firebaseDB.updateDoc(`users/${user.uid}`, {
          lastLoginAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
          loginCount: (userData.loginCount || 0) + 1
        });

        setCurrentScreen(
          userData.userType === 'student' ? 'student-browse' :
          userData.userType === 'admin' ? 'admin-dashboard' :
          'provider-dashboard'
        );

        if (userData.userType === 'student') {
          loadEnrolledCourses(user.uid);
          loadWatchedVideos(user.uid);
          loadAllCourses();
        } else if (userData.userType === 'provider') {
          loadProviderCourses(user.uid);
        } else if (userData.userType === 'admin') {
          setAdminDataLoading(true);
          await loadAllCourses();
          await loadAllUsers();
          await loadAllTransactions();
          await loadAdminSettings();
          await loadAllEnrollments();
          setAdminDataLoading(false);
        }
      } else {
        // Firebase Auth succeeded but no Firestore user doc exists for this UID.
        // Sign out cleanly — we don't know what account type they should be, and
        // onboarding is only for Google Sign In.
        await window.firebaseAuth.signOut();
        showToast('Account not found. Please sign up or contact support.', 'error');
        setCurrentScreen('login');
        setEmail('');
        setPassword('');
        return;
      }

      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('❌ LOGIN ERROR:', error);
      console.error('Error message:', error.message);
      setError('Invalid email or password. Signed up with Google? Use Forgot Password to set one.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address above first');
      return;
    }
    setLoading(true);
    try {
      await window.firebaseAuth.sendPasswordReset(email);
      showToast('Check your email. Google users: this lets you set a password for web access.', 'success');
      setError('');
    } catch (error) {
      console.error('❌ Password reset error:', error);
      setError('Could not send reset email. Please check the address.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    signupInProgress.current = true;
    // First-time Google users have no doc until onboarding writes it, so the guard must
    // stay held past this function — handleGoogleAccountTypeSubmit releases it instead.
    let awaitingOnboarding = false;
    try {
      const { GoogleAuth } = await import('@southdevs/capacitor-google-auth');
      await GoogleAuth.initialize({
        clientId: '734423804205-q5qt8e3lcrbd0lgvl28v2qclq8l4n585.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true
      });
      // iOS native requires scopes + serverClientId on the signIn call itself
      // (Android reads them from capacitor.config; iOS does not).
      const result = await GoogleAuth.signIn({
        scopes: ['profile', 'email'],
        serverClientId: '734423804205-q5qt8e3lcrbd0lgvl28v2qclq8l4n585.apps.googleusercontent.com'
      });
      const idToken = result.authentication.idToken;
      const credential = window.firebaseAuth.googleCredential(idToken);
      const userCredential = await window.firebaseAuth.signInWithCredential(credential);
      const user = userCredential.user;

      const userDocSnap = await window.firebaseDB.getDoc(`users/${user.uid}`);

      if (!userDocSnap.exists()) {
        awaitingOnboarding = true;
        setPendingGoogleUser({ uid: user.uid, email: user.email, displayName: user.displayName || result.name || '', provider: 'google' });
        setCurrentScreen('google-account-type');
      } else {
        const userData = userDocSnap.data();
        if (userData.status === 'suspended' || userData.status === 'banned') {
          await window.firebaseAuth.signOut();
          showToast('Your account has been suspended. Please contact support at renewceuapp@gmail.com.', 'error');
          return;
        }
        setCurrentUser({ uid: user.uid, email: user.email, ...userData });
        await window.firebaseDB.updateDoc(`users/${user.uid}`, {
          lastLoginAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
          loginCount: (userData.loginCount || 0) + 1
        });
        const screen = userData.userType === 'student' ? 'student-browse'
                     : userData.userType === 'admin' ? 'admin-dashboard'
                     : 'provider-dashboard';
        setCurrentScreen(screen);
        if (userData.userType === 'student') {
          loadEnrolledCourses(user.uid);
          loadWatchedVideos(user.uid);
          loadAllCourses();
        } else if (userData.userType === 'provider') {
          loadProviderCourses(user.uid);
        } else if (userData.userType === 'admin') {
          setAdminDataLoading(true);
          await loadAllCourses();
          await loadAllUsers();
          await loadAllTransactions();
          await loadAdminSettings();
          await loadAllEnrollments();
          setAdminDataLoading(false);
        }
      }
    } catch (error) {
      console.error('❌ Google Sign-In error:', error);
      const code = error.code || error.message || 'unknown';
      const cancelled = code === '12501' || error.message === 'User cancelled' || error.message === 'popup_closed_by_user';
      if (!cancelled) {
        setError(`Google sign-in failed (${code}). Please try again.`);
      }
    } finally {
      if (!awaitingOnboarding) signupInProgress.current = false;
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');
    signupInProgress.current = true;
    // First-time Apple users have no doc until onboarding writes it, so the guard must
    // stay held past this function — handleGoogleAccountTypeSubmit releases it instead.
    let awaitingOnboarding = false;
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      // Firebase requires a nonce: send Apple the SHA-256 hash, give Firebase the raw value.
      const rawNonce = Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, '0')).join('');
      const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawNonce));
      const hashedNonce = Array.from(new Uint8Array(hashBuffer), (b) => b.toString(16).padStart(2, '0')).join('');
      const result = await SignInWithApple.authorize({
        scopes: 'email name',
        nonce: hashedNonce
      });
      const idToken = result.response.identityToken;
      const credential = window.firebaseAuth.appleCredential(idToken, rawNonce);
      const userCredential = await window.firebaseAuth.signInWithCredential(credential);
      const user = userCredential.user;

      const userDocSnap = await window.firebaseDB.getDoc(`users/${user.uid}`);

      if (!userDocSnap.exists()) {
        awaitingOnboarding = true;
        // Apple only returns name/email on the very first authorization — capture them now.
        const appleName = [result.response.givenName, result.response.familyName].filter(Boolean).join(' ');
        setPendingGoogleUser({ uid: user.uid, email: user.email || result.response.email || '', displayName: user.displayName || appleName || '', provider: 'apple' });
        setCurrentScreen('google-account-type');
      } else {
        const userData = userDocSnap.data();
        if (userData.status === 'suspended' || userData.status === 'banned') {
          await window.firebaseAuth.signOut();
          showToast('Your account has been suspended. Please contact support at renewceuapp@gmail.com.', 'error');
          return;
        }
        setCurrentUser({ uid: user.uid, email: user.email, ...userData });
        await window.firebaseDB.updateDoc(`users/${user.uid}`, {
          lastLoginAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
          loginCount: (userData.loginCount || 0) + 1
        });
        const screen = userData.userType === 'student' ? 'student-browse'
                     : userData.userType === 'admin' ? 'admin-dashboard'
                     : 'provider-dashboard';
        setCurrentScreen(screen);
        if (userData.userType === 'student') {
          loadEnrolledCourses(user.uid);
          loadWatchedVideos(user.uid);
          loadAllCourses();
        } else if (userData.userType === 'provider') {
          loadProviderCourses(user.uid);
        } else if (userData.userType === 'admin') {
          setAdminDataLoading(true);
          await loadAllCourses();
          await loadAllUsers();
          await loadAllTransactions();
          await loadAdminSettings();
          await loadAllEnrollments();
          setAdminDataLoading(false);
        }
      }
    } catch (error) {
      console.error('❌ Apple Sign-In error:', error);
      const code = error.code || error.message || 'unknown';
      const cancelled = code === '1001' || /cancel/i.test(String(error.message || ''));
      if (!cancelled) {
        setError(`Apple sign-in failed (${code}). Please try again.`);
      }
    } finally {
      if (!awaitingOnboarding) signupInProgress.current = false;
      setLoading(false);
    }
  };

  const toggleProfessionActive = async (professionId, currentActive) => {
    try {
      await window.firebaseDB.updateDoc(`professions/${professionId}`, {
        active: !currentActive
      });
      
      await loadProfessions();
      showToast(`Profession ${!currentActive ? 'activated' : 'deactivated'} successfully!`, 'success');
    } catch (error) {
      showToast('Error updating profession: ' + error.message, 'error');
    }
  };

  const updateProviderCredentials = async () => {
    if (!profession || !governingBody || !providerNumber || !providerExpiration) {
      showToast('Please fill in all required credential fields (Profession, Governing Body, Provider Number, and Expiration)', 'error');
      return;
    }
    
    setUpdatingCredentials(true);

    try {
      const updateData = {
        profession: profession,
        governingBody: governingBody,
        providerNumber: providerNumber,
        providerExpiration: providerExpiration,
        credentialNotes: credentialNotes || '',
        licenseNumber: licenseNumber
      };

      await window.firebaseDB.updateDoc(`users/${currentUser.uid}`, updateData);
      
      setCurrentUser({
        ...currentUser,
        ...updateData
      });
      
      showToast('Provider credentials updated successfully!', 'success');
      setShowProviderSettings(false);
      
    } catch (error) {
      showToast('Error updating credentials: ' + error.message, 'error');
    } finally {
      setUpdatingCredentials(false);
    }
  };

  const handleLogout = async () => {
    await window.firebaseAuth.signOut();
    setCurrentUser(null);
    setCurrentScreen('login');
    setEnrolledCourses([]);
    setProviderCourses([]);
    setWatchedVideos({});
  };

  const handleDeleteAccount = async () => {
    // Providers are blocked — modal handles this but guard here too
    if (currentUser?.userType === 'provider') {
      setShowDeleteAccountModal(false);
      return;
    }

    // Email users must supply password before we start
    if (currentUser?.authProvider !== 'google' && !deleteAccountPassword) {
      showToast('Please enter your password to confirm deletion.', 'error');
      return;
    }

    setDeletingAccount(true);
    try {
      // Re-auth for email users
      if (currentUser?.authProvider !== 'google') {
        await window.firebaseAuth.reauthWithPassword(deleteAccountPassword);
      }

      // Delete Auth first, then Firestore doc, then device token
      const uid = currentUser.uid;
      await window.firebaseAuth.deleteCurrentUser();
      await window.firebaseDB.deleteDoc(`users/${uid}`);
      await window.firebaseDB.deleteDoc(`device_tokens/${uid}`);

      setShowDeleteAccountModal(false);
      setDeleteAccountPassword('');
      setCurrentUser(null);
      setCurrentScreen('login');
      setEnrolledCourses([]);
      setProviderCourses([]);
      setWatchedVideos({});
      showToast('Your account has been deleted.', 'info');
    } catch (error) {
      console.error('❌ Delete account error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        showToast('Incorrect password. Please try again.', 'error');
      } else if (error.code === 'auth/requires-recent-login') {
        showToast('For security, please sign out and sign back in, then try again.', 'error');
      } else {
        showToast('Could not delete account. Please try again.', 'error');
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleGoogleAccountTypeSubmit = async () => {
    if (!pendingGoogleUser) return;
    if (userType === 'student' && !studentProfession) {
      setError('Please select your profession');
      return;
    }
    if (userType === 'student' && studentProfession === 'other' && !studentCustomProfession.trim()) {
      setError('Please specify your profession');
      return;
    }
    setLoading(true);
    setError('');
    // Held from handleGoogleSignIn/handleAppleSignIn; re-asserted here so retries stay guarded.
    signupInProgress.current = true;
    try {
      const newUserData = {
        email: pendingGoogleUser.email,
        fullName: pendingGoogleUser.displayName || '',
        userType: userType,
        status: 'active',
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        lastActiveAt: new Date().toISOString(),
        loginCount: 1,
        authProvider: pendingGoogleUser.provider || 'google',
        ...(userType === 'student' && {
          profession: studentProfession === 'other' ? studentCustomProfession : studentProfession,
          licenseExpirationDate: licenseExpirationDate || null,
        }),
      };
      await window.firebaseDB.setDoc(`users/${pendingGoogleUser.uid}`, newUserData);
      setCurrentUser({ uid: pendingGoogleUser.uid, ...newUserData });
      setPendingGoogleUser(null);
      setStudentProfession('');
      setStudentCustomProfession('');
      setLicenseExpirationDate('');
      setUserType('student');
      const screen = userType === 'student' ? 'student-browse' : 'provider-dashboard';
      setCurrentScreen(screen);
      if (userType === 'student') {
        loadEnrolledCourses(pendingGoogleUser.uid);
        loadWatchedVideos(pendingGoogleUser.uid);
        loadAllCourses();
      } else if (userType === 'provider') {
        loadProviderCourses(pendingGoogleUser.uid);
      }
    } catch (error) {
      console.error('❌ Google account type submit error:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      signupInProgress.current = false;
      setLoading(false);
    }
  };

// ===================================================================
// BOOKMARK FUNCTIONS
// ===================================================================
const loadBookmarks = async () => {
  if (!currentUser) return;
  
  try {
    const bookmarksRef = await window.firebaseDB.query(
      'bookmarks',
      window.firebaseDB.where('userId', '==', currentUser.uid)
    );
    
    const bookmarks = bookmarksRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    setBookmarkedCourses(bookmarks);
  } catch (error) {
    console.error('Error loading bookmarks:', error);
  }
};

const toggleBookmark = async (courseId) => {
  if (!currentUser) {
    showToast('Please login to bookmark courses', 'info');
    return;
  }
  
  try {
    const existingBookmark = bookmarkedCourses.find(b => b.courseId === courseId);

if (existingBookmark) {
// Remove bookmark
await window.firebaseDB.deleteDoc(`bookmarks/${existingBookmark.id}`);
  setBookmarkedCourses(prev => prev.filter(b => b.id !== existingBookmark.id));
  
  // Track removal
  await trackEvent({
    type: 'course_unbookmarked',
    courseId,
    userId: currentUser.uid
  });
      
    } else {
      // Add bookmark
      const bookmark = {
        userId: currentUser.uid,
        courseId: courseId,
        createdAt: new Date().toISOString()
      };
      
      const docRef = await window.firebaseDB.addDoc('bookmarks', bookmark);
      setBookmarkedCourses(prev => [...prev, { id: docRef.id, ...bookmark }]);
      
      // Track bookmark
      await trackEvent({
        type: 'course_bookmarked',
        courseId,
        userId: currentUser.uid
      });
    }
  } catch (error) {
    console.error('Error toggling bookmark:', error);
  }
};

const isBookmarked = (courseId) => {
  return bookmarkedCourses.some(b => b.courseId === courseId);
};  

  const sendEmail = async (to, subject, text, html = null) => {
    try {
      await window.firebaseDB.addDoc('mail', {
        to: to,
        message: {
          subject: subject,
          text: text,
          ...(html && { html: html })
        }
      });
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

const applyTransactionFilters = (transactions) => {
  return transactions.filter(t => {
    // Provider filter
    if (filterProvider !== 'all' && t.providerId !== filterProvider) return false;
    
    // Course filter
    if (filterCourse !== 'all' && t.courseId !== filterCourse) return false;
    
    // Status filter
    if (filterStatus === 'paid' && !t.providerPaid) return false;
    if (filterStatus === 'pending' && t.providerPaid) return false;
    if (filterStatus === 'refunded' && !t.refunded) return false;
    
    // Payment source filter
    if (filterSource !== 'all' && t.paymentSource !== filterSource) return false;
    
    // Date range filter
    if (filterDateFrom) {
      const purchaseDate = new Date(t.purchaseDate);
      const fromDate = new Date(filterDateFrom);
      if (purchaseDate < fromDate) return false;
    }
    if (filterDateTo) {
      const purchaseDate = new Date(t.purchaseDate);
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59); // End of day
      if (purchaseDate > toDate) return false;
    }
    
    return true;
  });
};

const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    showToast('No data to export', 'info');
    return;
  }
  
  // Convert data to CSV
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header] || '';
        // Escape commas and quotes
        return typeof value === 'string' && (value.includes(',') || value.includes('"'))
          ? `"${value.replace(/"/g, '""')}"`
          : value;
      }).join(',')
    )
  ].join('\n');
  
  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportPayoutCSV = () => {
  // Get pending transactions that need to be paid
  let pendingPayouts = allTransactions.filter(t => 
    !t.refunded && 
    !t.providerPaid
  );
  
  // Apply payment source filter
  if (filterSource !== 'all') {
    pendingPayouts = pendingPayouts.filter(t => t.paymentSource === filterSource);
  }
  
  // Sort by purchase date (oldest first)
  pendingPayouts.sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
  
  if (pendingPayouts.length === 0) {
    showToast('No pending payouts to export', 'info');
    return;
  }
  
  // Format for Melio (payment processing)
  const csvData = pendingPayouts.map(t => ({
    'Provider Name': t.providerName || 'Unknown',
    'Email': '', // You'll need to add provider email lookup if needed
    'Amount': Number(t.providerEarnings || 0).toFixed(2),
    'Course': t.courseTitle || 'Unknown',
    'Transaction Date': new Date(t.purchaseDate).toLocaleDateString(),
    'Payment Source': t.paymentSource || 'Unknown',
    'Transaction ID': t.id
  }));
  
  const filename = `payouts_${new Date().toISOString().slice(0, 10)}.csv`;
  exportToCSV(csvData, filename);
  
  logActivity('Exported Payout CSV', `Exported ${pendingPayouts.length} pending payouts`, 'financial');
};

const export1099CSV = (year) => {
  // Get paid transactions for selected year
  const yearTransactions = allTransactions.filter(t => {
    if (!t.providerPaidDate || t.refunded) return false;
    const paidYear = new Date(t.providerPaidDate).getFullYear();
    return paidYear === year;
  });
  
  // Group by provider
  const providerTotals = {};
  yearTransactions.forEach(t => {
    const pid = t.providerId;
    if (!providerTotals[pid]) {
      providerTotals[pid] = {
        providerId: pid,
        providerName: t.providerName,
        totalEarnings: 0,
        paymentCount: 0
      };
    }
    providerTotals[pid].totalEarnings += Number(t.providerEarnings || 0);
    providerTotals[pid].paymentCount += 1;
  });
  
  // Filter providers earning $600+
  const qualified1099 = Object.values(providerTotals)
    .filter(p => p.totalEarnings >= 600)
    .map(p => ({
      'Provider Name': p.providerName,
      'Total Earnings': p.totalEarnings.toFixed(2),
      'Payment Count': p.paymentCount,
      'Tax Year': year,
      'W-9 Status': allProviders.find(ap => ap.id === p.providerId)?.w9Submitted ? 'Submitted' : 'Not submitted'
    }));
  
  if (qualified1099.length === 0) {
    showToast(`No providers earned $600+ in ${year}`, 'info');
    return;
  }
  
  const filename = `RenewCEU_1099_${year}.csv`;
  exportToCSV(qualified1099, filename);
  
  logActivity('Exported 1099 CSV', `Exported 1099 data for ${qualified1099.length} providers (${year})`, 'financial');
};

const exportTransactionsCSV = () => {
  if (!allTransactions || allTransactions.length === 0) {
    showToast('No transactions to export', 'info');
    return;
  }
  
  const filteredTransactions = applyTransactionFilters(allTransactions);
  
  if (filteredTransactions.length === 0) {
    showToast('No transactions match the selected filters', 'info');
    return;
  }
  
  const csvData = filteredTransactions.map(t => ({
    'Date': new Date(t.purchaseDate).toLocaleDateString(),
    'Student': t.studentName || 'Unknown',
    'Provider': t.providerName || 'Unknown',
    'Course': t.courseTitle || 'Unknown',
    'Gross Amount': Number(t.amount || 0).toFixed(2),
    'App Store Cut': Number(t.appStoreCutAmount || 0).toFixed(2),
    'Net Revenue': Number(t.netRevenue || 0).toFixed(2),
    'Platform Commission': Number(t.platformCommission || 0).toFixed(2),
    'Provider Earnings': Number(t.providerEarnings || 0).toFixed(2),
    'Payment Source': t.paymentSource || 'Unknown',
    'Status': t.providerPaid ? 'Paid' : 'Pending',
    'Paid Date': t.providerPaidDate ? new Date(t.providerPaidDate).toLocaleDateString() : '',
    'Refunded': t.refunded ? 'Yes' : 'No'
  }));
  
  const filename = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
  exportToCSV(csvData, filename);
  
  logActivity('Exported Transactions CSV', `Exported ${filteredTransactions.length} transactions (${allTransactions.length} total)`, 'financial');
};

  const logActivity = async (action, details, category = 'admin', notes = '', extraFields = {}) => {
  if (!currentUser) return null;

  try {
    const activityData = {
      adminId: currentUser.uid,
      adminName: currentUser.fullName,
      action: action,
      details: details,
      category: category,
      notes: notes,
      timestamp: new Date().toISOString(),
      ...extraFields
    };

    const docRef = await window.firebaseDB.addDoc('activity_log', activityData);

    // Update local state with doc ID so resolve can reference it
    setActivityLog(prev => [{ ...activityData, id: docRef.id }, ...prev]);
    return docRef.id;
  } catch (error) {
    console.error('Error logging activity:', error);
    return null;
  }
};

const hasPermission = (permission) => {
  if (!currentUser) return false;
  if (currentUser.userType !== 'admin') return false;
  // Managing other admins (invite/remove) stays owner-only. Everything else is
  // available to any admin account — admin roles have been consolidated.
  if (permission === 'manageAdmins') return currentUser.isOwner === true;
  return true;
};

const loadAllAdmins = async () => {
  try {
    const adminsRef = await window.firebaseDB.query(
      'users',
      window.firebaseDB.where('userType', '==', 'admin')
    );
    
    const adminDocs = adminsRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    setAllAdmins(adminDocs);
  } catch (error) {
    console.error('Error loading admins:', error);
  }
};

const loadAllProviders = async () => {
  try {
    const providersRef = await window.firebaseDB.query(
      'users',
      window.firebaseDB.where('userType', '==', 'provider')
    );

    const providerDocs = providersRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setAllProviders(providerDocs);
  } catch (error) {
    console.error('Error loading providers:', error);
  }
};

const loadActivityLog = async () => {
  try {
    const logRef = await window.firebaseDB.getDocs('activity_log');
    
    const logDocs = logRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    setActivityLog(logDocs.slice(0, 50)); // Last 50 activities
  } catch (error) {
    console.error('Error loading activity log:', error);
  }
};

const removeAdmin = async (adminId, adminName, adminEmail) => {
  if (!currentUser.isOwner) {
    showToast('Only the owner can remove admins', 'error');
    return;
  }
  
  if (!confirm(`Remove admin access for ${adminName}? They will become a regular student account.`)) {
    return;
  }
  
  try {
    // Convert admin to student
    await window.firebaseDB.updateDoc(`users/${adminId}`, {
      userType: 'student',
      adminRole: null,
      isOwner: false
    });
    
    await logActivity('Removed Admin', `Removed admin access from ${adminName} (${adminEmail})`, 'admin');
    
    // Reload admins list
    const adminsSnapshot = await window.firebaseDB.getDocs('users');
    const adminsList = adminsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(user => user.userType === 'admin');
    setAdmins(adminsList);
    
    showToast('Admin removed successfully!', 'success');
  } catch (error) {
    showToast('Error removing admin: ' + error.message, 'error');
  }
};

const inviteAdmin = async () => {
  if (!newAdminEmail || !newAdminName) {
    showToast('Please enter email and name', 'error');
    return;
  }
  
  if (!currentUser.isOwner) {
    showToast('Only the owner can invite admins', 'error');
    return;
  }
  
  setInvitingAdmin(true);
  
  try {
    // Create admin invitation record
    await window.firebaseDB.addDoc('admin_invitations', {
      email: newAdminEmail,
      name: newAdminName,
      invitedBy: currentUser.uid,
      invitedByName: currentUser.fullName,
      invitedAt: new Date().toISOString(),
      status: 'pending'
    });

    // Send invitation email
    await sendEmail(
      newAdminEmail,
      'Admin Invitation - RenewCEU',
      `Hi ${newAdminName},\n\nYou've been invited to join the RenewCEU admin team as an administrator.\n\nPlease sign up at the platform to accept your admin role.\n\nThe RenewCEU Team`
    );

    await logActivity('Invited Admin', `Invited ${newAdminName} (${newAdminEmail}) as Admin`, 'admin');

    showToast(`Invitation sent to ${newAdminEmail}`, 'success');

    setNewAdminEmail('');
    setNewAdminName('');
    
  } catch (error) {
    showToast('Error sending invitation: ' + error.message, 'error');
  } finally {
    setInvitingAdmin(false);
  }
};

  // handleCreateCourse deleted - inline editing will replace this
  // Temporary placeholder to prevent crashes (modal still exists)
  const handleCreateCourse = async () => {
    showToast('Please use the Edit Course button on existing courses. Create Course button will be updated soon.', 'info');
    setShowCreateCourse(false);
  };

  // Create new draft course and enter inline editing mode
  const createNewCourse = async () => {
    if (!currentUser) return;
    
    try {
      // Create new course in Firebase with draft status
      const courseData = {
        title: 'Untitled Course',
        profession: professions.find(p => p.active)?.id || 'massage-therapy',
        hours: '',
        price: '',
        description: '',
        isCEU: true,
        providerId: currentUser.uid,
        provider: currentUser.fullName,
        status: 'draft',
        rating: 0,
        students: 0,
        createdAt: new Date().toISOString(),
        featuredImage: '',
        files: [],
        approvalEntries: [{ governingBody: '', approvalNumber: '', expirationDate: '' }],
        approvedStates: ALL_STATE_CODES,
      };
      
      const docRef = await window.firebaseDB.addDoc('courses', courseData);
      
      // Reload courses to include new one
      await loadProviderCourses(currentUser.uid);
      
      // Enter inline editing mode for the new course
      setEditingCourseId(docRef.id);
      
      showToast('New course created! Fill in the details below.', 'success');
      
    } catch (error) {
      showToast('Error creating course: ' + error.message, 'error');
    }
  };

  // Save Draft - creates or updates course without submitting for review
  const saveDraft = async (courseId) => {
    const course = providerCourses.find(c => c.id === courseId);
    
    // Basic validation
    if (!course.title) {
      showToast('Course title is required', 'error');
      return;
    }
    
    try {
      // Upload banner if selected
      let bannerUrl = course.featuredImage || '';
      if (selectedBanner && selectedBanner.courseId === courseId) {
        const uploadedUrl = await uploadBanner(courseId);
        if (uploadedUrl) {
          bannerUrl = uploadedUrl;
        }
      }
      
      // Update existing course
      await window.firebaseDB.updateDoc(`courses/${courseId}`, {
        title: course.title,
        profession: course.profession,
        hours: course.hours || '',
        price: course.price || '',
        description: course.description || '',
        isCEU: course.isCEU,
        featuredImage: bannerUrl,
        approvalEntries: course.approvalEntries || [{ governingBody: '', approvalNumber: '', expirationDate: '' }],
        approvedStates: course.approvedStates || ALL_STATE_CODES,
        updatedAt: new Date().toISOString()
      });
      
      showToast('Course saved as draft', 'success');
      setEditingCourseId(null); // Exit edit mode
      setExpandedCourseId(null); // Collapse card
      
      // Reload courses
      await loadProviderCourses(currentUser.uid);
      
      // Scroll to the saved course card
      setTimeout(() => {
        const courseCard = document.getElementById(`course-card-${courseId}`);
        if (courseCard) {
          courseCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      
    } catch (error) {
      showToast('Error saving course: ' + error.message, 'error');
    }
  };

  // Update course field inline (before saving to Firebase)
  const updateCourseField = (courseId, field, value) => {
    setProviderCourses(prev => prev.map(c => 
      c.id === courseId ? { ...c, [field]: value } : c
    ));
  };

  // Marketplace Banner Upload Functions
  const handleBannerSelect = (e, courseId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('Image must be under 5MB', 'error');
      return;
    }
    
    // Validate 16:9 aspect ratio (lenient - warns but allows)
    const img = new Image();
    const reader = new FileReader();
    
    reader.onload = (event) => {
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const target16by9 = 16 / 9;
        const tolerance = 0.1; // Allow 10% tolerance
        
        if (Math.abs(aspectRatio - target16by9) > tolerance) {
          showToast('⚠️ Warning: Image should be 16:9 ratio for best appearance (e.g., 1920x1080)', 'info');
        }
        
        // Allow upload regardless
        setSelectedBanner({ file, courseId });
      };
      img.src = event.target.result;
    };
    
    reader.readAsDataURL(file);
  };

  const uploadBanner = async (courseId) => {
    if (!selectedBanner || selectedBanner.courseId !== courseId) return null;
    
    setUploadingBanner(true);
    
    try {
      const timestamp = Date.now();
      const fileName = `banner_${courseId}_${timestamp}.${selectedBanner.file.name.split('.').pop()}`;
      const storagePath = `course_banners/${courseId}/${fileName}`;
      
      const uploadTask = window.firebaseStorage.uploadFile(
        storagePath,
        selectedBanner.file,
        (progress) => {
          setBannerUploadProgress(progress);
        }
      );
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => {
            console.error('Upload error:', error);
            setUploadingBanner(false);
            showToast('Failed to upload banner', 'error');
            reject(error);
          },
          async () => {
            const downloadURL = await window.firebaseStorage.getDownloadURL(storagePath);
            setUploadingBanner(false);
            setBannerUploadProgress(0);
            setSelectedBanner(null);
            
            // Update course in state immediately
            updateCourseField(courseId, 'featuredImage', downloadURL);
            
            showToast('Banner uploaded successfully!', 'success');
            resolve(downloadURL);
          }
        );
      });
    } catch (error) {
      console.error('Upload error:', error);
      setUploadingBanner(false);
      showToast('Failed to upload banner', 'error');
      return null;
    }
  };

  const removeBanner = async (courseId) => {
    if (!confirm('Remove marketplace banner?')) return;
    
    try {
      // Update in local state
      updateCourseField(courseId, 'featuredImage', '');
      
      // Update in Firebase
      await window.firebaseDB.updateDoc(`courses/${courseId}`, {
        featuredImage: ''
      });
      
      showToast('Banner removed', 'success');
    } catch (error) {
      showToast('Error removing banner: ' + error.message, 'error');
    }
  };

  const submitCourseForReview = async (courseId) => {
  const course = providerCourses.find(c => c.id === courseId);
  
  // Validation
  if (!course.title || !course.hours || !course.price || !course.description) {
    showToast('Cannot submit: Course must have title, hours, price, and description', 'error');
    return;
  }
  
  const files = courseFiles[courseId] || [];
  const videos = courseVideos[courseId] || [];
  const quiz = courseQuizzes[courseId];
  
  if (files.length === 0 && videos.length === 0) {
    showToast('Cannot submit: Course must have at least one video or material file', 'error');
    return;
  }
  
  if (!quiz || quiz.questions.length === 0) {
    showToast('Cannot submit: Course must have a quiz with at least one question', 'error');
    return;
  }
  
  const warningMessage = `⚠️ IMPORTANT: CEU Course Submission

Once your CEU course is APPROVED by admin:
- You CANNOT add or edit videos, materials, or quiz questions
- Any changes will require re-approval
- Your course will be taken offline during re-review

Please verify before submitting:
✓ All videos uploaded
✓ All materials included
✓ Quiz is complete and accurate
✓ Course details are correct

Submit "${course.title}" for admin review?`;

if (!confirm(warningMessage)) {
  return;
}
  
  try {
    await window.firebaseDB.updateDoc(`courses/${courseId}`, {
      status: 'pending',
      submittedAt: new Date().toISOString()
    });
    
    // Send email notification
    if (adminSettings.notifyNewCEUCourse) {
      try {
        const adminsSnap = await window.firebaseDB.query('users', window.firebaseDB.where('userType', '==', 'admin'));
        const adminEmails = [];
        for (const doc of adminsSnap.docs) {
          if (doc.data().email) {
            adminEmails.push(doc.data().email);
          }
        }
        const courseTypeLabel = course.isCEU ? 'CEU course' : 'course';
        const hoursLabel = course.isCEU ? 'CEU Hours' : 'Hours';
        for (const adminEmail of adminEmails) {
          await sendEmail(
            adminEmail,
            `⏰ New Course Pending Review: ${course.title}`,
            `A new ${courseTypeLabel} has been submitted for approval:\n\nTitle: ${course.title}\nProvider: ${course.provider}\nProfession: ${course.profession}\n${hoursLabel}: ${course.hours}\n\nPlease log in to the admin dashboard to review and approve.`
          );
        }
      } catch (err) {
        console.error('Error sending admin course notification:', err);
      }
    }
    
    showToast("Course submitted for review! You'll be notified when it's approved.", 'success');
    
    // Reload courses
    await loadProviderCourses(currentUser.uid);
    await loadAllCourses();
    
  } catch (error) {
    showToast('Error submitting course: ' + error.message, 'error');
  }
};
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('File is too large. Maximum size is 500MB.', 'error');
      return;
    }

    const allowedTypes = ['application/pdf', 'application/msword',
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Allowed: PDF, DOC, DOCX', 'error');
      return;
    }
    
    setSelectedFile(file);
  };
  
  const uploadFile = async (courseId) => {
    if (!selectedFile) {
      showToast('Please select a file first', 'error');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const fileName = `courses/${courseId}/files/${Date.now()}_${selectedFile.name}`;
      
      const uploadTask = window.firebaseStorage.uploadFile(
        fileName,
        selectedFile,
        (progress) => setUploadProgress(progress)
      );
      
      await uploadTask;
      const downloadURL = await window.firebaseStorage.getDownloadURL(fileName);
      
      const fileData = {
        courseId: courseId,
        fileName: selectedFile.name,
        fileSize: selectedFile.size < 1024 * 1024
          ? (selectedFile.size / 1024).toFixed(0) + ' KB'
          : (selectedFile.size / (1024 * 1024)).toFixed(2) + ' MB',
        fileType: selectedFile.type.includes('pdf') ? 'PDF' : 'Document',
        storagePath: fileName,
        downloadURL: downloadURL,
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser.uid
      };
      
      await window.firebaseDB.addDoc('course_files', fileData);
      
      setCourseFiles(prev => ({
        ...prev,
        [courseId]: [...(prev[courseId] || []), fileData]
      }));
      
      showToast('File uploaded successfully!', 'success');
      setSelectedFile(null);
      setUploadProgress(0);
      
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      showToast('Upload failed: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (fileId, storagePath, courseId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await window.firebaseStorage.deleteFile(storagePath);
      await window.firebaseDB.deleteDoc(`course_files/${fileId}`);
      
      setCourseFiles(prev => ({
        ...prev,
        [courseId]: (prev[courseId] || []).filter(f => f.id !== fileId)
      }));

      showToast('File deleted successfully', 'success');
    } catch (error) {
      showToast('Delete failed: ' + error.message, 'error');
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('Video file is too large. Maximum size is 500MB.', 'error');
      return;
    }

    if (!file.type.includes('video/mp4')) {
      showToast('Invalid file type. Only MP4 videos are supported.', 'error');
      return;
    }
    
    setSelectedVideo(file);
  };

  const addYoutubeVideo = async (courseId) => {
  if (!youtubeUrl.trim()) {
    showToast('Please enter a YouTube URL', 'error');
    return;
  }
  
  // Extract video ID from various YouTube URL formats
  let videoId = null;
  try {
    if (youtubeUrl.includes('youtube.com/watch?v=')) {
      videoId = youtubeUrl.split('v=')[1].split('&')[0];
    } else if (youtubeUrl.includes('youtu.be/')) {
      videoId = youtubeUrl.split('youtu.be/')[1].split('?')[0];
    } else if (youtubeUrl.includes('youtube.com/embed/')) {
      videoId = youtubeUrl.split('embed/')[1].split('?')[0];
    } else {
      showToast('Invalid YouTube URL format', 'error');
      return;
    }
  } catch (error) {
    showToast('Could not parse YouTube URL', 'error');
    return;
  }
  
  setAddingYoutubeVideo(true);
  
  try {
    const videoData = {
  courseId: courseId,
  videoType: 'youtube',
  youtubeVideoId: videoId,
  youtubeUrl: `https://www.youtube.com/embed/${videoId}`,
  videoName: videoTitle.trim() || `YouTube Video`,
  uploadedAt: new Date().toISOString(),
  uploadedBy: currentUser.uid
};
    
    const docRef = await window.firebaseDB.addDoc('course_videos', videoData);
    
    setCourseVideos(prev => ({
      ...prev,
      [courseId]: [...(prev[courseId] || []), { ...videoData, id: docRef.id }]
    }));
    
    showToast('YouTube video added successfully!', 'success');
setYoutubeUrl('');
setVideoTitle('');
  } catch (error) {
    showToast('Failed to add YouTube video: ' + error.message, 'error');
  } finally {
    setAddingYoutubeVideo(false);
  }
};
  
  const uploadVideo = async (courseId) => {
    if (!selectedVideo) {
      showToast('Please select a video first', 'error');
      return;
    }
    
    setUploadingVideo(true);
    setVideoUploadProgress(0);
    
    try {
      const fileName = `courses/${courseId}/videos/${Date.now()}_${selectedVideo.name}`;
      
      const uploadTask = window.firebaseStorage.uploadFile(
        fileName,
        selectedVideo,
        (progress) => setVideoUploadProgress(progress)
      );
      
      await uploadTask;
      const downloadURL = await window.firebaseStorage.getDownloadURL(fileName);
      
      const videoData = {
        courseId: courseId,
        videoName: selectedVideo.name,
        videoSize: (selectedVideo.size / (1024 * 1024)).toFixed(2) + ' MB',
        storagePath: fileName,
        downloadURL: downloadURL,
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser.uid,
        duration: 0
      };
      
      const docRef = await window.firebaseDB.addDoc('course_videos', videoData);
      
      setCourseVideos(prev => ({
        ...prev,
        [courseId]: [...(prev[courseId] || []), { ...videoData, id: docRef.id }]
      }));
      
      showToast('Video uploaded successfully!', 'success');
      setSelectedVideo(null);
      setVideoUploadProgress(0);
      
      const videoInput = document.querySelector('input[type="file"][accept="video/mp4"]');
      if (videoInput) videoInput.value = '';
    } catch (error) {
      showToast('Video upload failed: ' + error.message, 'error');
    } finally {
      setUploadingVideo(false);
    }
  };
  
  const deleteVideo = async (videoId, storagePath, courseId) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await window.firebaseStorage.deleteFile(storagePath);
      await window.firebaseDB.deleteDoc(`course_videos/${videoId}`);
      
      setCourseVideos(prev => ({
        ...prev,
        [courseId]: (prev[courseId] || []).filter(v => v.id !== videoId)
      }));

      showToast('Video deleted successfully', 'success');
    } catch (error) {
      showToast('Delete failed: ' + error.message, 'error');
    }
  };
  
  const saveVideoProgress = async (videoId, courseId, currentTime, duration) => {
    if (!currentUser) return;
    
    try {
      const percentWatched = (currentTime / duration) * 100;
      const completed = percentWatched >= 90;
      
      const progressData = {
        userId: currentUser.uid,
        videoId: videoId,
        courseId: courseId,
        lastPosition: Math.floor(currentTime),
        duration: Math.floor(duration),
        percentWatched: Math.floor(percentWatched),
        completed: completed,
        lastWatched: new Date().toISOString(),
        totalWatchTime: (videoWatchTime[videoId] || 0) + 10
      };
      
      const existingProgress = await window.firebaseDB.query(
        'video_progress',
        window.firebaseDB.where('userId', '==', currentUser.uid),
        window.firebaseDB.where('videoId', '==', videoId)
      );
      
      if (existingProgress.docs.length > 0) {
        const docId = existingProgress.docs[0].id;
        await window.firebaseDB.updateDoc(`video_progress/${docId}`, progressData);
      } else {
        await window.firebaseDB.addDoc('video_progress', progressData);
      }
      
      if (completed && !watchedVideos[videoId]) {
        setWatchedVideos(prev => ({
          ...prev,
          [videoId]: true
        }));
      }
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  };

  const loadVideoProgress = async (videoId, courseId) => {
    if (!currentUser) return null;
    
    try {
      const progressRef = await window.firebaseDB.query(
        'video_progress',
        window.firebaseDB.where('userId', '==', currentUser.uid),
        window.firebaseDB.where('videoId', '==', videoId),
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      if (progressRef.docs.length > 0) {
        const progress = progressRef.docs[0].data();
        return progress.lastPosition || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error loading video progress:', error);
      return 0;
    }
  };

  const handleVideoEnded = async (videoId, courseId) => {
    if (!currentUser) return;
    
    try {
      // Mark as completed in watchedVideos with composite key
      const key = `${courseId}_${videoId}`;
      
      // Only mark if not already completed
      if (!watchedVideos[key]) {
        setWatchedVideos(prev => ({
          ...prev,
          [key]: true
        }));
      }
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  };

  const loadCourseQuiz = async (courseId) => {
  try {
    const quizRef = await window.firebaseDB.query('course_quizzes', window.firebaseDB.where('courseId', '==', courseId));
    if (quizRef.docs.length > 0) {
      const quizData = { id: quizRef.docs[0].id, ...quizRef.docs[0].data() };
      setCourseQuizzes(prev => ({ ...prev, [courseId]: quizData }));
    }
  } catch (error) {
    console.error('❌ Error loading quiz:', error);
  }
};

  const submitAdminQuiz = () => {
    const quiz = takingQuizAsAdmin;
    let correctCount = 0;
    
    quiz.questions.forEach((question, index) => {
      if (adminQuizAnswers[index] === question.correctAnswer) {
        correctCount++;
      }
    });
    
    const score = Math.round((correctCount / quiz.questions.length) * 100);
    const passed = score >= quiz.passingScore;
    
    setAdminQuizResults({
      score,
      passed,
      correctCount,
      totalQuestions: quiz.questions.length
    });
  };

  const retakeAdminQuiz = () => {
    setAdminQuizAnswers({});
    setAdminQuizResults(null);
  };
  
  const openQuizBuilder = (courseId) => {
  const existingQuiz = courseQuizzes[courseId];
    if (existingQuiz) {
      setCurrentQuiz({
        passingScore: existingQuiz.passingScore,
        quizFormat: existingQuiz.quizFormat,
        questions: [...existingQuiz.questions]
      });
    } else {
      setCurrentQuiz({
        passingScore: 70,
        quizFormat: 'all-on-page',
        questions: []
      });
    }
    setShowQuizBuilder(courseId);
    setEditingQuestionIndex(null);
    resetQuestionForm();
  };
  
  const closeQuizBuilder = () => {
    setShowQuizBuilder(null);
    setCurrentQuiz({
      passingScore: 70,
      quizFormat: 'all-on-page',
      questions: []
    });
    setEditingQuestionIndex(null);
    resetQuestionForm();
  };
  
  const resetQuestionForm = () => {
    setQuestionForm({
      question: '',
      hasImage: false,
      imageFile: null,
      imageURL: null,
      options: ['', ''],
      correctAnswer: 0
    });
  };
  
  const handleQuizImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.includes('image/')) {
      showToast('Please select an image file (JPG, PNG, etc.)', 'error');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('Image is too large. Maximum size is 5MB.', 'error');
      return;
    }
    
    const previewURL = URL.createObjectURL(file);
    
    setQuestionForm(prev => ({
      ...prev,
      hasImage: true,
      imageFile: file,
      imageURL: previewURL
    }));
  };
  
  const removeQuestionImage = () => {
    if (questionForm.imageURL && questionForm.imageURL.startsWith('blob:')) {
      URL.revokeObjectURL(questionForm.imageURL);
    }
    setQuestionForm(prev => ({
      ...prev,
      hasImage: false,
      imageFile: null,
      imageURL: null
    }));
  };
  
  const addOption = () => {
    if (questionForm.options.length >= 6) {
      showToast('Maximum 6 options allowed', 'error');
      return;
    }
    setQuestionForm(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };
  
  const removeOption = (index) => {
    if (questionForm.options.length <= 2) {
      showToast('Minimum 2 options required', 'error');
      return;
    }
    
    const newOptions = questionForm.options.filter((_, i) => i !== index);
    const newCorrectAnswer = questionForm.correctAnswer >= newOptions.length 
      ? newOptions.length - 1 
      : questionForm.correctAnswer;
    
    setQuestionForm(prev => ({
      ...prev,
      options: newOptions,
      correctAnswer: newCorrectAnswer
    }));
  };
  
  const updateOption = (index, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = value;
    setQuestionForm(prev => ({
      ...prev,
      options: newOptions
    }));
  };

const addOrUpdateQuestion = async () => {
    if (!questionForm.question.trim()) {
      showToast('Please enter a question', 'error');
      return;
    }

    if (questionForm.options.some(opt => !opt.trim())) {
      showToast('Please fill in all options', 'error');
      return;
    }
    
    let imageURL = questionForm.imageURL;
    let imagePath = null;
    
    if (questionForm.imageFile && questionForm.imageURL?.startsWith('blob:')) {
      setUploadingQuizImage(true);
      setQuizImageUploadProgress(0);
      
      try {
        const fileName = `courses/${showQuizBuilder}/quiz-images/${Date.now()}_${questionForm.imageFile.name}`;
        
        const uploadTask = window.firebaseStorage.uploadFile(
          fileName,
          questionForm.imageFile,
          (progress) => setQuizImageUploadProgress(progress)
        );
        
        await uploadTask;
        imageURL = await window.firebaseStorage.getDownloadURL(fileName);
        imagePath = fileName;
      } catch (error) {
        showToast('Image upload failed: ' + error.message, 'error');
        setUploadingQuizImage(false);
        return;
      }
      
      setUploadingQuizImage(false);
    }
    
    const questionData = {
      question: questionForm.question.trim(),
      hasImage: questionForm.hasImage,
      imageURL: imageURL,
      imagePath: imagePath,
      options: questionForm.options.map(opt => opt.trim()),
      correctAnswer: questionForm.correctAnswer
    };
    
    if (editingQuestionIndex !== null) {
      const newQuestions = [...currentQuiz.questions];
      newQuestions[editingQuestionIndex] = questionData;
      setCurrentQuiz(prev => ({
        ...prev,
        questions: newQuestions
      }));
      setEditingQuestionIndex(null);
    } else {
      setCurrentQuiz(prev => ({
        ...prev,
        questions: [...prev.questions, questionData]
      }));
    }
    
    resetQuestionForm();
  };
  
  const editQuestion = (index) => {
    const question = currentQuiz.questions[index];
    setQuestionForm({
      question: question.question,
      hasImage: question.hasImage,
      imageFile: null,
      imageURL: question.imageURL,
      options: [...question.options],
      correctAnswer: question.correctAnswer
    });
    setEditingQuestionIndex(index);
  };
  
  const deleteQuestion = (index) => {
    if (!confirm('Delete this question?')) return;
    
    const newQuestions = currentQuiz.questions.filter((_, i) => i !== index);
    setCurrentQuiz(prev => ({
      ...prev,
      questions: newQuestions
    }));
    
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null);
      resetQuestionForm();
    }
  };
  
  const saveQuiz = async () => {
    if (!currentQuiz.questions.length) {
      showToast('Please add at least one question', 'error');
      return;
    }

    if (currentQuiz.passingScore < 0 || currentQuiz.passingScore > 100) {
      showToast('Passing score must be between 0 and 100', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      const quizData = {
        ...currentQuiz,
        courseId: showQuizBuilder,
        createdBy: currentUser.uid,
        updatedAt: new Date().toISOString()
      };
      
      const existingQuiz = await window.firebaseDB.query(
        'course_quizzes',
        window.firebaseDB.where('courseId', '==', showQuizBuilder)
      );
      
      if (existingQuiz.docs.length > 0) {
        const quizId = existingQuiz.docs[0].id;
        await window.firebaseDB.updateDoc(`course_quizzes/${quizId}`, quizData);
        
        setCourseQuizzes(prev => ({
          ...prev,
          [showQuizBuilder]: { ...quizData, id: quizId }
        }));
      } else {
        const docRef = await window.firebaseDB.addDoc('course_quizzes', quizData);
        
        setCourseQuizzes(prev => ({
          ...prev,
          [showQuizBuilder]: { ...quizData, id: docRef.id }
        }));
      }
      
      showToast('Quiz saved successfully!', 'success');
      closeQuizBuilder();
    } catch (error) {
      showToast('Error saving quiz: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const loadMaterialProgress = async (userId, courseId) => {
    try {
      const progressRef = await window.firebaseDB.query(
        'material_progress',
        window.firebaseDB.where('userId', '==', userId),
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      if (progressRef.docs.length > 0) {
        const progressData = progressRef.docs[0].data();
        setMaterialProgress(prev => ({
          ...prev,
          [courseId]: progressData
        }));
      }
    } catch (error) {
      console.error('Error loading material progress:', error);
    }
  };
  
  const trackFileDownload = async (fileId, courseId) => {
    if (!currentUser) return;
    
    try {
      const progressRef = await window.firebaseDB.query(
        'material_progress',
        window.firebaseDB.where('userId', '==', currentUser.uid),
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      let downloadedFiles = [];
      let progressDocId = null;
      
      if (progressRef.docs.length > 0) {
        const progressData = progressRef.docs[0].data();
        downloadedFiles = progressData.downloadedFiles || [];
        progressDocId = progressRef.docs[0].id;
      }
      
      if (!downloadedFiles.includes(fileId)) {
        downloadedFiles.push(fileId);
        
        const progressData = {
          userId: currentUser.uid,
          courseId: courseId,
          downloadedFiles: downloadedFiles,
          updatedAt: new Date().toISOString()
        };
        
        if (progressDocId) {
          await window.firebaseDB.updateDoc(`material_progress/${progressDocId}`, progressData);
        } else {
          await window.firebaseDB.addDoc('material_progress', progressData);
        }
        
        setMaterialProgress(prev => ({
          ...prev,
          [courseId]: progressData
        }));
      }
    } catch (error) {
      console.error('Error tracking file download:', error);
    }
  };
  
const areAllMaterialsViewed = (courseId) => {
  const files = courseFiles[courseId] || [];
  const videos = courseVideos[courseId] || [];
  const progress = materialProgress[courseId];
  
  if (files.length === 0 && videos.length === 0) return true;
  
  const downloadedFiles = progress?.downloadedFiles || [];
  const allFilesDownloaded = files.length === 0 || files.every(f => downloadedFiles.includes(f.id));
  const allVideosWatched = videos.length === 0 || videos.every(v => watchedVideos[`${courseId}_${v.id}`]);
  
  return allFilesDownloaded && allVideosWatched;
};
  
  const canTakeQuiz = (courseId) => {
    const quiz = courseQuizzes[courseId];
    if (!quiz) return false;
    return areAllMaterialsViewed(courseId);
  };
  
  const startQuiz = (courseId) => {
    if (!canTakeQuiz(courseId)) {
      showToast('Please watch all videos and download all materials before taking the quiz.', 'error');
      return;
    }

    const quiz = courseQuizzes[courseId];
    if (!quiz) {
      showToast('No quiz available for this course.', 'info');
      return;
    }
    
    setTakingQuiz({ ...quiz, courseId });
    setQuizAnswers({});
    setCurrentQuizQuestion(0);
  };
  
  const closeQuiz = () => {
    setTakingQuiz(null);
    setQuizAnswers({});
    setCurrentQuizQuestion(0);
  };
  
  const selectAnswer = (questionIndex, optionIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };
  
  const allQuestionsAnswered = () => {
    if (!takingQuiz) return false;
    return takingQuiz.questions.every((_, index) => quizAnswers[index] !== undefined);
  };
  
  const getAnsweredCount = () => {
    return Object.keys(quizAnswers).length;
  };
  
  const loadStudentQuizAttempts = async (userId) => {
    try {
      const attemptsRef = await window.firebaseDB.query(
        'quiz_attempts',
        window.firebaseDB.where('userId', '==', userId)
      );
      
      const attemptDocs = attemptsRef.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const attemptsMap = {};
      attemptDocs.forEach(attempt => {
        if (!attemptsMap[attempt.courseId] || new Date(attempt.attemptedAt) > new Date(attemptsMap[attempt.courseId].attemptedAt)) {
          attemptsMap[attempt.courseId] = attempt;
        }
      });
      
      setStudentQuizAttempts(attemptsMap);
    } catch (error) {
      console.error('Error loading quiz attempts:', error);
    }
  };
  
  const submitQuiz = async () => {
    if (!allQuestionsAnswered()) {
      showToast('Please answer all questions before submitting.', 'error');
      return;
    }
    
    let correctAnswers = 0;
    takingQuiz.questions.forEach((question, index) => {
      if (quizAnswers[index] === question.correctAnswer) {
        correctAnswers++;
      }
    });
    
    const score = (correctAnswers / takingQuiz.questions.length) * 100;
    const passed = score >= takingQuiz.passingScore;
    
    const results = {
      score: score,
      passed: passed,
      correctAnswers: correctAnswers,
      totalQuestions: takingQuiz.questions.length,
      passingScore: takingQuiz.passingScore,
      questions: takingQuiz.questions,
      studentAnswers: quizAnswers,
      courseId: takingQuiz.courseId
    };
    
    setQuizResults(results);
    
    try {
      // Check if already passed before
      const alreadyPassed = hasPassedQuiz(takingQuiz.courseId);
      
      // Only save attempt if: (1) they passed now, OR (2) they haven't passed before
      const shouldSaveAttempt = passed || !alreadyPassed;
      
      if (shouldSaveAttempt) {
        const attemptData = {
          userId: currentUser.uid,
          courseId: takingQuiz.courseId,
          score: score,
          passed: passed,
          correctAnswers: correctAnswers,
          totalQuestions: takingQuiz.questions.length,
          attemptedAt: new Date().toISOString()
        };
        
        await window.firebaseDB.addDoc('quiz_attempts', attemptData);
        
        setStudentQuizAttempts(prev => ({
          ...prev,
          [takingQuiz.courseId]: attemptData
        }));
      }
      
      if (passed) {
        const enrollment = enrolledCourses.find(e => e.courseId === takingQuiz.courseId);
        if (enrollment && enrollment.id) {
          // Only update if NOT already completed (first pass only)
          if (!enrollment.completed) {
            await window.firebaseDB.updateDoc(`enrollments/${enrollment.id}`, {
              completed: true,
              completedAt: new Date().toISOString(),
              progress: 100,
              quizPassed: true
            });
            
            setEnrolledCourses(enrolledCourses.map(e => 
              e.courseId === takingQuiz.courseId 
                ? { ...e, completed: true, progress: 100, quizPassed: true } 
                : e
            ));
            
            // Prompt for rating after FIRST pass only
            setTimeout(() => {
              promptForRating(takingQuiz.courseId);
            }, 1000);
          }
        }
      }
    } catch (error) {
      console.error('Error saving quiz attempt:', error);
    }
  };
  
  const retakeQuiz = () => {
    setShowRetakeWarning(true);
  };
  
  const confirmRetake = () => {
    const courseId = takingQuiz?.courseId || quizResults?.courseId;
    if (!courseId) {
      console.error('Cannot retake: courseId not found');
      setShowRetakeWarning(false);
      return;
    }
    const freshQuiz = courseQuizzes[courseId];
    if (!freshQuiz) {
      console.error('Quiz not found for course:', courseId);
      setShowRetakeWarning(false);
      return;
    }
    
    // Close everything and start fresh
    setShowRetakeWarning(false);
    setQuizResults(null);
    setQuizAnswers({});
    setCurrentQuizQuestion(0);
    
    // Start fresh quiz with randomized questions
    const randomizedQuestions = [...freshQuiz.questions].sort(() => Math.random() - 0.5);
    setTakingQuiz({ ...freshQuiz, courseId: courseId, questions: randomizedQuestions });
  };
  
  const cancelRetake = () => {
    setShowRetakeWarning(false);
  };
  
  const hasPassedQuiz = (courseId) => {
    const attempt = studentQuizAttempts[courseId];
    return attempt && attempt.passed;
  };

const loadCourseCertificateSettings = async (courseId) => {
    try {
      const settingsRef = await window.firebaseDB.query(
        'course_certificate_settings',
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      if (settingsRef.docs.length > 0) {
        const settings = settingsRef.docs[0].data();
        
        setCourseCredentials(prev => ({
          ...prev,
          [courseId]: {
            credentials: settings.providerCredentials,
            expirationYears: settings.expirationYears
          }
        }));
        
        if (settings.signatureURL) {
          setCourseSignatures(prev => ({
            ...prev,
            [courseId]: settings.signatureURL
          }));
        }
      }
    } catch (error) {
      console.error('Error loading certificate settings:', error);
    }
  };
  
  const handleSignatureSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.includes('image/')) {
      showToast('Please select an image file (PNG, JPG)', 'error');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast('Image too large. Maximum size is 2MB.', 'error');
      return;
    }
    
    setSignatureFile(file);
  };
  
  const saveCertificateSettings = async (courseId, certSettings) => {
    setUploadingSignature(true);
    setSignatureUploadProgress(0);
    
    try {
      let signatureURL = courseSignatures[courseId] || null; // Default to null, not undefined
      let signaturePath = null;
      
      if (signatureFile) {
        const fileName = `courses/${courseId}/signature/${Date.now()}_signature.png`;
        
        const uploadTask = window.firebaseStorage.uploadFile(
          fileName,
          signatureFile,
          (progress) => setSignatureUploadProgress(progress)
        );
        
        await uploadTask;
        signatureURL = await window.firebaseStorage.getDownloadURL(fileName);
        signaturePath = fileName;
      }
      
      const settingsData = {
        courseId: courseId,
        providerId: currentUser.uid,
        providerCredentials: certSettings.credentials || '',
        expirationType: certSettings.expirationType || 'type2',
        expirationFixedDate: certSettings.expirationFixedDate || null,
        expirationYears: certSettings.expirationYears || null,
        signatureURL: signatureURL,
        signaturePath: signaturePath,
        updatedAt: new Date().toISOString()
      };
      
      const existingSettings = await window.firebaseDB.query(
        'course_certificate_settings',
        window.firebaseDB.where('courseId', '==', courseId)
      );
      
      if (existingSettings.docs.length > 0) {
        await window.firebaseDB.updateDoc(
          `course_certificate_settings/${existingSettings.docs[0].id}`,
          settingsData
        );
      } else {
        await window.firebaseDB.addDoc('course_certificate_settings', settingsData);
      }
      
      setCourseCredentials(prev => ({
        ...prev,
        [courseId]: certSettings
      }));
      
      if (signatureURL) {
        setCourseSignatures(prev => ({
          ...prev,
          [courseId]: signatureURL
        }));
      }
      
      showToast('Certificate settings saved!', 'success');
      setShowCertificateSettings(null);
      setSignatureFile(null);
    } catch (error) {
      showToast('Error saving certificate settings: ' + error.message, 'error');
    } finally {
      setUploadingSignature(false);
    }
  };

// ===================================================================
// RATING SYSTEM FUNCTIONS
// ===================================================================

const loadUserRatings = async (userId) => {
  try {
    const ratingsRef = await window.firebaseDB.query(
      'ratings',
      window.firebaseDB.where('userId', '==', userId)
    );
    
    const ratingsMap = {};
    ratingsRef.docs.forEach(doc => {
      const data = doc.data();
      ratingsMap[data.courseId] = {
        id: doc.id,
        stars: data.stars,
        timestamp: data.timestamp
      };
    });
    
    setUserRatings(ratingsMap);
  } catch (error) {
    console.error('Error loading user ratings:', error);
  }
};

const loadRatingPromptCounts = async (userId) => {
  try {
    const progressRef = await window.firebaseDB.query(
      'course_progress',
      window.firebaseDB.where('userId', '==', userId)
    );
    
    const countsMap = {};
    progressRef.docs.forEach(doc => {
      const data = doc.data();
      const key = `${userId}_${data.courseId}`;
      countsMap[data.courseId] = {
        ratingPromptShown: data.ratingPromptShown || 0,
        hasRated: data.hasRated || false
      };
    });
    
    setRatingPromptCounts(countsMap);
  } catch (error) {
    console.error('Error loading rating prompt counts:', error);
  }
};

const promptForRating = async (courseId) => {
  if (!currentUser) return;
  
  // Check if already rated
  if (userRatings[courseId]) {
    return;
  }
  
  // Check prompt count
  const promptCount = ratingPromptCounts[courseId]?.ratingPromptShown || 0;
  
  if (promptCount >= 2) {
    return;
  }
  
  // Show rating prompt
  setRatingCourseId(courseId);
  setSelectedRating(0);
  setShowRatingPrompt(true);
  
  // Update prompt count
  try {
    const progressKey = `${currentUser.uid}_${courseId}`;
    const progressRef = await window.firebaseDB.query(
      'video_progress',
      window.firebaseDB.where('userId', '==', currentUser.uid),
      window.firebaseDB.where('courseId', '==', courseId)
    );
    
    // Use first video progress doc to store course-level data
    if (progressRef.docs.length > 0) {
      const docId = progressRef.docs[0].id;
      const currentData = progressRef.docs[0].data();
      await window.firebaseDB.updateDoc(`video_progress/${docId}`, {
        ratingPromptShown: promptCount + 1,
        lastPromptDate: new Date().toISOString()
      });
    }
    
    setRatingPromptCounts(prev => ({
      ...prev,
      [courseId]: {
        ...prev[courseId],
        ratingPromptShown: promptCount + 1
      }
    }));
  } catch (error) {
    console.error('Error updating prompt count:', error);
  }
};

const submitRating = async () => {
  if (!selectedRating || selectedRating < 1) {
    showToast('Please select a rating', 'error');
    return;
  }
  
  setSubmittingRating(true);
  
  try {
    const ratingData = {
      userId: currentUser.uid,
      courseId: ratingCourseId,
      stars: selectedRating,
      timestamp: new Date().toISOString()
    };
    
    const ratingId = `${currentUser.uid}_${ratingCourseId}`;
    await window.firebaseDB.setDoc(`ratings/${ratingId}`, ratingData);
    
    setUserRatings(prev => ({
      ...prev,
      [ratingCourseId]: {
        id: ratingId,
        stars: selectedRating,
        timestamp: ratingData.timestamp
      }
    }));
    
    setRatingPromptCounts(prev => ({
      ...prev,
      [ratingCourseId]: {
        ...prev[ratingCourseId],
        hasRated: true
      }
    }));
    
    await updateCourseAverageRating(ratingCourseId);
    
    setShowRatingPrompt(false);
    setRatingCourseId(null);
    setSelectedRating(0);
    
    showToast('Thank you for rating this course!', 'success');

  } catch (error) {
    showToast('Error submitting rating: ' + error.message, 'error');
    console.error(error);
  } finally {
    setSubmittingRating(false);
  }
};

const updateCourseAverageRating = async (courseId) => {
  try {
    const ratingsRef = await window.firebaseDB.query(
      'ratings',
      window.firebaseDB.where('courseId', '==', courseId)
    );
    
    const ratings = ratingsRef.docs.map(doc => doc.data());
    
    if (ratings.length === 0) {
      return;
    }
    
    const totalStars = ratings.reduce((sum, r) => sum + r.stars, 0);
    const average = totalStars / ratings.length;
    
    await window.firebaseDB.updateDoc(`courses/${courseId}`, {
      averageRating: Math.round(average * 10) / 10,
      totalRatings: ratings.length
    });
    
    setCourses(prevCourses => 
      prevCourses.map(c => 
        c.id === courseId 
          ? { ...c, averageRating: Math.round(average * 10) / 10, totalRatings: ratings.length }
          : c
      )
    );
    
  } catch (error) {
    console.error('Error updating course average:', error);
  }
};

const loadOrganizations = async () => {
  try {
    const orgsRef = await window.firebaseDB.getDocs('organizations');
    const orgsList = orgsRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setOrganizations(orgsList);
  } catch (error) {
    console.error('Error loading organizations:', error);
  }
};

// Redemption code data loader intentionally deferred. When building the redemption code management UI, add: const [redemptionCodes, setRedemptionCodes] = useState([]); and call loadRedemptionCodes() to populate it. The code_redemptions Firestore collection and all redemption logic (redeemCode, generateRedemptionCodes) remain intact.

const loadOrgCodes = async (organizationId) => {
  try {
    const codesRef = await window.firebaseDB.query(
      'redemption_codes',
      window.firebaseDB.where('organizationId', '==', organizationId)
    );

    const codesList = codesRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter out expired codes
    const today = new Date().toISOString().split('T')[0];
    const activeCodes = codesList.filter(code => code.expiresAt >= today);
    
    // Sort: redeemed codes first, then unredeemed
    const sortedCodes = activeCodes.sort((a, b) => {
      const aRedeemed = a.usedCount >= a.maxUses;
      const bRedeemed = b.usedCount >= b.maxUses;
      if (aRedeemed && !bRedeemed) return -1;
      if (!aRedeemed && bRedeemed) return 1;
      return 0;
    });
    
    setOrgCodes(sortedCodes);
  } catch (error) {
    console.error('Error loading org codes:', error);
    setOrgCodes([]);
  }
};

const loadCodeRedemptions = async (codeId, courseId) => {
  setLoadingCodeRedemptions(true);
  try {
    const ref = await window.firebaseDB.query(
      'code_redemptions',
      window.firebaseDB.where('codeId', '==', codeId)
    );
    const redemptions = ref.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // For each redemption, fetch the matching enrollment to get completion date
    const enriched = await Promise.all(redemptions.map(async (r) => {
      try {
        const enrollRef = await window.firebaseDB.query(
          'enrollments',
          window.firebaseDB.where('userId', '==', r.userId),
          window.firebaseDB.where('courseId', '==', courseId)
        );
        const enrollment = enrollRef.docs.length > 0 ? enrollRef.docs[0].data() : null;
        return {
          ...r,
          completed: enrollment?.completed || false,
          completedAt: enrollment?.completedAt || null
        };
      } catch {
        return { ...r, completed: false, completedAt: null };
      }
    }));

    setSelectedCodeRedemptions(enriched);
  } catch (error) {
    console.error('Error loading code redemptions:', error);
    setSelectedCodeRedemptions([]);
  } finally {
    setLoadingCodeRedemptions(false);
  }
};

const loadOrgCourses = async (organizationId) => {
  try {
    const coursesRef = await window.firebaseDB.query(
      'courses',
      window.firebaseDB.where('organizationId', '==', organizationId)
    );
    
    const coursesList = coursesRef.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    setOrgCourses(coursesList);
  } catch (error) {
    console.error('Error loading org courses:', error);
    setOrgCourses([]);
  }
};

const redeemCode = async () => {
  if (!redemptionCodeInput.trim()) {
    showToast('Please enter a code', 'error');
    return;
  }
  
  setRedeeming(true);
  
  try {
    // Find the code
    const codesRef = await window.firebaseDB.query(
      'redemption_codes',
      window.firebaseDB.where('code', '==', redemptionCodeInput.toUpperCase().trim())
    );
    
    if (codesRef.docs.length === 0) {
      showToast('Invalid code. Please check and try again.', 'error');
      setRedeeming(false);
      return;
    }
    
    const codeDoc = codesRef.docs[0];
    const code = { id: codeDoc.id, ...codeDoc.data() };
    
    // Validate code
    if (code.status !== 'active') {
      showToast('This code is no longer active.', 'error');
      setRedeeming(false);
      return;
    }
    
    if (code.expiresAt && new Date(code.expiresAt) < new Date()) {
      showToast(`This code expired on ${new Date(code.expiresAt).toLocaleDateString()}`, 'error');
      setRedeeming(false);
      return;
    }
    
    if (code.maxUses && code.usedCount >= code.maxUses) {
      showToast('This code has reached its maximum usage limit.', 'error');
      setRedeeming(false);
      return;
    }
    
    // Check if user already redeemed this code
    const existingRedemption = await window.firebaseDB.query(
      'code_redemptions',
      window.firebaseDB.where('userId', '==', currentUser.uid),
      window.firebaseDB.where('codeId', '==', code.id)
    );
    
    if (existingRedemption.docs.length > 0) {
      showToast('You have already redeemed this code.', 'error');
      setRedeeming(false);
      return;
    }
    
    // Check if already enrolled
    const existingEnrollment = enrolledCourses.find(e => e.courseId === code.courseId);
    if (existingEnrollment) {
      showToast('You are already enrolled in this course.', 'info');
      setRedeeming(false);
      return;
    }
    
    // Create enrollment
    const enrollmentData = {
      userId: currentUser.uid,
      userName: currentUser.fullName,
      userEmail: currentUser.email,
      courseId: code.courseId,
      redeemedCode: code.code,
      enrolledAt: new Date().toISOString(),
      paid: true,
      completed: false,
      progress: 0,
      paymentMethod: 'redemption_code'
    };
    
    await window.firebaseDB.addDoc('enrollments', enrollmentData);
    
    // Record redemption
    await window.firebaseDB.addDoc('code_redemptions', {
      codeId: code.id,
      code: code.code,
      userId: currentUser.uid,
      userName: currentUser.fullName,
      userEmail: currentUser.email,
      courseId: code.courseId,
      redeemedAt: new Date().toISOString()
    });
    
    // Update code usage count
    await window.firebaseDB.updateDoc(`redemption_codes/${code.id}`, {
      usedCount: (code.usedCount || 0) + 1
    });
    
    // Reload enrolled courses
    await loadEnrolledCourses(currentUser.uid);
    
    showToast('Code redeemed successfully! Course added to My Courses.', 'success');
    setShowRedeemModal(false);
    setRedemptionCodeInput('');
    setCurrentScreen('student-courses');
    
  } catch (error) {
    showToast('Error redeeming code: ' + error.message, 'error');
    console.error(error);
  } finally {
    setRedeeming(false);
  }
};

const createOrgCourse = async () => {
  if (!orgCourseForm.title || !orgCourseForm.description || !orgCourseForm.organizationId || !orgCourseForm.hours) {
    showToast('Please fill in all required fields', 'error');
    return;
  }

  setCreatingOrgCourse(true);
  
  try {
    const courseData = {
      title: orgCourseForm.title,
      description: orgCourseForm.description,
      organizationId: orgCourseForm.organizationId,
      hours: parseFloat(orgCourseForm.hours),
      price: 0, // Org courses are free
      status: 'approved', // Auto-approved
      provider: 'Platform Admin',
      providerId: currentUser.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      profession: 'General', // Can be customized later
      isCEU: orgCourseForm.isCEU
    };
    
    await window.firebaseDB.addDoc('courses', courseData);
    
    // Created Organization Course - ADD 'content':
await logActivity('Created Organization Course', `Created "${orgCourseForm.title}" for ${organizations.find(o => o.id === orgCourseForm.organizationId)?.name}`, 'content');
    
    showToast('Organization course created successfully!', 'success');
    
    // Reset form
    setOrgCourseForm({
      title: '',
      description: '',
      organizationId: '',
      hours: 0
    });
    
    setShowCreateOrgCourse(false);
    await loadAllCourses();
    
  } catch (error) {
    showToast('Error creating course: ' + error.message, 'error');
    console.error(error);
  } finally {
    setCreatingOrgCourse(false);
  }
};

const generateRedemptionCodes = async () => {
  if (!codeGeneration.courseId || !codeGeneration.prefix || !codeGeneration.quantity || !codeGeneration.expiresAt) {
    showToast('Please fill in all required fields', 'error');
    return;
  }
  
  // Validate prefix (1-6 chars, alphanumeric only)
  const prefix = codeGeneration.prefix.toUpperCase().trim();
  if (prefix.length < 1 || prefix.length > 6) {
    showToast('Prefix must be 1-6 characters', 'error');
    return;
  }
  if (!/^[A-Z0-9]+$/.test(prefix)) {
    showToast('Prefix can only contain letters and numbers', 'error');
    return;
  }
  
  // Validate quantity
  const quantity = parseInt(codeGeneration.quantity);
  if (quantity < 1 || quantity > 500) {
    showToast('Quantity must be between 1 and 500', 'error');
    return;
  }
  
  setLoading(true);
  
  try {
    const course = courses.find(c => c.id === codeGeneration.courseId);
    const org = organizations.find(o => o.id === course?.organizationId);
    
    if (!course || !org) {
      showToast('Invalid course or organization', 'error');
      setLoading(false);
      return;
    }
    
    // Character set excluding confusing chars (0/O, 1/I/l)
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const generatedCodes = [];
    
    // Generate codes with collision prevention
    for (let i = 0; i < quantity; i++) {
      let code;
      let attempts = 0;
      let isUnique = false;
      
      while (!isUnique && attempts < 50) {
        // Generate random 6-character suffix
        let suffix = '';
        for (let j = 0; j < 6; j++) {
          suffix += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        
        code = `${prefix}-${suffix}`;
        
        // Check if code already exists in database
        const existingCode = await window.firebaseDB.query(
          'redemption_codes',
          window.firebaseDB.where('code', '==', code)
        );
        
        // Also check if we generated it in this batch
        const duplicateInBatch = generatedCodes.some(gc => gc.code === code);
        
        if (existingCode.docs.length === 0 && !duplicateInBatch) {
          isUnique = true;
        }
        
        attempts++;
      }
      
      if (!isUnique) {
        throw new Error(`Failed to generate unique code after 50 attempts. Try a different prefix.`);
      }
      
      generatedCodes.push({
        code,
        courseId: codeGeneration.courseId,
        organizationId: course.organizationId,
        batchPrefix: prefix,
        discountType: codeGeneration.discountType,
        discountValue: parseFloat(codeGeneration.discountValue),
        expiresAt: codeGeneration.expiresAt,
        maxUses: 1, // Default to 1 use per code for org training
        usedCount: 0,
        status: 'active',
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });
    }
    
    // Save all codes to database
    for (const codeData of generatedCodes) {
      await window.firebaseDB.addDoc('redemption_codes', codeData);
    }
    
    // Generated Redemption Codes - ADD 'content':
await logActivity('Generated Redemption Codes', `Generated ${quantity} codes (${prefix}-XXXXXX) for "${course.title}" - ${org.name}`, 'content');
    
    // Download CSV
    downloadCodesCSV(generatedCodes, course, org);
    
    showToast(`Successfully generated ${quantity} codes! CSV downloaded for ${org.name}.`, 'success');
    
    // Reset form
    setCodeGeneration({
      courseId: '',
      prefix: '',
      quantity: 50,
      expiresAt: '',
      discountType: 'free',
      discountValue: 100
    });
    
    setShowGenerateCodes(false);

  } catch (error) {
    showToast('Error generating codes: ' + error.message, 'error');
    console.error(error);
  } finally {
    setLoading(false);
  }
};

const createOrganization = async () => {
  if (!newOrgForm.name) {
    showToast('Please enter organization name', 'error');
    return;
  }
  
  setCreatingOrg(true);
  
  try {
    const orgData = {
      name: newOrgForm.name,
      contactEmail: newOrgForm.contactEmail || '',
      contactName: newOrgForm.contactName || '',
      status: 'active',
      createdAt: new Date().toISOString(),
      createdBy: currentUser.uid
    };
    
    await window.firebaseDB.addDoc('organizations', orgData);
    
    // Created Organization - ADD 'admin':
await logActivity('Created Organization', `Created organization: ${newOrgForm.name}`, 'admin');
    
    showToast(`Organization "${newOrgForm.name}" created successfully!`, 'success');
    
    setNewOrgForm({ name: '', contactEmail: '', contactName: '' });
    await loadOrganizations();
    
  } catch (error) {
    showToast('Error creating organization: ' + error.message, 'error');
    console.error(error);
  } finally {
    setCreatingOrg(false);
  }
};

const saveOrgSettings = async () => {
  if (!editingOrgForm.name) {
    showToast('Organization name is required', 'error');
    return;
  }
  
  setSavingOrgSettings(true);
  
  try {
    await window.firebaseDB.updateDoc(`organizations/${selectedOrg.id}`, {
      name: editingOrgForm.name,
      contactName: editingOrgForm.contactName,
      contactEmail: editingOrgForm.contactEmail,
      status: editingOrgForm.status,
      updatedAt: new Date().toISOString()
    });
    
    await loadOrganizations();
    showToast('Organization settings saved successfully!', 'success');
    setSelectedOrg(null);
    setOrgDetailTab('codes');
  } catch (error) {
    console.error('Error saving organization settings:', error);
    showToast('Error saving settings: ' + error.message, 'error');
  } finally {
    setSavingOrgSettings(false);
  }
};

const downloadCodesCSV = (codes, course, organization) => {
  // CSV header
  let csv = 'Code,Course,Organization,Expires,Max Uses,Status\n';
  
  // CSV rows
  codes.forEach(code => {
    csv += `${code.code},"${course.title}","${organization.name}",${new Date(code.expiresAt).toLocaleDateString()},${code.maxUses},${code.status}\n`;
  });
  
  // Create download
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${organization.name.replace(/\s+/g, '_')}_codes_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const openManualRating = (courseId) => {
  setManualRatingCourseId(courseId);
  setSelectedRating(0);
  setHoverRating(0);
  setShowManualRating(true);
};

const submitManualRating = async () => {
  if (!selectedRating || selectedRating < 1) {
    showToast('Please select a rating', 'error');
    return;
  }
  
  setSubmittingRating(true);
  
  try {
    const ratingData = {
      userId: currentUser.uid,
      courseId: manualRatingCourseId,
      stars: selectedRating,
      timestamp: new Date().toISOString()
    };
    
    const ratingId = `${currentUser.uid}_${manualRatingCourseId}`;
    await window.firebaseDB.setDoc(`ratings/${ratingId}`, ratingData);
    
    setUserRatings(prev => ({
      ...prev,
      [manualRatingCourseId]: {
        id: ratingId,
        stars: selectedRating,
        timestamp: ratingData.timestamp
      }
    }));
    
    setRatingPromptCounts(prev => ({
      ...prev,
      [manualRatingCourseId]: {
        ...prev[manualRatingCourseId],
        hasRated: true
      }
    }));
    
    await updateCourseAverageRating(manualRatingCourseId);
    
    setShowManualRating(false);
    setManualRatingCourseId(null);
    setSelectedRating(0);
    
    showToast('Thank you for rating this course!', 'success');

  } catch (error) {
    showToast('Error submitting rating: ' + error.message, 'error');
    console.error(error);
  } finally {
    setSubmittingRating(false);
  }
};

  const generateCertificate = async (courseId) => {
    // Show Type 5 warning before generating
    const warningConfirmed = confirm(
      '⚠️ State boards may have recency requirements for CEU acceptance (typically 2-5 years). ' +
      'Verify with your state board before using.\n\n' +
      'Click OK to generate your certificate.'
    );
    
    if (!warningConfirmed) {
      return; // User cancelled
    }
    
    setGeneratingCertificate(true);
    
    try {
      const course = courses.find(c => c.id === courseId);
      const enrollment = enrolledCourses.find(e => e.courseId === courseId);
      const certSettings = courseCredentials[courseId];
      const signature = courseSignatures[courseId];
      
      if (!course || !enrollment) {
        showToast('Course information not found', 'error');
        return;
      }
      
      const certId = `CERT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
      
      // Calculate expiration based on type
      let expirationDate = null;
      let expirationText = '';
      const completionTimestamp = enrollment.completedAt || new Date().toISOString();
      const completionDate = new Date(completionTimestamp);
      
      const expirationType = certSettings?.expirationType || 'type2';
      
      if (expirationType === 'type1' && certSettings?.expirationFixedDate) {
        // Type 1: Fixed Date
        expirationDate = new Date(certSettings.expirationFixedDate);
        expirationText = `Expires: ${expirationDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}`;
      } else if (expirationType === 'type3' && currentUser?.providerExpiration) {
        // Type 3: Tied to Provider Credential
        expirationDate = new Date(currentUser.providerExpiration);
        expirationText = `Provider approved until: ${expirationDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}`;
      } else if (expirationType === 'type4' && certSettings?.expirationYears) {
        // Type 4: Years After Completion
        if (!isNaN(completionDate.getTime())) {
          expirationDate = new Date(completionDate);
          expirationDate.setFullYear(expirationDate.getFullYear() + parseInt(certSettings.expirationYears));
          expirationText = `Valid through: ${expirationDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}`;
        }
      }
      // Type 2: No expiration - expirationText remains empty
      
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      doc.setLineWidth(2);
      doc.rect(10, 10, 277, 190);
      doc.setLineWidth(0.5);
      doc.rect(15, 15, 267, 180);
      
      doc.setFontSize(28);
      doc.setFont(undefined, 'bold');
      doc.text('CERTIFICATE OF COMPLETION', 148.5, 40, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text('This certifies that', 148.5, 55, { align: 'center' });
      
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text(currentUser.fullName, 148.5, 70, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text('has successfully completed', 148.5, 82, { align: 'center' });
      
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      const titleLines = doc.splitTextToSize(course.title, 220);
      doc.text(titleLines, 148.5, 95, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      const completionDateStr = new Date(enrollment.completedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      const professionData = professions.find(p => p.id === course.profession);
      const creditLabel = professionData?.creditLabel || 'CEU Hours';
      
      let yPosition = 110;
      doc.text(`${creditLabel}: ${course.hours}`, 148.5, yPosition, { align: 'center' });
      yPosition += 8;
      
      doc.text(`Professional Category: ${professionData?.name || course.profession}`, 148.5, yPosition, { align: 'center' });
      yPosition += 8;
      
      doc.text(`Completion Date: ${completionDateStr}`, 148.5, yPosition, { align: 'center' });
      yPosition += 8;

      // Add provider name (Item 11 requirement)
      const providerLines = doc.splitTextToSize(`Provider: ${course.provider}`, 240);
      doc.text(providerLines, 148.5, yPosition, { align: 'center' });
      yPosition += providerLines.length * 8;

      // Add provider credentials if available
      if (certSettings?.credentials) {
        const credLines = doc.splitTextToSize(certSettings.credentials, 240);
        doc.text(credLines, 148.5, yPosition, { align: 'center' });
        yPosition += credLines.length * 8;
      }

      // REMOVED: Course Approval Number (Item 11 requirement)
      // REMOVED: Course Approval Expiration (Item 11 requirement)

      // Add expiration text based on type (Item 11 requirement)
      if (expirationText) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        const expirationLines = doc.splitTextToSize(expirationText, 240);
        doc.text(expirationLines, 148.5, yPosition, { align: 'center' });
        yPosition += expirationLines.length * 8;
      }

      // Add Certificate ID
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Certificate ID: ${certId}`, 148.5, yPosition, { align: 'center' });
      yPosition += 8;
      
      // Add signature if available
      if (signature) {
        try {
          const response = await fetch(signature);
          const blob = await response.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          doc.addImage(base64, 'PNG', 118.5, yPosition, 60, 15);
          yPosition += 18;
        } catch (err) {
          console.error('Error adding signature:', err);
          yPosition += 10;
        }
      }
      
      // Generate QR code for verification URL
      const verificationUrl = `https://renewceu.com/verify/${certId}`;
      try {
        // Generate QR code using canvas
        const QRCode = (await import('qrcode')).default;
        const qrDataUrl = await QRCode.toDataURL(verificationUrl, {
          width: 200,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
        
        // Add QR code to top right corner
        doc.addImage(qrDataUrl, 'PNG', 245, 20, 30, 30);
        
        // Add "Scan to Verify" text below QR
        doc.setFontSize(7);
        doc.text('Scan to', 260, 52, { align: 'center' });
        doc.text('Verify', 260, 56, { align: 'center' });
      } catch (qrError) {
        console.error('Error generating QR code:', qrError);
      }
      
      // Add verification URL text below certificate ID
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(`Verify at: renewceu.com/verify/${certId}`, 148.5, yPosition + 5, { align: 'center' });
      yPosition += 10;
      
      // Add Type 5 warning at bottom with better wrapping
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      const warningLines = doc.splitTextToSize(
  'Note: State boards may have recency requirements for CEU acceptance (typically 2-5 years). Verify with your state board before using.',
  240
      );
      doc.text(warningLines, 148.5, yPosition, { align: 'center' });
      yPosition += (warningLines.length * 5);
      
      doc.setFontSize(10);
      doc.text('RenewCEU', 148.5, yPosition + 5, { align: 'center' });
      
      const certificateRecord = {
        certificateId: certId,
        studentId: currentUser.uid,
        studentName: currentUser.fullName,
        courseId: courseId,
        courseTitle: course.title,
        ceuHours: course.hours,
        profession: course.profession,
        providerId: course.providerId,
        providerName: course.provider,
        issuedDate: enrollment.completedAt || new Date().toISOString(),
        expirationDate: expirationDate ? expirationDate.toISOString() : null,
        expirationType: expirationType,
        status: 'valid',
        createdAt: new Date().toISOString()
      };
      
      await window.firebaseDB.addDoc('certificates', certificateRecord);
      
      setCertificates(prev => ({
        ...prev,
        [courseId]: certificateRecord
      }));
      
      const fileName = `Certificate_${currentUser.fullName.replace(/\s+/g, '_')}_${course.title.replace(/\s+/g, '_')}.pdf`;

// Check if running in Capacitor (mobile) or web - improved detection
const isNativeMobile = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();

if (isNativeMobile) {
  // Mobile: Write to app's private data directory (no external storage permission needed),
  // then open with FileOpener so the user can view and share it.
  try {
    const pdfData = doc.output('dataurlstring');
    const base64Data = pdfData.split(',')[1];

    await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Data
    });

    const uriResult = await Filesystem.getUri({
      path: fileName,
      directory: Directory.Data
    });

    await FileOpener.openFile({
      path: uriResult.uri,
      mimeType: 'application/pdf'
    });
  } catch (fsError) {
    console.error('Filesystem error:', fsError);
    showToast('Error saving certificate: ' + fsError.message, 'error');
  }
} else {
  // Web: Open in new tab AND download
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  // Open in new tab for preview
  window.open(pdfUrl, '_blank');
  
  // Also trigger download
  doc.save(fileName);
}
      
    } catch (error) {
      showToast('Error generating certificate: ' + error.message, 'error');
      console.error(error);
    } finally {
      setGeneratingCertificate(false);
    }
  };
  
  // Auto-verify certificate from URL parameter
  const [autoVerifyResult, setAutoVerifyResult] = useState(null);
  const [autoVerifying, setAutoVerifying] = useState(false);
  
  useEffect(() => {
    const checkVerificationUrl = async () => {
      const path = window.location.pathname;
      if (path.startsWith('/verify/')) {
        const certId = path.replace('/verify/', '');
        if (certId && !autoVerifying && !autoVerifyResult) {
          setAutoVerifying(true);
          try {
            const certRef = await window.firebaseDB.query(
              'certificates',
              window.firebaseDB.where('certificateId', '==', certId)
            );
            
            if (certRef.docs.length === 0) {
              setAutoVerifyResult({
                found: false,
                certId: certId
              });
            } else {
              const cert = certRef.docs[0].data();
              let status = cert.status;
              if (cert.expirationDate && new Date(cert.expirationDate) < new Date()) {
                status = 'expired';
              }
              
              setAutoVerifyResult({
                found: true,
                status: status,
                studentName: cert.studentName,
                courseTitle: cert.courseTitle,
                ceuHours: cert.ceuHours,
                profession: cert.profession,
                issuedDate: new Date(cert.issuedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }),
                expirationDate: cert.expirationDate 
                  ? new Date(cert.expirationDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : null
              });
            }
          } catch (error) {
            setAutoVerifyResult({
              found: false,
              error: error.message
            });
          } finally {
            setAutoVerifying(false);
          }
        }
      }
    };
    
    checkVerificationUrl();
  }, [autoVerifying, autoVerifyResult]);
    
  const filteredCourses = (courses || [])
    .filter(course => {
      const matchesSearch = 
        searchTerm === '' ||
        (course.title && course.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (course.provider && course.provider.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesProfession = 
        selectedProfession === 'all' ||
        course.profession === selectedProfession ||
        (course.profession && professions.find(p => {
          if (p.id !== selectedProfession) return false;
          
          const courseProfLower = course.profession.toLowerCase();
          const profNameLower = p.name.toLowerCase();
          
          const courseWords = courseProfLower.split(/\s+/);
          const profWords = profNameLower.split(/\s+/);
          
          return courseWords.some(cw => 
            cw.length > 3 && profWords.some(pw => 
              pw.length > 3 && (cw.includes(pw) || pw.includes(cw))
            )
          );
        }));
      
      const matchesCEU = 
        ceuFilter === 'all' ||
        (ceuFilter === '1-5' && course.hours >= 1 && course.hours <= 5) ||
        (ceuFilter === '6-10' && course.hours >= 6 && course.hours <= 10) ||
        (ceuFilter === '11+' && course.hours >= 11);
      
      const matchesPrice = 
        priceFilter === 'all' ||
        (priceFilter === '0-50' && course.price <= 50) ||
        (priceFilter === '50-100' && course.price > 50 && course.price <= 100) ||
        (priceFilter === '100+' && course.price > 100);
      
      const isApproved = course.status === 'approved';

      const matchesCEUOnly = !ceuOnlyFilter || course.isCEU === true;

      // Hide organization courses from browse (only accessible via redemption)
      const isMarketplaceCourse = !course.organizationId;

      const isNotEnrolled = !enrolledCourses.find(e => e.courseId === course.id);

      return matchesSearch && matchesProfession && matchesCEU && matchesPrice && matchesCEUOnly && isApproved && isMarketplaceCourse && isNotEnrolled;
    })
    .sort((a, b) => {
  const aBookmarked = isBookmarked(a.id);
  const bBookmarked = isBookmarked(b.id);

  if (selectedState !== 'all') {
    const isApprovedInState = (c) => !c.approvedStates?.length || c.approvedStates.includes(selectedState);
    const aInState = isApprovedInState(a);
    const bInState = isApprovedInState(b);

    const getPriority = (course, bookmarked, inState) => {
      if (bookmarked && inState) return 1;
      if (bookmarked && !inState) return 2;
      if (course.isFeatured && inState) return 3;
      if (!course.isFeatured && inState) return 4;
      if (course.isFeatured && !inState) return 5;
      return 6;
    };

    const aPriority = getPriority(a, aBookmarked, aInState);
    const bPriority = getPriority(b, bBookmarked, bInState);
    if (aPriority !== bPriority) return aPriority - bPriority;

    if (aBookmarked && bBookmarked) {
      const aBookmark = bookmarkedCourses.find(bm => bm.courseId === a.id);
      const bBookmark = bookmarkedCourses.find(bm => bm.courseId === b.id);
      return new Date(bBookmark.createdAt) - new Date(aBookmark.createdAt);
    }
    return 0;
  }

  // Default sort behavior (no state filter)
  if (aBookmarked && !bBookmarked) return -1;
  if (!aBookmarked && bBookmarked) return 1;
  if (aBookmarked && bBookmarked) {
    const aBookmark = bookmarkedCourses.find(bm => bm.courseId === a.id);
    const bBookmark = bookmarkedCourses.find(bm => bm.courseId === b.id);
    return new Date(bBookmark.createdAt) - new Date(aBookmark.createdAt);
  }
  if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
  if (sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
  if (sortBy === 'rating-high') return (b.averageRating || 0) - (a.averageRating || 0);
  if (sortBy === 'rating-low') return (a.averageRating || 0) - (b.averageRating || 0);
  if (sortBy === 'price-low') return a.price - b.price;
  if (sortBy === 'price-high') return b.price - a.price;
  if (sortBy === 'hours-low') return a.hours - b.hours;
  if (sortBy === 'hours-high') return b.hours - a.hours;
  return 0;
});

// Stable shuffled non-featured courses — only re-shuffles when the non-featured set changes
const shuffledRegularCourses = React.useMemo(() => {
  const nonFeatured = filteredCourses.filter(c => !c.isFeatured);
  const arr = [...nonFeatured];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}, [filteredCourses]);

// ===== PRIVACY POLICY WEBVIEW =====


  // Add animation styles for toasts
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes slideDown {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Toast Container Component (render at top of each screen)
  const ToastContainer = () => (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] w-full max-w-md px-4">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`mb-2 rounded-lg shadow-lg p-4 flex items-start justify-between ${
            toast.type === 'success' ? 'bg-green-500 text-white' :
            toast.type === 'error' ? 'bg-red-500 text-white' :
            toast.type === 'warning' ? 'bg-yellow-500 text-white' :
            'bg-blue-500 text-white'
          }`}
          style={{
            transition: 'all 0.2s ease-out'
          }}
        >
          <p className="flex-1 font-medium">{toast.message}</p>
          <button
            onClick={() => removeToast(toast.id)}
            className="ml-3 text-white hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </div>
      ))}
    </div>
  );


  // Help Modal Component

if (currentScreen === 'privacy-policy') {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 flex items-center">
        <button
          onClick={() => setCurrentScreen('login')}
          className="flex items-center text-purple-600 hover:text-purple-700"
        >
          ← Back
        </button>
        <h1 className="ml-4 text-lg font-semibold">Privacy Policy</h1>
      </div>
      <iframe
        src="/privacy.html"
        className="w-full"
        style={{ height: 'calc(100vh - 64px)', border: 'none' }}
        title="Privacy Policy"
      />
    </div>
  );
}

// ===== TERMS OF SERVICE WEBVIEW =====
if (currentScreen === 'terms-of-service') {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 flex items-center">
        <button
          onClick={() => setCurrentScreen('login')}
          className="flex items-center text-purple-600 hover:text-purple-700"
        >
          ← Back
        </button>
        <h1 className="ml-4 text-lg font-semibold">Terms of Service</h1>
      </div>
      <iframe
        src="/terms.html"
        className="w-full"
        style={{ height: 'calc(100vh - 64px)', border: 'none' }}
        title="Terms of Service"
      />
    </div>
  );
}

// Show splash screen while checking authentication
if (authChecking) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center">
      <div className="text-center">
        <Book className="text-white mx-auto mb-4 animate-pulse" size={80} />
        <h1 className="text-4xl font-bold text-white mb-2">RenewCEU</h1>
        <div className="flex items-center justify-center gap-2 text-white mt-6">
          <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
          <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
        </div>
      </div>
    </div>
  );
}

// Auto-verification screen (triggered by /verify/ URL)
if (window.location.pathname.startsWith('/verify/')) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="flex items-center mb-6">
          <Award className="text-purple-600 mr-3" size={32} />
          <h1 className="text-2xl font-bold text-gray-800">Certificate Verification</h1>
        </div>
        
        {autoVerifying ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Verifying certificate...</p>
          </div>
        ) : autoVerifyResult ? (
          <div>
            {autoVerifyResult.found ? (
              <div className={`rounded-lg p-4 ${
                autoVerifyResult.status === 'valid' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center mb-3">
                  {autoVerifyResult.status === 'valid' ? (
                    <>
                      <CheckCircle size={24} className="text-green-600 mr-2" />
                      <h3 className="font-bold text-green-800">Certificate Valid</h3>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={24} className="text-red-600 mr-2" />
                      <h3 className="font-bold text-red-800">Certificate Expired</h3>
                    </>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <p><strong>Student:</strong> {autoVerifyResult.studentName}</p>
                  <p><strong>Course:</strong> {autoVerifyResult.courseTitle}</p>
                  <p><strong>CEU Hours:</strong> {autoVerifyResult.ceuHours}</p>
                  <p><strong>Profession:</strong> {professions.find(p => p.id === autoVerifyResult.profession)?.name || autoVerifyResult.profession}</p>
                  <p><strong>Issued:</strong> {autoVerifyResult.issuedDate}</p>
                  {autoVerifyResult.expirationDate && (
                    <p><strong>Expires:</strong> {autoVerifyResult.expirationDate}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <X size={24} className="text-red-600 mr-2" />
                  <h3 className="font-bold text-red-800">Certificate Not Found</h3>
                </div>
                <p className="text-sm text-red-700">
                  {autoVerifyResult.error || 'No certificate found with this ID. Please check the ID and try again.'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading verification...</p>
          </div>
        )}
        
        <div className="mt-6 pt-6 border-t text-center">
          <button
            onClick={() => {
              window.location.href = '/';
            }}
            className="text-sm text-purple-600 hover:text-purple-700 font-semibold"
          >
            Go to RenewCEU Home
          </button>
        </div>
      </div>
    </div>
  );
}

if (currentScreen === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <Book className="text-purple-600 mr-3" size={40} />
            <h1 className="text-3xl font-bold text-gray-800">RenewCEU</h1>
          </div>
          
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Login</h2>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
              <AlertCircle size={20} className="mr-2 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="flex items-center border rounded-lg px-3 py-2">
                <Mail size={20} className="text-gray-400 mr-2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 outline-none"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="flex items-center border rounded-lg px-3 py-2">
                <Lock size={20} className="text-gray-400 mr-2" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 outline-none"
                  placeholder="••••••••"
                />
              </div>
              <div className="text-right mt-1">
                <button
                  onClick={handleForgotPassword}
                  className="text-sm text-purple-600 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Continue with Google
            </button>

            {window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'ios' && (
              <button
                onClick={handleAppleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                <svg width="18" height="18" viewBox="0 0 814 1000" fill="#000000">
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                </svg>
                Continue with Apple
              </button>
            )}
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={() => setCurrentScreen('signup')}
                className="text-purple-600 font-semibold hover:underline"
              >
                Sign Up
              </button>
            </p>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              <button
                onClick={() => setCurrentScreen('privacy-policy')}
                className="hover:text-purple-600 underline"
              >
                Privacy Policy
              </button>
              {' · '}
              <button
                onClick={() => setCurrentScreen('terms-of-service')}
                className="hover:text-purple-600 underline"
              >
                Terms of Service
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }
      
  if (currentScreen === 'signup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <Book className="text-purple-600 mr-3" size={40} />
            <h1 className="text-3xl font-bold text-gray-800">RenewCEU</h1>
          </div>
          
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Create Account</h2>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
              <AlertCircle size={20} className="mr-2 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 outline-none"
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="flex items-center border rounded-lg px-3 py-2">
                <Mail size={20} className="text-gray-400 mr-2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 outline-none"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="flex items-center border rounded-lg px-3 py-2">
                <Lock size={20} className="text-gray-400 mr-2" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 outline-none"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
              <div className="flex items-center border rounded-lg px-3 py-2">
                <Lock size={20} className="text-gray-400 mr-2" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="flex-1 outline-none"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">I am a</label>
              <select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 outline-none"
              >
                <option value="student">Student / Professional</option>
                <option value="provider">Course Provider</option>
              </select>
            </div>

            {userType === 'student' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-blue-900 text-sm mb-2">Your Profession</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What is your profession?
                  </label>
                  <select
                    value={studentProfession}
                    onChange={(e) => {
                      setStudentProfession(e.target.value);
                      if (e.target.value !== 'other') {
                        setStudentCustomProfession('');
                      }
                    }}
                    className="w-full border rounded-lg px-3 py-2 outline-none"
                  >
                    <option value="">Select your profession...</option>
                    {professions.filter(p => p.active).map(prof => (
                      <option key={prof.id} value={prof.id}>
                        {prof.name}
                      </option>
                    ))}
                    <option value="other">Other (specify below)</option>
                  </select>
                </div>
                
                {studentProfession === 'other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specify your profession
                    </label>
                    <input
                      type="text"
                      value={studentCustomProfession}
                      onChange={(e) => setStudentCustomProfession(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 outline-none"
                      placeholder="e.g., Physical Therapist, Chiropractor"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    License Expiration Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={licenseExpirationDate}
                    onChange={(e) => setLicenseExpirationDate(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll use this to send you renewal reminders
                  </p>
                </div>
                
              </div>
            )}

            {userType === 'provider' && (
              <>
                {adminSettings.providerEnrollmentOpen === false ? (
                  // Provider enrollment closed - show message
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mt-4">
                    <div className="flex items-start mb-3">
                      <AlertCircle size={24} className="text-yellow-600 mr-3 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-yellow-800 text-lg mb-2">
                          Provider Enrollment Currently Closed
                        </h3>
                        <p className="text-gray-700 mb-4">
                          We're not accepting new course providers through open enrollment at this time.
                        </p>
                        <div className="bg-white rounded-lg p-4 border border-yellow-300">
                          <p className="font-semibold text-gray-800 mb-2">To request a provider invitation:</p>
                          <p className="text-gray-700 mb-3">Email: <strong>support@renewceu.com</strong></p>
                          <p className="text-sm text-gray-600 mb-2">Please include:</p>
                          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                            <li>Your name and professional credentials</li>
                            <li>License/certification number</li>
                            <li>Brief overview of course topics you'd like to create</li>
                          </ul>
                          <p className="text-sm text-gray-500 mt-3 italic">
                            We typically respond within 2-3 business days.
                          </p>
                        </div>
                        <button
                          onClick={() => setUserType('student')}
                          className="mt-4 text-purple-600 hover:text-purple-800 font-semibold flex items-center gap-2"
                        >
                          ← Back to Student Registration
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}
            <button
              onClick={handleSignup}
              disabled={loading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Continue with Google
            </button>

            {window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'ios' && (
              <button
                onClick={handleAppleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
              >
                <svg width="18" height="18" viewBox="0 0 814 1000" fill="#000000">
                  <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                </svg>
                Continue with Apple
              </button>
            )}
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => setCurrentScreen('login')}
                className="text-purple-600 font-semibold hover:underline"
              >
                Login
              </button>
            </p>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              <button
                onClick={() => setCurrentScreen('privacy-policy')}
                className="hover:text-purple-600 underline"
              >
                Privacy Policy
              </button>
              {' · '}
              <button
                onClick={() => setCurrentScreen('terms-of-service')}
                className="hover:text-purple-600 underline"
              >
                Terms of Service
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }


  // ===================================================================
  // GOOGLE ACCOUNT TYPE SELECTION SCREEN
  // ===================================================================
  if (currentScreen === 'google-account-type') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <Book className="text-purple-600 mr-3" size={36} />
            <h1 className="text-2xl font-bold text-gray-800">RenewCEU</h1>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">Welcome{pendingGoogleUser?.displayName ? `, ${pendingGoogleUser.displayName.split(' ')[0]}` : ''}!</h2>
          <p className="text-sm text-gray-500 text-center mb-6">Choose how you'll use RenewCEU</p>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
              <AlertCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">I am a</label>
              <select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 outline-none"
              >
                <option value="student">Student / Professional</option>
                <option value="provider">Course Provider</option>
              </select>
            </div>
            {userType === 'student' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-blue-900 text-sm">Your Profession</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What is your profession?</label>
                  <select
                    value={studentProfession}
                    onChange={(e) => { setStudentProfession(e.target.value); if (e.target.value !== 'other') setStudentCustomProfession(''); }}
                    className="w-full border rounded-lg px-3 py-2 outline-none"
                  >
                    <option value="">Select your profession...</option>
                    {professions.filter(p => p.active).map(prof => (
                      <option key={prof.id} value={prof.id}>{prof.name}</option>
                    ))}
                    <option value="other">Other (specify below)</option>
                  </select>
                </div>
                {studentProfession === 'other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specify your profession</label>
                    <input
                      type="text"
                      value={studentCustomProfession}
                      onChange={(e) => setStudentCustomProfession(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 outline-none"
                      placeholder="e.g., Physical Therapist, Chiropractor"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">License Expiration Date (Optional)</label>
                  <input
                    type="date"
                    value={licenseExpirationDate}
                    onChange={(e) => setLicenseExpirationDate(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 outline-none"
                  />
                </div>
              </div>
            )}
            <button
              onClick={handleGoogleAccountTypeSubmit}
              disabled={loading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Setting up account...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

if (currentScreen === 'student-browse' || currentScreen === 'student-courses') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <ToastContainer />
        {/* Simple Header */}
        <div className="bg-white shadow-sm safe-top">
          <div className="px-4 py-3 flex items-center justify-center">
            <h1 className="text-xl font-bold text-gray-800">
              {studentTab === 'browse' ? 'Browse Courses' : 
               studentTab === 'courses' ? 'My Courses' : 
               studentTab === 'redeem' ? 'Redeem Code' : 
               'Profile'}
            </h1>
          </div>
        </div>

        {/* Main Content - Flex Grow */}
        <div className="flex-1 overflow-y-auto pb-20">
          
          {/* BROWSE TAB */}
          {studentTab === 'browse' && (
            <div className="max-w-6xl mx-auto px-4 py-6">
              <div className="bg-white rounded-xl shadow-sm p-3 mb-4">
                {/* Search */}
                <div className="flex items-center mb-3">
                  <Search size={18} className="text-gray-400 mr-2 flex-shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value || '')}
                    placeholder="Search courses..."
                    className="flex-1 outline-none text-sm"
                  />
                </div>
                {/* Profession chips */}
                <div className="flex gap-2 overflow-x-auto pb-1 mb-2 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
                  <button
                    onClick={() => setSelectedProfession('all')}
                    className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition ${selectedProfession === 'all' ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-400'}`}
                  >All</button>
                  {professions.filter(p => p.active).map(prof => (
                    <button
                      key={prof.id}
                      onClick={() => setSelectedProfession(prof.id)}
                      className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition ${selectedProfession === prof.id ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-400'}`}
                    >{prof.name}</button>
                  ))}
                  <button
                    onClick={() => setCeuOnlyFilter(f => !f)}
                    className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition ${ceuOnlyFilter ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-400'}`}
                  >🎓 CEU Only</button>
                </div>
                {/* State filter row */}
                <div className="mb-2">
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-full px-3 py-1 outline-none bg-white text-gray-600"
                  >
                    <option value="all">All States</option>
                    {ALL_US_STATES.map(s => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {/* Secondary filter row: hours, price, sort */}
                <div className="flex gap-2 items-center overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
                  {[
                    { value: 'all', label: 'All Hours' },
                    { value: '1-5', label: '1–5 hrs' },
                    { value: '6-10', label: '6–10 hrs' },
                    { value: '11+', label: '11+ hrs' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setCeuFilter(opt.value)}
                      className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition ${ceuFilter === opt.value ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-400'}`}
                    >{opt.label}</button>
                  ))}
                  <div className="flex-shrink-0 w-px h-4 bg-gray-200" />
                  {[
                    { value: 'all', label: 'Any Price' },
                    { value: '0-50', label: 'Under $50' },
                    { value: '50-100', label: '$50–100' },
                    { value: '100+', label: '$100+' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPriceFilter(opt.value)}
                      className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition ${priceFilter === opt.value ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-400'}`}
                    >{opt.label}</button>
                  ))}
                  <div className="flex-shrink-0 w-px h-4 bg-gray-200" />
                  <select
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setHasUserSorted(true); }}
                    className="flex-shrink-0 text-xs border border-gray-200 rounded-full px-3 py-1 outline-none bg-white text-gray-600"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="rating-high">Top Rated</option>
                    <option value="rating-low">Rating ↑</option>
                    <option value="price-low">Price ↑</option>
                    <option value="price-high">Price ↓</option>
                    <option value="hours-low">Hours ↑</option>
                    <option value="hours-high">Hours ↓</option>
                  </select>
                </div>
              </div>
              
              {(() => {
                const renderCourseCard = (course) => (
                  <div
                    key={course.id}
                    className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer active:scale-95 transition-transform relative"
                    onClick={() => {
                      setSelectedCourseForDetail(course);
                      setShowCourseDetailModal(true);
                      trackEvent({
                        type: 'course_viewed',
                        courseId: course.id,
                        userId: currentUser?.uid || 'anonymous',
                        source: 'browse',
                        profession: course.profession,
                        price: course.price,
                        isCEU: course.isCEU
                      });
                    }}
                  >
                    {/* Bookmark indicator */}
                    {isBookmarked(course.id) && (
                      <div className="absolute top-2 right-2 bg-yellow-400 rounded-full p-1 shadow z-10">
                        <Star size={12} className="fill-yellow-600 text-yellow-600" />
                      </div>
                    )}
                    {/* Thumbnail */}
                    <div className="relative">
                      {course.featuredImage ? (
                        <div
                          className="aspect-video bg-cover bg-center"
                          style={{ backgroundImage: `url(${course.featuredImage})` }}
                        />
                      ) : (
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 aspect-video" />
                      )}
                      {(() => {
                        if (selectedState !== 'all') {
                          const isApproved = !course.approvedStates?.length || course.approvedStates.includes(selectedState);
                          if (!isApproved) {
                            return (
                              <div className="absolute top-1.5 left-1.5 bg-amber-500 text-white text-xs font-medium px-1.5 py-0.5 rounded shadow leading-tight">
                                Not approved in {selectedState}
                              </div>
                            );
                          }
                        }
                        return course.isFeatured ? (
                          <div className="absolute top-1.5 left-1.5 bg-yellow-400 rounded-full w-6 h-6 flex items-center justify-center shadow">
                            <span className="text-xs">⭐</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div className="p-2.5">
                      {/* Title */}
                      <h3 className="text-sm font-bold text-gray-900 leading-tight mb-2 line-clamp-3">{course.title}</h3>
                      {/* Price */}
                      <div className="flex items-center justify-end">
                        <span className="text-sm font-bold text-purple-700">${course.price}</span>
                      </div>
                    </div>
                  </div>
                );

                // split featured vs non-featured only in default (unsorted, no state filter) state
                const showFeaturedSection = !hasUserSorted && selectedState === 'all';
                const featuredCourses = showFeaturedSection ? filteredCourses.filter(c => c.isFeatured) : [];
                // Use stable shuffled array in default state; use filteredCourses (sorted) when user has sorted or filtered by state
                const displayRegular = (hasUserSorted || selectedState !== 'all') ? filteredCourses : shuffledRegularCourses;

                const totalCount = featuredCourses.length + displayRegular.length;
                const gridCols = totalCount >= 4 ? 'grid-cols-2' : 'grid-cols-1';

                return (
                  <>
                    {/* Featured section — only in default (unsorted) state */}
                    {showFeaturedSection && featuredCourses.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center mb-3">
                          <span className="text-base font-bold text-gray-800 mr-2">⭐ Featured</span>
                          <div className="flex-1 h-px bg-yellow-200" />
                        </div>
                        <div className={`grid ${gridCols} gap-3`}>
                          {featuredCourses.map(course => renderCourseCard(course))}
                        </div>
                      </div>
                    )}

                    {/* Main grid */}
                    {showFeaturedSection && featuredCourses.length > 0 && (
                      <div className="flex items-center mb-3">
                        <span className="text-base font-bold text-gray-800 mr-2">All Courses</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    )}
                    <div className={`grid ${gridCols} gap-3`}>
                      {displayRegular.map(course => renderCourseCard(course))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* MY COURSES TAB */}
          {studentTab === 'courses' && (
            <div className="max-w-6xl mx-auto px-4 py-6">
              {enrolledCourses.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <Book className="mx-auto text-gray-400 mb-4" size={64} />
                  <p className="text-gray-600 mb-4">You haven't enrolled in any courses yet.</p>
                  <button
                    onClick={() => { setStudentTab('browse'); setHasUserSorted(false); }}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
                  >
                    Browse Courses
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {enrolledCourses
                    .sort((a, b) => {
                      const dateA = (a.purchaseDate || a.enrolledAt)?.seconds || (a.purchaseDate || a.enrolledAt);
                      const dateB = (b.purchaseDate || b.enrolledAt)?.seconds || (b.purchaseDate || b.enrolledAt);
                      return dateB - dateA;
                    })
                    .map(enrollment => {
                      const course = courses.find(c => c.id === enrollment.courseId);
                      if (!course) return null;
                      
                      const files = courseFiles[course.id] || [];
                      const videos = courseVideos[course.id] || [];
                      
                      return (
                        <div 
  key={enrollment.courseId} 
  className="bg-white rounded-lg shadow-md p-6 border-4 border-slate-600 cursor-pointer hover:shadow-xl transition"
  onClick={() => {
    setViewingEnrolledCourse({ course, enrollment });
  }}
>
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold mb-2">{course.title}</h3>
                              <p className="text-gray-600 mb-2">{professions.find(p => p.id === course.profession)?.name || course.profession} • {course.hours} CEU Hours</p>
                              {enrollment.paid && (
                                <span className="inline-flex items-center text-sm bg-green-100 text-green-700 px-3 py-1 rounded">
                                  <CheckCircle size={16} className="mr-1" />
                                  Paid
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 items-end">
                              {userRatings[course.id] ? (
                                <div className="flex items-center bg-yellow-50 px-3 py-1 rounded-lg border border-yellow-200">
                                  <Star className="text-yellow-400 fill-yellow-400" size={18} />
                                  <span className="text-sm font-semibold ml-1">
                                    {userRatings[course.id].stars} star{userRatings[course.id].stars !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              ) : enrollment.completed && hasPassedQuiz(course.id) && (
                                <button
                                  onClick={() => openManualRating(course.id)}
                                  className="flex items-center bg-purple-100 text-purple-700 px-3 py-1 rounded-lg hover:bg-purple-200 text-sm font-semibold"
                                >
                                  <Star size={16} className="mr-1" />
                                  Rate This Course
                                </button>
                              )}
                            </div>
                            
                            <div className="text-right">
                              {enrollment.completed && (
                                <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded text-sm font-semibold mb-2">
                                  ✓ Completed
                                </span>
                              )}
                            </div>
                          </div>

                          {(course.status === 'draft' || course.status === 'pending' || course.status === 'rejected') ? (
                            <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-6 text-center">
                              <AlertCircle size={48} className="mx-auto text-yellow-600 mb-3" />
                              <h4 className="font-bold text-yellow-900 text-lg mb-2">
                                ⏰ Course Temporarily Unavailable
                              </h4>
                              <p className="text-yellow-800 text-sm mb-3">
                                This course is being updated and reviewed by our team.
                              </p>
                              <p className="text-yellow-700 text-xs">
                                You'll be able to access it again once the updates are approved. This typically takes 24-48 hours.
                              </p>
                            </div>
                          ) : (
                            <>
                              {/* Videos, Files, Quiz sections - keeping existing code */}
                              {videos.length > 0 && (
                                <div className="mb-6">
                                  <h4 className="font-semibold mb-3 flex items-center">
                                    <Video size={20} className="mr-2 text-purple-600" />
                                    Course Videos
                                  </h4>
                                 
                                </div>
                              )}
                              
                              {/* Progress bar */}
                              <div className="mb-4">
                                <div className="flex items-center justify-between text-sm mb-2">
                                  <span className="text-gray-600">Progress</span>
                                  <span className="font-semibold text-purple-600">{enrollment.progress || 0}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-purple-600 h-2 rounded-full transition-all"
                                    style={{ width: `${enrollment.progress || 0}%` }}
                                  ></div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
          
          {/* ENROLLED COURSE MODAL */}
{viewingEnrolledCourse && (
  <>
    {/* Overlay */}
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999]"
      onClick={() => { setViewingEnrolledCourse(null); setExpandedVideoId(null); }}
    />
    {/* Bottom sheet */}
    <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[9999] max-h-[90vh] flex flex-col shadow-2xl">
      {/* Handle + close */}
      <div className="flex justify-center pt-3 pb-1 flex-shrink-0 relative">
        <div className="w-10 h-1 bg-gray-300 rounded-full" />
        <button
          onClick={() => { setViewingEnrolledCourse(null); setExpandedVideoId(null); }}
          className="absolute right-4 top-2 text-gray-400 hover:text-gray-600 touch-target"
        >
          <X size={22} />
        </button>
      </div>
      {/* Title + provider */}
      <div className="px-5 pt-2 pb-3 border-b flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-900 leading-tight mb-0.5">{viewingEnrolledCourse.course.title}</h2>
        <p className="text-sm text-gray-400">by {viewingEnrolledCourse.course.provider || 'Provider'}</p>
        <p className="text-xs text-gray-500 mt-1">
          {professions.find(p => p.id === viewingEnrolledCourse.course.profession)?.name || viewingEnrolledCourse.course.profession} • {viewingEnrolledCourse.course.hours} CEU Hours
        </p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
        
        {/* Progress Bar */}
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Course Progress</span>
            <span className="font-semibold text-purple-600">{viewingEnrolledCourse.enrollment.progress || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-purple-600 h-3 rounded-full transition-all"
              style={{ width: `${viewingEnrolledCourse.enrollment.progress || 0}%` }}
            ></div>
          </div>
        </div>

        {/* Course Videos Section */}
        {courseVideos[viewingEnrolledCourse.course.id]?.length > 0 && (
          <div>
            <h3 className="text-lg font-bold mb-3 flex items-center">
              <Video size={20} className="mr-2 text-purple-600" />
              Course Videos ({courseVideos[viewingEnrolledCourse.course.id].filter(v => watchedVideos[`${viewingEnrolledCourse.course.id}_${v.id}`]).length}/{courseVideos[viewingEnrolledCourse.course.id].length} watched)
            </h3>
            
            <div className="space-y-2">
              {courseVideos[viewingEnrolledCourse.course.id].map((video, index) => {
                const isWatched = watchedVideos[`${viewingEnrolledCourse.course.id}_${video.id}`];
                const isExpanded = expandedVideoId === video.id;
                
                return (
                  <div key={video.id} className="border rounded-lg overflow-hidden">
                    {/* Video Header */}
                    <button
                      onClick={() => setExpandedVideoId(isExpanded ? null : video.id)}
                      className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition text-left"
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-shrink-0 mr-3">
                          {isWatched ? (
                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <CheckCircle size={16} className="text-white" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                              <Play size={12} className="text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{video.videoName || `Video ${index + 1}`}</p>
                          {isWatched && (
                            <p className="text-xs text-green-600">Completed</p>
                          )}
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0">
                        {isExpanded ? <X size={20} /> : <Play size={20} />}
                      </div>
                    </button>
                    
                    {/* Video Player (Expanded) */}
                    {isExpanded && (
                      <div className="p-4 bg-white">
                        {video.videoType === 'youtube' ? (
                          <YouTubePlayer
                            video={video}
                            course={viewingEnrolledCourse.course}
                            onClose={() => setExpandedVideoId(null)}
                          />
                        ) : (
                          <div className="text-center text-gray-500 py-8">
                            <p>Video type not supported</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Course Materials Section */}
        {courseFiles[viewingEnrolledCourse.course.id]?.length > 0 && (
          <div>
            <h3 className="text-lg font-bold mb-3 flex items-center">
              <FileText size={20} className="mr-2 text-purple-600" />
              Course Materials ({(materialProgress[viewingEnrolledCourse.course.id]?.downloadedFiles || []).length}/{courseFiles[viewingEnrolledCourse.course.id].length} viewed)
            </h3>
            
            <div className="space-y-2">
              {courseFiles[viewingEnrolledCourse.course.id].map(file => {
                const isDownloaded = (materialProgress[viewingEnrolledCourse.course.id]?.downloadedFiles || []).includes(file.id);
                
                return (
                  <div key={file.id} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0 mr-4">
                      <FileText size={20} className="text-gray-600 mr-3 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate whitespace-nowrap overflow-hidden">{file.fileName}</p>
                        <p className="text-xs text-gray-500">{file.fileSize}</p>
                      </div>
                      {isDownloaded && (
                        <CheckCircle size={20} className="text-green-500 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          await Browser.open({ url: file.downloadURL });
                          await trackFileDownload(file.id, viewingEnrolledCourse.course.id);
                        }}
                        className="bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-1 whitespace-nowrap text-sm"
                      >
                        <Eye size={16} />
                        View
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(file.downloadURL);
                            const blob = await response.blob();
                            const reader = new FileReader();
                            reader.onloadend = async () => {
                              const base64Data = reader.result.split(',')[1];
                              await Filesystem.writeFile({
                                path: file.fileName,
                                data: base64Data,
                                directory: Directory.Documents
                              });
                              await trackFileDownload(file.id, viewingEnrolledCourse.course.id);
                              showToast(`${file.fileName} saved to Documents`, 'success');
                            };
                            reader.readAsDataURL(blob);
                          } catch (err) {
                            showToast('Save failed: ' + err.message, 'error');
                          }
                        }}
                        className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-1 whitespace-nowrap text-sm"
                      >
                        <Download size={16} />
                        Save
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quiz Section */}
        {courseQuizzes[viewingEnrolledCourse.course.id] && (
          <div>
            <h3 className="text-lg font-bold mb-3 flex items-center">
              <ClipboardList size={20} className="mr-2 text-purple-600" />
              Course Quiz
            </h3>
            
            {canTakeQuiz(viewingEnrolledCourse.course.id) ? (
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-purple-900 mb-2">Quiz Available</p>
                    <p className="text-sm text-purple-700 mb-4">
                      {courseQuizzes[viewingEnrolledCourse.course.id].questions.length} questions • 
                      Passing score: {courseQuizzes[viewingEnrolledCourse.course.id].passingScore}%
                    </p>
                    {hasPassedQuiz(viewingEnrolledCourse.course.id) ? (
                      <div className="flex items-center text-green-600 mb-4">
                        <CheckCircle size={20} className="mr-2" />
                        <span className="font-semibold">Quiz Passed!</span>
                      </div>
                    ) : null}
                  </div>
                  <button
                    onClick={() => {
                      setViewingEnrolledCourse(null);
                      setExpandedVideoId(null);
                      startQuiz(viewingEnrolledCourse.course.id);
                    }}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold whitespace-nowrap"
                  >
                    {hasPassedQuiz(viewingEnrolledCourse.course.id) ? 'Retake Quiz' : 'Take Quiz'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 text-center">
                <Lock size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="font-semibold text-gray-700 mb-2">Quiz Locked</p>
                <p className="text-sm text-gray-600">
                  Complete all videos and download all materials to unlock the quiz
                </p>
              </div>
            )}
          </div>
        )}

        {/* Certificate Section */}
        {hasPassedQuiz(viewingEnrolledCourse.course.id) && viewingEnrolledCourse.enrollment.completed && (
          <div>
            <h3 className="text-lg font-bold mb-3 flex items-center">
              <Award size={20} className="mr-2 text-purple-600" />
              Certificate of Completion
            </h3>
            
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center text-green-600 mb-2">
                    <CheckCircle size={24} className="mr-2" />
                    <span className="font-bold text-lg">Course Completed!</span>
                  </div>
                  <p className="text-sm text-green-700 mb-4">
                    Congratulations! You've successfully completed this course.
                  </p>
                </div>
                <button
  onClick={() => generateCertificate(viewingEnrolledCourse.course.id)}
  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2 text-sm"
>
  <Download size={16} />
  Get Certificate
</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  </>
)}

          {/* REDEEM TAB */}
          {studentTab === 'redeem' && (
            <div className="max-w-md mx-auto px-4 py-12">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="text-center mb-6">
                  <Gift size={48} className="mx-auto text-purple-600 mb-3" />
                  <h2 className="text-2xl font-bold mb-2">Redeem Code</h2>
                  <p className="text-sm text-gray-600">
                    Enter your enrollment or promo code below to access courses or discounts.
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2">Code</label>
                  <input
                    type="text"
                    value={redemptionCodeInput}
                    onChange={(e) => setRedemptionCodeInput(e.target.value.toUpperCase())}
                    placeholder="MENV-X7K9P2"
                    className="w-full border-2 rounded-lg px-4 py-3 uppercase text-lg font-mono"
                    disabled={redeeming}
                  />
                </div>
                
                <button
                  onClick={redeemCode}
                  disabled={redeeming || !redemptionCodeInput.trim()}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 font-semibold disabled:bg-gray-400"
                >
                  {redeeming ? 'Redeeming...' : 'Redeem Code'}
                </button>
              </div>
            </div>
          )}

          {/* PROFILE TAB */}
          {studentTab === 'profile' && (
            <div className="max-w-md mx-auto px-4 py-6">
              <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <User size={32} className="text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{currentUser?.fullName}</h2>
                    <p className="text-sm text-gray-600">{currentUser?.email}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Courses Enrolled</span>
                    <span className="font-semibold">{enrolledCourses.length}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Courses Completed</span>
                    <span className="font-semibold">
                      {enrolledCourses.filter(e => e.completed).length}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* License Expiration & Notifications */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                <h3 className="text-lg font-bold mb-4">License Expiration & Reminders</h3>

                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">License Expiration Date</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={licenseEditDate}
                      onChange={(e) => setLicenseEditDate(e.target.value)}
                      className="flex-1 border rounded-lg px-3 py-2 outline-none"
                    />
                    <button
                      onClick={async () => {
                        if (!licenseEditDate) return;
                        const iso = new Date(licenseEditDate + 'T12:00:00').toISOString();
                        try {
                          await window.firebaseDB.updateDoc(`users/${currentUser.uid}`, { licenseExpirationDate: iso });
                          setCurrentUser({ ...currentUser, licenseExpirationDate: iso });
                          showToast('License expiration saved!', 'success');
                        } catch (err) {
                          showToast('Error saving date', 'error');
                        }
                      }}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm font-medium"
                    >
                      Save
                    </button>
                  </div>
                </div>

                <label className="flex items-start cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={currentUser?.pushNotificationsEnabled === true}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      setCurrentUser({ ...currentUser, pushNotificationsEnabled: newValue });
                      try {
                        await window.firebaseDB.updateDoc(`users/${currentUser.uid}`, { pushNotificationsEnabled: newValue });
                        showToast('Reminder preference saved!', 'success');
                      } catch (err) {
                        setCurrentUser({ ...currentUser, pushNotificationsEnabled: !newValue });
                        showToast('Failed to save preference', 'error');
                      }
                    }}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 block mb-1">
                      Send me reminders before my license expires
                    </span>
                    <span className="text-xs text-gray-600 block">
                      We'll notify you 3, 2, and 1 month before your license expires
                    </span>
                  </div>
                </label>

                {currentUser?.pushNotificationsEnabled && notificationPermission !== 'granted' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
                    <p className="text-sm text-yellow-800">
                      {notificationPermission === 'denied'
                        ? 'Notifications are blocked — enable in device settings to receive reminders.'
                        : 'Enable device notifications to receive license expiration reminders.'}
                    </p>
                    <button
                      onClick={() => {
                        if (notificationPermission === 'denied') {
                          Browser.open({ url: 'app-settings:' });
                        } else {
                          registerDeviceToken(currentUser.uid);
                        }
                      }}
                      className="ml-3 flex-shrink-0 bg-yellow-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-yellow-700"
                    >
                      Enable
                    </button>
                  </div>
                )}

                {currentUser?.pushNotificationsEnabled && notificationPermission === 'granted' && (
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle size={16} className="mr-2" />
                    Reminders active
                  </div>
                )}
              </div>


              {/* My Certificates - Item 12 */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                <h3 className="text-lg font-bold mb-4">My Certificates</h3>
                
                {enrolledCourses.filter(e => e.completed).length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-8">
                    Complete a course to earn your first certificate!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {enrolledCourses
                      .filter(e => e.completed)
                      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
                      .map(enrollment => {
                        const course = courses.find(c => c.id === enrollment.courseId);
                        if (!course) return null;
                        
                        const certSettings = courseCredentials[course.id];
                        const professionData = professions.find(p => p.id === course.profession);
                        
                        // Calculate expiration text based on type
                        let expirationText = '';
                        const expirationType = certSettings?.expirationType || 'type2';
                        
                        if (expirationType === 'type1' && certSettings?.expirationFixedDate) {
                          const expDate = new Date(certSettings.expirationFixedDate);
                          expirationText = `Expires: ${expDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}`;
                        } else if (expirationType === 'type3' && currentUser?.providerExpiration) {
                          const expDate = new Date(currentUser.providerExpiration);
                          expirationText = `Provider approved until: ${expDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}`;
                        } else if (expirationType === 'type4' && certSettings?.expirationYears) {
                          const completionDate = new Date(enrollment.completedAt);
                          const expDate = new Date(completionDate);
                          expDate.setFullYear(expDate.getFullYear() + parseInt(certSettings.expirationYears));
                          expirationText = `Valid through: ${expDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}`;
                        } else {
                          expirationText = 'No expiration';
                        }
                        
                        return (
                          <div key={enrollment.id} className="border rounded-lg p-4 hover:shadow-md transition">
                            <h4 className="font-bold text-lg mb-2">{course.title}</h4>
                            <div className="text-sm text-gray-600 space-y-1 mb-3">
                              <p>{course.hours} {professionData?.creditLabel || 'CEU Hours'} • {professionData?.name || course.profession}</p>
                              <p>Completed: {new Date(enrollment.completedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}</p>
                              <p className="text-purple-600 font-medium">{expirationText}</p>
                            </div>
                            <button
                              onClick={() => generateCertificate(course.id)}
                              disabled={generatingCertificate}
                              className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 text-sm font-semibold"
                            >
                              {generatingCertificate ? 'Generating...' : 'Download Certificate PDF'}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setShowDeleteAccountModal(true)}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 flex items-center justify-center mb-3"
              >
                Delete Account
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 flex items-center justify-center"
              >
                <LogOut size={20} className="mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>

        {/* BOTTOM TAB BAR - NATIVE STYLE */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom z-50">
          <div className="flex items-center justify-around h-16">
            <button
              onClick={() => { setStudentTab('browse'); setHasUserSorted(false); }}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                studentTab === 'browse' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <Home size={24} />
              <span className="text-xs mt-1">Browse</span>
            </button>
            
            <button
              onClick={() => setStudentTab('courses')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                studentTab === 'courses' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <GraduationCap size={24} />
              <span className="text-xs mt-1">My Courses</span>
            </button>
            
            <button
              onClick={() => setStudentTab('redeem')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                studentTab === 'redeem' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <Gift size={24} />
              <span className="text-xs mt-1">Redeem</span>
            </button>
            
            
            <button
              onClick={() => {
                setHelpForm({ name: currentUser?.fullName || '', email: currentUser?.email || '', message: '' });
                setShowHelpModal(true);
              }}
              className="flex-1 flex flex-col items-center justify-center h-full transition text-gray-500"
            >
              <Mail size={24} />
              <span className="text-xs mt-1">Help</span>
            </button>
            <button
              onClick={() => setStudentTab('profile')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                studentTab === 'profile' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <User size={24} />
              <span className="text-xs mt-1">Profile</span>
            </button>
          </div>
        </div>

        {/* COURSE DETAIL MODAL */}
        {showCourseDetailModal && selectedCourseForDetail && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => { setShowCourseDetailModal(false); setSelectedCourseForDetail(null); }}
            />
            {/* Bottom sheet */}
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 max-h-[90vh] flex flex-col shadow-2xl">
              {/* Handle + close */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0 relative">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
                <button
                  onClick={() => { setShowCourseDetailModal(false); setSelectedCourseForDetail(null); }}
                  className="absolute right-4 top-2 text-gray-400 hover:text-gray-600 touch-target"
                >
                  <X size={22} />
                </button>
              </div>
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-5 pt-2 pb-4">
                {/* Hero */}
                {selectedCourseForDetail.featuredImage ? (
                  <img
                    src={selectedCourseForDetail.featuredImage}
                    alt={selectedCourseForDetail.title}
                    className="w-full h-44 object-cover rounded-xl mb-4"
                  />
                ) : (
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-36 rounded-xl mb-4" />
                )}
                {/* Badges */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {selectedCourseForDetail.isFeatured && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-semibold">⭐ Featured</span>
                  )}
                  {selectedCourseForDetail.isCEU ? (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold">🎓 CEU</span>
                  ) : (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">📚 Educational</span>
                  )}
                  {selectedCourseForDetail.isCEU && selectedCourseForDetail.accreditationNumber && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">✓ CEU Approved</span>
                  )}
                </div>
                {/* Title */}
                <h2 className="text-2xl font-bold text-gray-900 leading-tight mb-1">{selectedCourseForDetail.title}</h2>
                <p className="text-sm text-gray-500 mb-3">by {selectedCourseForDetail.provider || selectedCourseForDetail.providerName || 'Provider'}</p>
                {/* Rating + hours row */}
                <div className="flex items-center gap-4 mb-4">
                  {selectedCourseForDetail.totalRatings > 0 && (
                    <div className="flex items-center gap-1">
                      <Star size={15} className="fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-semibold">{selectedCourseForDetail.averageRating}</span>
                      <span className="text-xs text-gray-400">({selectedCourseForDetail.totalRatings})</span>
                    </div>
                  )}
                  <span className="text-sm text-gray-600 font-medium">{selectedCourseForDetail.hours} {selectedCourseForDetail.isCEU ? 'CEU Hours' : 'Hours'}</span>
                </div>
                {/* Description */}
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-gray-800 mb-1.5">About This Course</h3>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{selectedCourseForDetail.description}</p>
                </div>
                {/* Compliance */}
                {selectedCourseForDetail.isCEU && (
                  <div className="mb-4">
                    <h3 className="text-sm font-bold text-gray-800 mb-2">Approval Information</h3>
                    {selectedCourseForDetail.approvalEntries?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {selectedCourseForDetail.approvalEntries.map((entry, i) => {
                          const isExpired = entry.expirationDate && new Date(entry.expirationDate) < new Date();
                          return (
                            <div key={i} className={`rounded-xl p-3 text-sm ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                              {entry.governingBody && <p className="font-medium text-gray-800">{entry.governingBody}</p>}
                              {entry.approvalNumber && <p className="text-gray-600">Approval #: {entry.approvalNumber}</p>}
                              {entry.expirationDate && (
                                <p className={isExpired ? 'text-red-600 font-medium text-xs' : 'text-gray-500 text-xs'}>
                                  Expires: {new Date(entry.expirationDate).toLocaleDateString()}
                                  {isExpired && <span className="ml-1.5 bg-red-600 text-white px-1.5 py-0.5 rounded">Expired</span>}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {selectedCourseForDetail.approvedStates?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-600 mb-1.5">
                          Approved in {selectedCourseForDetail.approvedStates.length} of 50 states
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {selectedCourseForDetail.approvedStates.map(code => (
                            <span key={code} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">{code}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 leading-relaxed">Approval status is declared by the provider. You are responsible for verifying this course meets your state board's specific CE requirements.</p>
                  </div>
                )}
                {/* Course content */}
                {(() => {
                  const videos = courseVideos[selectedCourseForDetail.id] || [];
                  const materials = courseFiles[selectedCourseForDetail.id] || [];
                  if (videos.length === 0 && materials.length === 0) return null;
                  return (
                    <div className="mb-4">
                      <h3 className="text-sm font-bold text-gray-800 mb-2">Course Content</h3>
                      <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                        {videos.map((video, idx) => (
                          <div key={video.id} className="flex items-center gap-2 text-sm text-gray-700">
                            <Video size={13} className="text-purple-500 flex-shrink-0" />
                            <span className="truncate">{video.videoName || `Video ${idx + 1}`}</span>
                          </div>
                        ))}
                        {materials.map((material, idx) => (
                          <div key={material.id} className="flex items-center gap-2 text-sm text-gray-700">
                            <FileText size={13} className="text-green-500 flex-shrink-0" />
                            <span className="truncate">{material.fileName || `Material ${idx + 1}`}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {/* Quiz info — condensed to one line */}
                {(() => {
                  const quiz = courseQuizzes[selectedCourseForDetail.id];
                  if (!quiz || !quiz.questions || quiz.questions.length === 0) return null;
                  return (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 bg-blue-50 rounded-xl px-3 py-2.5 text-sm text-blue-800">
                        <ClipboardList size={15} className="flex-shrink-0" />
                        <span>Final Quiz · {quiz.questions.length} questions · {quiz.passingScore || 70}% to pass</span>
                      </div>
                    </div>
                  );
                })()}
                {/* Bookmark */}
                <div className="flex justify-end pb-2">
                  <button
                    onClick={() => toggleBookmark(selectedCourseForDetail.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition text-sm ${
                      isBookmarked(selectedCourseForDetail.id)
                        ? 'bg-yellow-50 border-yellow-400 text-yellow-700'
                        : 'border-gray-200 text-gray-500 hover:border-yellow-400'
                    }`}
                  >
                    <Star size={15} className={isBookmarked(selectedCourseForDetail.id) ? 'fill-yellow-500 text-yellow-500' : ''} />
                    {isBookmarked(selectedCourseForDetail.id) ? 'Saved' : 'Save'}
                  </button>
                </div>
              </div>
              {/* Sticky bottom: price + purchase/enrolled */}
              <div className="flex-shrink-0 px-5 py-4 border-t bg-white">
                {(() => {
                  const enrollment = enrolledCourses.find(e => e.courseId === selectedCourseForDetail.id);
                  return enrollment ? (
                    <div className="space-y-2">
                      <div className="bg-green-100 text-green-700 py-2.5 px-4 rounded-xl text-center font-semibold flex items-center justify-center text-sm">
                        <CheckCircle size={16} className="mr-2" />
                        {enrollment.paid ? 'Enrolled & Paid' : 'Enrolled'}
                      </div>
                      <button
                        onClick={() => {
                          setShowCourseDetailModal(false);
                          setStudentTab('courses');
                        }}
                        className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition"
                      >
                        Continue Learning →
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedState !== 'all' && selectedCourseForDetail.approvedStates?.length > 0 && !selectedCourseForDetail.approvedStates.includes(selectedState) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-sm text-amber-800">
                          ⚠️ This course is not approved for CEUs in {ALL_US_STATES.find(s => s.code === selectedState)?.name || selectedState}. Please verify with your state board before purchasing.
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-purple-700">${selectedCourseForDetail.price}</span>
                        <button
                          onClick={() => {
                            handlePurchaseCourse(selectedCourseForDetail);
                          }}
                          className="flex-1 bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition text-base"
                        >
                          Purchase Course
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </>
        )}

       {/* QUIZ TAKING MODAL */}
        {takingQuiz && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
              {!quizResults ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold">Course Quiz</h3>
                    <button onClick={closeQuiz} className="text-gray-500 hover:text-gray-700 touch-target">
  <X size={24} />
</button>
                  </div>
                  
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">Progress:</span>
                      <span className="text-sm text-blue-600 font-bold">
                        {getAnsweredCount()} / {takingQuiz.questions.length} answered
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${(getAnsweredCount() / takingQuiz.questions.length) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Passing Score: {takingQuiz.passingScore}%
                    </p>
                  </div>
                  
                  {takingQuiz.quizFormat === 'one-per-screen' ? (
                    <>
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold">
                            Question {currentQuizQuestion + 1} of {takingQuiz.questions.length}
                          </h4>
                        </div>
                        
                        {(() => {
                          const question = takingQuiz.questions[currentQuizQuestion];
                          return (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="font-medium mb-4">{question.question}</p>
                              
                              {question.hasImage && question.imageURL && (
                                <img
                                  src={question.imageURL}
                                  alt="Question"
                                  className="w-full max-w-md mx-auto rounded-lg mb-4"
                                />
                              )}
                              
                              <div className="space-y-3">
                                {question.options.map((option, optionIndex) => (
                                  <button
                                    key={optionIndex}
                                    onClick={() => selectAnswer(currentQuizQuestion, optionIndex)}
                                    className={`w-full text-left p-3 rounded-lg border-2 transition ${
                                      quizAnswers[currentQuizQuestion] === optionIndex
                                        ? 'border-blue-600 bg-blue-50'
                                        : 'border-gray-300 hover:border-blue-400'
                                    }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => setCurrentQuizQuestion(Math.max(0, currentQuizQuestion - 1))}
                          disabled={currentQuizQuestion === 0}
                          className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600 disabled:bg-gray-300"
                        >
                          Previous
                        </button>
                        
                        {currentQuizQuestion === takingQuiz.questions.length - 1 ? (
                          <button
                            onClick={submitQuiz}
                            disabled={!allQuestionsAnswered()}
                            className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
                          >
                            Submit Quiz
                          </button>
                        ) : (
                          <button
                            onClick={() => setCurrentQuizQuestion(Math.min(takingQuiz.questions.length - 1, currentQuizQuestion + 1))}
                            className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
                          >
                            Next
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-6 mb-6 max-h-96 overflow-y-auto">
                        {takingQuiz.questions.map((question, qIndex) => (
                          <div key={qIndex} className="bg-gray-50 rounded-lg p-4">
                            <p className="font-medium mb-3">
                              {qIndex + 1}. {question.question}
                            </p>
                            
                            {question.hasImage && question.imageURL && (
                              <img
                                src={question.imageURL}
                                alt="Question"
                                className="w-full max-w-md mx-auto rounded-lg mb-4"
                              />
                            )}
                            
                            <div className="space-y-2">
                              {question.options.map((option, optionIndex) => (
                                <button
                                  key={optionIndex}
                                  onClick={() => selectAnswer(qIndex, optionIndex)}
                                  className={`w-full text-left p-3 rounded-lg border-2 transition ${
                                    quizAnswers[qIndex] === optionIndex
                                      ? 'border-blue-600 bg-blue-50'
                                      : 'border-gray-300 hover:border-blue-400'
                                  }`}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <button
                        onClick={submitQuiz}
                        disabled={!allQuestionsAnswered()}
                        className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
                      >
                        Submit Quiz
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    {quizResults.passed ? (
                      <div className="bg-green-50 p-6 rounded-lg">
                        <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-green-800 mb-2">Congratulations!</h3>
                        <p className="text-gray-700 mb-4">You passed the quiz!</p>
                        <p className="text-3xl font-bold text-green-600 mb-2">
                          {quizResults.score.toFixed(1)}%
                        </p>
                        <p className="text-sm text-gray-600">
                          {quizResults.correctAnswers} correct out of {quizResults.totalQuestions}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-red-50 p-6 rounded-lg">
                        <X size={64} className="text-red-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-red-800 mb-2">Not Quite</h3>
                        <p className="text-gray-700 mb-4">You need {quizResults.passingScore}% to pass</p>
                        <p className="text-3xl font-bold text-red-600 mb-2">
                          {quizResults.score.toFixed(1)}%
                        </p>
                        <p className="text-sm text-gray-600">
                          {quizResults.correctAnswers} correct out of {quizResults.totalQuestions}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                    {quizResults.questions.map((question, qIndex) => {
                      const studentAnswer = quizResults.studentAnswers[qIndex];
                      const isCorrect = studentAnswer === question.correctAnswer;
                      
                      return (
                        <div key={qIndex} className={`p-4 rounded-lg border-2 ${
                          isCorrect ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                        }`}>
                          <div className="flex items-start mb-2">
                            {isCorrect ? (
                              <CheckCircle size={20} className="text-green-600 mr-2 mt-1" />
                            ) : (
                              <X size={20} className="text-red-600 mr-2 mt-1" />
                            )}
                            <p className="font-medium flex-1">{question.question}</p>
                          </div>
                          
                          <div className="ml-7 text-sm">
                            <p className={isCorrect ? 'text-green-700' : 'text-red-700'}>
                              Your answer: {question.options[studentAnswer]}
                            </p>
                            {!isCorrect && (
                              <p className="text-green-700 mt-1">
                                Correct answer: {question.options[question.correctAnswer]}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={retakeQuiz}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
                    >
                      Retake Quiz
                    </button>
                    <button
                      onClick={closeQuiz}
                      className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* RETAKE WARNING MODAL */}
        {showRetakeWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={32} className="text-yellow-500 mr-3" />
                <h3 className="text-xl font-bold">Retake Quiz?</h3>
              </div>
              
              <p className="text-gray-700 mb-4">
                Questions will be randomized. Your previous score will be replaced with your new score.
              </p>
              
              {(() => {
                const courseId = takingQuiz?.courseId || quizResults?.courseId;
                const hasPassed = hasPassedQuiz(courseId);
                
                return hasPassed ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-green-800">
                      <strong>✓ You've already passed this course.</strong>
                      <br />
                      Retaking is for practice only - your completion status and certificate will not be affected.
                    </p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-blue-800">
                      This is a new attempt. Your score will determine if you pass the course.
                    </p>
                  </div>
                );
              })()}
              
              <div className="flex gap-3">
                <button
                  onClick={cancelRetake}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRetake}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
                >
                  Yes, Retake
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RATING PROMPT MODAL */}
        {showRatingPrompt && ratingCourseId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">🎉 Congratulations!</h3>
                  <p className="text-gray-600">
                    You've completed{' '}
                    <strong>
                      {courses.find(c => c.id === ratingCourseId)?.title}
                    </strong>
                  </p>
                </div>
                
                <div className="my-6">
                  <p className="text-sm font-semibold mb-3">How would you rate this course?</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map(stars => (
                      <button
                        key={stars}
                        onMouseEnter={() => setHoverRating(stars)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setSelectedRating(stars)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          size={40}
                          className={`${
                            stars <= (hoverRating || selectedRating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  {selectedRating > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedRating} star{selectedRating !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRatingPrompt(false);
                      setRatingCourseId(null);
                      setSelectedRating(0);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Maybe Later
                  </button>
                  <button
                    onClick={submitRating}
                    disabled={!selectedRating || submittingRating}
                    className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 font-semibold disabled:bg-gray-400"
                  >
                    {submittingRating ? 'Submitting...' : 'Submit Rating'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* MANUAL RATING MODAL */}
        {showManualRating && manualRatingCourseId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Rate This Course</h3>
                <button
                  onClick={() => {
                    setShowManualRating(false);
                    setManualRatingCourseId(null);
                    setSelectedRating(0);
                  }}
                  className="text-gray-500 hover:text-gray-700 touch-target"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="mb-2">
                <p className="text-gray-700 font-medium">
                  {courses.find(c => c.id === manualRatingCourseId)?.title}
                </p>
              </div>
              
              <div className="my-6">
                <p className="text-sm font-semibold mb-3 text-center">Select your rating</p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map(stars => (
                    <button
                      key={stars}
                      onMouseEnter={() => setHoverRating(stars)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setSelectedRating(stars)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        size={40}
                        className={`${
                          stars <= (hoverRating || selectedRating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {selectedRating > 0 && (
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    {selectedRating} star{selectedRating !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowManualRating(false);
                    setManualRatingCourseId(null);
                    setSelectedRating(0);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={submitManualRating}
                  disabled={!selectedRating || submittingRating}
                  className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 font-semibold disabled:bg-gray-400"
                >
                  {submittingRating ? 'Submitting...' : 'Submit Rating'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* HELP MODAL */}
        {showHelpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Need Help?</h2>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Send us a message and we'll get back to you as soon as possible.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={helpForm.name}
                    onChange={(e) => setHelpForm({...helpForm, name: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={helpForm.email}
                    onChange={(e) => setHelpForm({...helpForm, email: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How can we help?
                  </label>
                  <textarea
                    value={helpForm.message}
                    onChange={(e) => setHelpForm({...helpForm, message: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2 h-32 resize-none"
                    placeholder="Describe your issue or question..."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleHelpSubmit}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Send Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PRE-PURCHASE ACKNOWLEDGMENT MODAL */}
        {pendingPurchaseCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={28} className="text-yellow-500 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-900">Before You Purchase</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Please read and acknowledge the following before completing your purchase of{' '}
                <span className="font-semibold">{pendingPurchaseCourse.course.title}</span>.
              </p>
              <label className="flex items-start gap-3 cursor-pointer mb-6">
                <input
                  type="checkbox"
                  checked={purchaseAckChecked}
                  onChange={(e) => setPurchaseAckChecked(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-purple-600 flex-shrink-0"
                />
                <span className="text-sm text-gray-700">
                  I understand that receiving a certificate of completion and requesting a refund will result in a permanent ban from the platform.
                </span>
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => { setPendingPurchaseCourse(null); setPurchaseAckChecked(false); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPurchase}
                  disabled={!purchaseAckChecked}
                  className="flex-1 bg-purple-600 text-white py-2 rounded-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Confirm Purchase
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE ACCOUNT MODAL */}
        {showDeleteAccountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={28} className="text-red-500 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-900">Delete Account?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                This will permanently delete your account and all associated data. This cannot be undone.
              </p>
              {currentUser?.authProvider !== 'google' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enter your password to confirm</label>
                  <div className="flex items-center border rounded-lg px-3 py-2">
                    <Lock size={18} className="text-gray-400 mr-2" />
                    <input
                      type="password"
                      value={deleteAccountPassword}
                      onChange={(e) => setDeleteAccountPassword(e.target.value)}
                      className="flex-1 outline-none text-sm"
                      placeholder="Your password"
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteAccountModal(false); setDeleteAccountPassword(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 font-semibold disabled:bg-gray-400"
                >
                  {deletingAccount ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

if (currentScreen === 'provider-dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <ToastContainer />
        {/* Simple Header */}
        <div className="bg-white shadow-sm safe-top">
          <div className="px-4 py-3 flex items-center justify-center">
            <h1 className="text-xl font-bold text-gray-800">
              {providerTab === 'dashboard' ? 'Dashboard' :
               providerTab === 'courses' ? 'My Courses' :
               providerTab === 'earnings' ? 'Earnings' :
               providerTab === 'payouts' ? 'Payout History' :
               'Profile'}
            </h1>
          </div>
        </div>

        {/* Main Content - Flex Grow */}
        <div className="flex-1 overflow-y-auto pb-20">
          
          {/* DASHBOARD TAB */}
          {providerTab === 'dashboard' && (
            <div className="max-w-6xl mx-auto px-4 py-6">
              {/* Stats Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-gray-600 font-semibold">Total Courses</h3>
                    <Book className="text-purple-600" size={24} />
                  </div>
                  <p className="text-3xl font-bold text-gray-800">{providerCourses.length}</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-gray-600 font-semibold">Total Students</h3>
                    <User className="text-blue-600" size={24} />
                  </div>
                  <p className="text-3xl font-bold text-gray-800">
                    {providerCourses.reduce((sum, course) => sum + (course.students || 0), 0)}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-gray-600 font-semibold">Total Revenue</h3>
                    <DollarSign className="text-green-600" size={24} />
                  </div>
                  <p className="text-3xl font-bold text-gray-800">
                    ${providerCourses.reduce((sum, course) => sum + (course.price * (course.students || 0)), 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Quick Earnings Summary */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">Current Month Earnings</h2>

                {(() => {
                  const currentMonth = new Date().getMonth();
                  const currentYear = new Date().getFullYear();
                  const monthTxns = (allTransactions || []).filter(t => {
                    const tDate = new Date(t.purchaseDate);
                    return tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear &&
                           t.providerId === currentUser.uid && !t.refunded;
                  });
                  const pendingTxns   = monthTxns.filter(t => !t.processorCleared && !t.providerPaid);
                  const availableTxns = monthTxns.filter(t => t.processorCleared === true && !t.providerPaid);
                  const paidTxns      = monthTxns.filter(t => t.providerPaid);
                  const pendingAmt   = pendingTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
                  const availableAmt = availableTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
                  const paidAmt      = paidTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
                  return (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-xl font-bold text-yellow-700">${pendingAmt.toFixed(2)}</p>
                        <p className="text-xs text-gray-500 mt-1">Unconfirmed</p>
                        <p className="text-xs text-gray-400 mt-0.5">{pendingTxns.length} transaction{pendingTxns.length !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                        <p className="text-sm text-gray-600">Available</p>
                        <p className="text-xl font-bold text-blue-700">${availableAmt.toFixed(2)}</p>
                        <p className="text-xs text-gray-500 mt-1">Confirmed</p>
                        <p className="text-xs text-gray-400 mt-0.5">{availableTxns.length} transaction{availableTxns.length !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                        <p className="text-sm text-gray-600">Paid</p>
                        <p className="text-xl font-bold text-green-700">${paidAmt.toFixed(2)}</p>
                        <p className="text-xs text-gray-500 mt-1">Paid this month</p>
                        <p className="text-xs text-gray-400 mt-0.5">{paidTxns.length} transaction{paidTxns.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  );
                })()}
                
                <button
                  onClick={() => setProviderTab('earnings')}
                  className="w-full mt-4 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700"
                >
                  View Full Earnings →
                </button>
              </div>
            </div>
          )}

          {/* COURSES TAB */}
          {providerTab === 'courses' && (
            <div className="max-w-6xl mx-auto px-4 py-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">My Courses</h2>
                <button
                  onClick={createNewCourse}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition flex items-center"
                >
                  <Plus size={20} className="mr-2" />
                  Create Course
                </button>
              </div>
              
              {providerCourses.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <Upload className="mx-auto text-gray-400 mb-4" size={64} />
                  <p className="text-gray-600 mb-4">You haven't created any courses yet.</p>
                  <button
                    onClick={createNewCourse}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
                  >
                    Create Your First Course
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {providerCourses
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map(course => (
                      (expandedCourseId === course.id || editingCourseId === course.id) ? (
                        /* EXPANDED VIEW */
                        <div key={course.id} className="bg-white rounded-lg shadow-md p-6 border-4 border-slate-600">
                        {/* HEADER SECTION - Editable when editingCourseId matches */}
                        {editingCourseId === course.id ? (
                          // EDIT MODE - Input fields
                          <div className="space-y-3 mb-4">

                            {/* MARKETPLACE BANNER UPLOAD */}
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <h4 className="font-semibold mb-3 text-blue-900">📸 Marketplace Banner</h4>
                              
                              {course.featuredImage ? (
                                <div className="relative">
                                  <img 
                                    src={course.featuredImage} 
                                    alt="Course banner" 
                                    className="w-full h-48 object-cover rounded-lg"
                                  />
                                  <button
                                    onClick={() => removeBanner(course.id)}
                                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                  <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                                  <p className="text-sm text-gray-600 mb-2">No banner uploaded</p>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleBannerSelect(e, course.id)}
                                    className="text-sm"
                                  />
                                </div>
                              )}
                              
                              {selectedBanner && selectedBanner.courseId === course.id && !course.featuredImage && (
                                <div className="mt-3">
                                  <p className="text-sm text-green-600 mb-2">✓ Banner selected, will upload on Save Draft</p>
                                </div>
                              )}
                              
                              {uploadingBanner && (
                                <div className="mt-3">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-blue-600 h-2 rounded-full transition-all"
                                      style={{ width: `${bannerUploadProgress}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1 text-center">
                                    Uploading... {bannerUploadProgress.toFixed(0)}%
                                  </p>
                                </div>
                              )}
                              
                              <p className="text-xs text-gray-500 mt-2">
                                Recommended: 1920x1080px (16:9 ratio). Max 5MB.
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-1">Course Title *</label>
                              <input
                                type="text"
                                value={course.title}
                                onChange={(e) => updateCourseField(course.id, 'title', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                                placeholder="e.g., Advanced Deep Tissue Techniques"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-1">Course Type *</label>
                              <div className="flex gap-4">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    checked={course.isCEU === true}
                                    onChange={() => {
                                      // Check if provider has credentials before allowing CEU selection
                                      if (!currentUser.governingBody || !currentUser.providerNumber || !currentUser.providerExpiration) {
                                        if (confirm('⚠️ CEU Courses Require Credentials\n\nTo create CEU courses, please complete your Provider Settings first.\n\nThis includes:\n• Your profession\n• Governing body name\n• Provider/approval number\n• Credential expiration\n\nGo to Provider Settings now?')) {
                                          setProviderTab('profile');
                                          setShowProviderSettings(true);
                                        }
                                        return;
                                      }
                                      updateCourseField(course.id, 'isCEU', true);
                                    }}
                                    className="mr-2"
                                  />
                                  🎓 CEU Course
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    checked={course.isCEU === false}
                                    onChange={() => updateCourseField(course.id, 'isCEU', false)}
                                    className="mr-2"
                                  />
                                  📚 Educational
                                </label>
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-1">Profession *</label>
                              <select
                                value={course.profession}
                                onChange={(e) => updateCourseField(course.id, 'profession', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                              >
                                {professions.filter(p => p.active).map(prof => (
                                  <option key={prof.id} value={prof.id}>
                                    {prof.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-1">Hours *</label>
                              <input
                                type="number"
                                value={course.hours}
                                onChange={(e) => updateCourseField(course.id, 'hours', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                                placeholder="e.g., 6"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-1">Approval Entries</label>
                              <div className="space-y-2">
                                {(course.approvalEntries || [{ governingBody: '', approvalNumber: '', expirationDate: '' }]).map((entry, idx) => (
                                  <div key={idx} className="flex gap-2 items-start">
                                    <div className="flex-1 grid grid-cols-3 gap-2">
                                      <input
                                        type="text"
                                        value={entry.governingBody}
                                        onChange={(e) => {
                                          const updated = (course.approvalEntries || []).map((en, i) => i === idx ? { ...en, governingBody: e.target.value } : en);
                                          updateCourseField(course.id, 'approvalEntries', updated);
                                        }}
                                        className="w-full px-2 py-2 border rounded-lg text-sm min-w-0"
                                        placeholder="e.g. NCBTMB, Florida Board, ABMP"
                                      />
                                      <input
                                        type="text"
                                        value={entry.approvalNumber}
                                        onChange={(e) => {
                                          const updated = (course.approvalEntries || []).map((en, i) => i === idx ? { ...en, approvalNumber: e.target.value } : en);
                                          updateCourseField(course.id, 'approvalEntries', updated);
                                        }}
                                        className="w-full px-2 py-2 border rounded-lg text-sm min-w-0"
                                        placeholder="Provider/approval number"
                                      />
                                      <input
                                        type="date"
                                        value={entry.expirationDate}
                                        onChange={(e) => {
                                          const updated = (course.approvalEntries || []).map((en, i) => i === idx ? { ...en, expirationDate: e.target.value } : en);
                                          updateCourseField(course.id, 'approvalEntries', updated);
                                        }}
                                        className="w-full px-2 py-2 border rounded-lg text-sm min-w-0"
                                      />
                                    </div>
                                    {(course.approvalEntries || []).length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => updateCourseField(course.id, 'approvalEntries', (course.approvalEntries || []).filter((_, i) => i !== idx))}
                                        className="flex-shrink-0 text-gray-400 hover:text-red-500 text-lg leading-none pt-2"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => updateCourseField(course.id, 'approvalEntries', [...(course.approvalEntries || []), { governingBody: '', approvalNumber: '', expirationDate: '' }])}
                                className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                              >
                                + Add Another Approval
                              </button>
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-1">
                                Approved States — uncheck states where this course is not valid for CEU credit.
                              </label>
                              <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                                  {ALL_US_STATES.map(state => (
                                    <label key={state.code} className="flex items-center gap-2 py-0.5 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={(course.approvedStates || ALL_STATE_CODES).includes(state.code)}
                                        onChange={(e) => {
                                          const current = course.approvedStates || ALL_STATE_CODES;
                                          const next = e.target.checked
                                            ? [...current, state.code]
                                            : current.filter(c => c !== state.code);
                                          updateCourseField(course.id, 'approvedStates', next);
                                        }}
                                        className="h-4 w-4 flex-shrink-0"
                                      />
                                      <span className="text-sm truncate">{state.name}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-1">Price *</label>
                              <input
                                type="number"
                                value={course.price}
                                onChange={(e) => updateCourseField(course.id, 'price', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                                placeholder="e.g., 79.99"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-1">Description *</label>
                              <textarea
                                value={course.description}
                                onChange={(e) => updateCourseField(course.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                                rows="3"
                                placeholder="Describe your course..."
                              />
                            </div>
                            

                          </div>
                        ) : (
                          // VIEW MODE - Current display
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-bold">{course.title}</h3>
                                {course.status === 'pending' && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-semibold">
                                    ⏳ Pending Approval
                                  </span>
                                )}
                                {course.status === 'draft' && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-semibold">
                                    📝 Draft
                                  </span>
                                )}
                                {course.status === 'approved' && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                                    ✓ Approved
                                  </span>
                                )}
                                {course.status === 'rejected' && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-semibold">
                                    ✗ Rejected
                                  </span>
                                )}
                                {course.isCEU ? (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">
                                    🎓 CEU
                                  </span>
                                ) : (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    📚 Educational
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 mb-2">{course.description}</p>
                              <p className="text-sm text-gray-500">
                                {professions.find(p => p.id === course.profession)?.name || course.profession} • 
                                {course.hours} {course.isCEU ? 'CEU Hours' : 'Hours'} • ${course.price}
                              </p>
                              {course.status === 'rejected' && course.rejectionNotes && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <p className="text-sm font-semibold text-red-800 mb-1">Rejection Reason:</p>
                                  <p className="text-sm text-red-700">{course.rejectionNotes}</p>
                                </div>
                              )}
                            </div>
                            
                            <div className="text-right ml-4">
                              <p className="text-2xl font-bold text-purple-600">{course.students || 0}</p>
                              <p className="text-sm text-gray-600">Students</p>
                            </div>
                          </div>
                        )}

                        {/* ENROLLMENT WARNING */}
                        {(() => {
                          const enrolledCount = allEnrollments?.filter(e => e.courseId === course.id)?.length || 0;
                          if (enrolledCount > 0) {
                            return (
                              <div className="mt-4 mb-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                                <div className="flex items-center">
                                  <AlertCircle size={24} className="text-yellow-600 mr-3" />
                                  <div>
                                    <p className="font-semibold text-yellow-900">
                                      ⚠️ {enrolledCount} student{enrolledCount !== 1 ? 's' : ''} enrolled
                                    </p>
                                    <p className="text-sm text-yellow-800">
                                      Adding or removing content may affect student progress and completion status
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* VIDEO UPLOAD SECTION */}
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg mb-3 border border-blue-200">
                          <h4 className="font-semibold mb-3 flex items-center">
                            <Video size={20} className="mr-2 text-blue-600" />
                            Course Videos
                            {course.isCEU && course.status === 'approved' && (
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">🔒 Locked</span>
                            )}
                          </h4>
                          
                          {course.isCEU && course.status === 'approved' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                              <p className="text-sm text-yellow-800">
                                <strong>⚠️ Approved CEU Course:</strong> Cannot add videos. Click "Edit Course" button below to make changes (requires re-approval).
                              </p>
                            </div>
                          )}
                          
                          {courseVideos[course.id]?.length > 0 && (
                            <div className="space-y-2 mb-4">
                              {courseVideos[course.id].map(video => (
                                <div key={video.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                                  <div className="flex items-center">
                                    <Video size={20} className="text-blue-600 mr-3" />
                                    <div>
                                      <p className="font-medium text-sm break-words overflow-hidden">{video.videoName || 'YouTube Video'}</p>
                                      {video.videoType !== 'youtube' && video.videoSize && (
                                        <p className="text-xs text-gray-500">{video.videoSize}</p>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      if (course.status === 'approved') {
                                        showToast('Cannot delete from approved course. Click "Edit Course" to make changes.', 'error');
                                        return;
                                      }
                                      if (video.videoType === 'youtube') {
                                        if (!confirm('Remove this video from the course?')) return;
                                        window.firebaseDB.deleteDoc(`course_videos/${video.id}`).then(() => {
                                          setCourseVideos(prev => ({
                                            ...prev,
                                            [course.id]: (prev[course.id] || []).filter(v => v.id !== video.id)
                                          }));
                                          showToast('Video removed successfully', 'success');
                                        });
                                      } else {
                                        deleteVideo(video.id, video.storagePath, course.id);
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 size={20} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* YOUTUBE LINK INPUT */}
                          {currentUser?.userType !== 'admin' && course.status !== 'approved' && (
                            <>
                              <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mb-3">
                                <p className="text-sm text-blue-900 mb-2">
                                  <strong>📹 Add Video from YouTube:</strong>
                                </p>
                                <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
                                  <li>Upload your video to YouTube</li>
                                  <li>Set privacy to <strong>"Unlisted"</strong> (NOT Private)</li>
                                  <li>Copy the video URL</li>
                                  <li>Paste it below</li>
                                </ol>
                              </div>
                              
                              <div className="space-y-3">
                                <input
                                  type="text"
                                  value={videoTitle}
                                  onChange={(e) => setVideoTitle(e.target.value)}
                                  placeholder="Video title (e.g., 'Introduction to Anatomy')"
                                  className="w-full border rounded-lg px-3 py-2 text-sm"
                                />
                                <div className="flex flex-col sm:flex-row gap-3">
                                  <input
                                    type="text"
                                    value={youtubeUrl}
                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="w-full sm:flex-1 border rounded-lg px-3 py-2 text-sm"
                                  />
                                  <button
                                    onClick={() => addYoutubeVideo(course.id)}
                                    disabled={!youtubeUrl.trim() || addingYoutubeVideo}
                                    className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center text-sm whitespace-nowrap"
                                  >
                                    <Upload size={16} className="mr-2 flex-shrink-0" />
                                    <span>{addingYoutubeVideo ? 'Adding...' : 'Add Video'}</span>
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                          
                          {/* FIREBASE UPLOAD (Admin Only) */}
                          {currentUser?.userType === 'admin' && (
                            <>
                              <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 mb-3">
                                <p className="text-sm text-purple-900">
                                  <strong>👑 Admin:</strong> You can upload videos directly to Firebase Storage (for enterprise clients).
                                </p>
                              </div>
                              
                              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                <input
                                  type="file"
                                  accept="video/mp4"
                                  onChange={handleVideoSelect}
                                  className="w-full sm:flex-1 text-sm"
                                />
                                <button
                                  onClick={() => uploadVideo(course.id)}
                                  disabled={!selectedVideo || uploadingVideo}
                                  className="w-full sm:w-auto bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center text-sm whitespace-nowrap"
                                >
                                  <Upload size={16} className="mr-2 flex-shrink-0" />
                                  <span>{uploadingVideo ? 'Uploading...' : 'Upload'}</span>
                                </button>
                              </div>
                              
                              {uploadingVideo && (
                                <div className="mb-3">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-purple-600 h-2 rounded-full transition-all"
                                      style={{ width: `${videoUploadProgress}%` }}
                                    ></div>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1 text-center">
                                    {videoUploadProgress.toFixed(0)}%
                                  </p>
                                </div>
                              )}
                              
                              <p className="text-xs text-gray-500">
                                Firebase Upload: MP4 only (Max 500MB)
                              </p>
                            </>
                          )}
                        </div>

                        {/* COURSE MATERIALS SECTION */}
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg mb-3">
                          <h4 className="font-semibold mb-3 flex items-center">
                            <FileText size={20} className="mr-2 text-gray-600" />
                            Course Materials
                            {course.isCEU && course.status === 'approved' && (
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">🔒 Locked</span>
                            )}
                          </h4>
                          
                          {course.isCEU && course.status === 'approved' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                              <p className="text-sm text-yellow-800">
                                <strong>⚠️ Approved CEU Course:</strong> Cannot add materials. Click "Edit Course" button below to make changes (requires re-approval).
                              </p>
                            </div>
                          )}
                          
                          {courseFiles[course.id]?.length > 0 && (
                            <div className="space-y-2 mb-4">
                              {courseFiles[course.id].map(file => (
                                <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                                  <div className="flex items-center">
                                    <FileText size={20} className="text-gray-600 mr-3" />
                                    <div>
                                      <p className="font-medium text-sm break-words">{file.fileName}</p>
                                      <p className="text-xs text-gray-500">{file.fileSize} • {file.fileType}</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      if (course.status === 'approved') {
                                        showToast('Cannot delete from approved course. Click "Edit Course" to make changes.', 'error');
                                        return;
                                      }
                                      deleteFile(file.id, file.storagePath, course.id);
                                    }}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 size={20} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex flex-col sm:flex-row gap-3">
                            <input
                              ref={materialFileInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleFileSelect}
                              style={{ display: 'none' }}
                            />
                            <button
                              onClick={() => materialFileInputRef.current.click()}
                              className="w-full sm:flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-left text-gray-600 bg-white hover:bg-gray-50"
                            >
                              {selectedFile ? selectedFile.name : 'Choose File'}
                            </button>
                            <button
                              onClick={() => uploadFile(course.id)}
                              disabled={!selectedFile || uploading || (course.isCEU && course.status === 'approved')}
                              className="w-full sm:w-auto bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center whitespace-nowrap"
                            >
                              <Upload size={16} className="mr-2" />
                              {uploading ? 'Uploading...' : 'Upload'}
                            </button>
                          </div>
                          
                          {uploading && (
                            <div className="mt-3">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-purple-600 h-2 rounded-full transition-all"
                                  style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-600 mt-1 text-center">
                                {uploadProgress.toFixed(0)}%
                              </p>
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-500 mt-2">
                            Supported: PDF, DOC, DOCX (Max 500MB)
                          </p>
                        </div>
                        
                        {/* QUIZ BUILDER SECTION */}
                        <div className="mt-3 p-3 bg-green-50 rounded-lg mb-3 border border-green-200">
                          <h4 className="font-semibold mb-3 flex items-center">
                            <ClipboardList size={20} className="mr-2 text-green-600" />
                            Course Quiz
                            {course.isCEU && course.status === 'approved' && (
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">🔒 Locked</span>
                            )}
                          </h4>
                          
                          {course.isCEU && course.status === 'approved' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                              <p className="text-sm text-yellow-800">
                                <strong>⚠️ Approved CEU Course:</strong> Cannot edit quiz. Click "Edit Course" button below to make changes (requires re-approval).
                              </p>
                            </div>
                          )}
                          
                          {courseQuizzes[course.id] ? (
                            <div className="bg-white rounded-lg p-3 mb-3">
                              <p className="text-sm text-gray-700 mb-1">
                                ✓ Quiz configured with {courseQuizzes[course.id].questions.length} questions
                              </p>
                              <p className="text-xs text-gray-500">
                                Passing score: {courseQuizzes[course.id].passingScore}%
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 mb-3">
                              No quiz created yet. Add a quiz to test student knowledge.
                            </p>
                          )}
                          
                          <button 
                            onClick={() => openQuizBuilder(course.id)}
                            disabled={course.isCEU && course.status === 'approved'}
                            className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center"
                          >
                            <Edit size={16} className="mr-2" />
                            {courseQuizzes[course.id] ? 'Edit Quiz' : 'Create Quiz'}
                          </button>
                        </div>

                        {/* CERTIFICATE SETTINGS SECTION */}
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg mb-3 border border-purple-200">
                          <h4 className="font-semibold mb-3 flex items-center">
                            <Award size={20} className="mr-2 text-purple-600" />
                            Certificate Settings
                          </h4>
                          
                          {courseCredentials[course.id] ? (
                            <div className="bg-white rounded-lg p-3 mb-3">
                              <p className="text-sm text-gray-700 mb-1">
                                ✓ Certificate configured
                              </p>
                              <p className="text-xs text-gray-500">
                                Credentials: {courseCredentials[course.id].credentials}
                              </p>
                              {courseCredentials[course.id].expirationYears && (
                                <p className="text-xs text-gray-500">
                                  Expires after: {courseCredentials[course.id].expirationYears} years
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 mb-3">
                              No certificate settings configured. Set up credentials and signature.
                            </p>
                          )}
                          
                          <button
                            onClick={() => setShowCertificateSettings(course.id)}
                            className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 flex items-center justify-center"
                          >
                            <Settings size={16} className="mr-2" />
                            {courseCredentials[course.id] ? 'Edit Settings' : 'Configure Certificate'}
                          </button>
                        </div>
                        
                        {/* Submit for Review Button - Draft or Rejected courses, not in edit mode */}
                        {(course.status === 'draft' || course.status === 'rejected') && editingCourseId !== course.id && (
                          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                            <div className="flex items-center mb-3">
                              <AlertCircle size={20} className="text-yellow-600 mr-2" />
                              <h4 className="font-semibold text-yellow-800">Draft Course - Not Visible to Students</h4>
                            </div>
                            <p className="text-sm text-gray-700 mb-3">
                              This course is in draft mode. Complete the requirements below and submit for admin review:
                            </p>
                            
                            {/* Requirements Checklist */}
                            <div className="space-y-2 text-sm mb-4">
                              <div className={course.title && course.hours && course.price && course.description ? 'text-green-600' : 'text-gray-600'}>
                                {course.title && course.hours && course.price && course.description ? '✓' : '○'} Course details complete
                              </div>
                              <div className={(courseFiles[course.id]?.length > 0 || courseVideos[course.id]?.length > 0) ? 'text-green-600' : 'text-gray-600'}>
                                {(courseFiles[course.id]?.length > 0 || courseVideos[course.id]?.length > 0) ? '✓' : '○'} At least one video or material uploaded
                              </div>
                              <div className={(courseQuizzes[course.id]?.questions?.length > 0) ? 'text-green-600' : 'text-gray-600'}>
                                {(courseQuizzes[course.id]?.questions?.length > 0) ? '✓' : '○'} Quiz with at least one question
                              </div>
                            </div>
                            
                            <button
                              onClick={() => submitCourseForReview(course.id)}
                              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 flex items-center justify-center"
                            >
                              <CheckCircle size={20} className="mr-2" />
                              Submit for Admin Review
                            </button>
                          </div>
                        )}
                        
                        {/* Edit Button - Hidden for pending courses AND when already editing */}
                        {course.status !== 'pending' && editingCourseId !== course.id && (
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={() => {
                              if (course.status === 'approved') {
                                setCourseToEdit(course);
                                setShowEditWarning(true);
                              } else {
                                // Accordion behavior: expand this, collapse others
                                setExpandedCourseId(course.id);
                                setEditingCourseId(course.id);
                              }
                            }}
                            className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                          >
                            <Edit size={16} className="mr-2" />
                            Edit Course
                          </button>
                        </div>
                        )}

                        {/* Save Draft + Cancel — bottom of card, edit mode only */}
                        {editingCourseId === course.id && (
                          <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                            <button
                              onClick={() => saveDraft(course.id)}
                              className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 font-semibold flex items-center justify-center"
                            >
                              <Save size={18} className="mr-2" />
                              Save Draft
                            </button>
                            <button
                              onClick={() => {
                                setEditingCourseId(null);
                                setExpandedCourseId(null);
                                loadProviderCourses(currentUser.uid);
                              }}
                              className="px-6 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        </div>
                      ) : (
                        /* COLLAPSED VIEW - Compact summary */
                        <div key={course.id} id={`course-card-${course.id}`} className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-300 hover:border-purple-400 transition cursor-pointer">
                          {/* Banner or Gradient */}
                          {course.featuredImage ? (
                            <img src={course.featuredImage} alt={course.title} className="w-full aspect-video object-cover" />
                          ) : (
                            <div className="w-full aspect-video bg-gradient-to-r from-purple-500 to-blue-500"></div>
                          )}
                          
                          {/* Content */}
                          <div className="p-4">
                            {/* Title + Badges */}
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-bold flex-1">{course.title}</h3>
                              {course.status === 'pending' && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-semibold">
                                  ⏳ Pending
                                </span>
                              )}
                              {course.status === 'draft' && (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-semibold">
                                  📝 Draft
                                </span>
                              )}
                              {course.status === 'approved' && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                                  ✓ Approved
                                </span>
                              )}
                              {course.status === 'rejected' && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-semibold">
                                  ✗ Rejected
                                </span>
                              )}
                              {course.isCEU && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">
                                  🎓 CEU
                                </span>
                              )}
                            </div>
                            
                            {/* One-line stats */}
                            <p className="text-sm text-gray-600 mb-3">
                              {professions.find(p => p.id === course.profession)?.name || course.profession} • {course.hours} Hours • ${course.price}
                            </p>
                            
                            {/* Material counts */}
                            <div className="flex gap-4 text-sm text-gray-600 mb-3">
                              <span>📹 {courseVideos[course.id]?.length || 0} videos</span>
                              <span>📄 {courseFiles[course.id]?.length || 0} materials</span>
                              <span>❓ {courseQuizzes[course.id]?.questions?.length || 0} questions</span>
                            </div>
                            
                            {/* Student count */}
                            <p className="text-sm font-semibold text-purple-600 mb-3">
                              👥 {course.students || 0} student{course.students !== 1 ? 's' : ''} enrolled
                            </p>
                            
                            {/* Action buttons */}
                            <div className="flex gap-2">
                              {course.status !== 'pending' && (
                                <button
                                  onClick={() => {
                                    if (course.status === 'approved') {
                                      setCourseToEdit(course);
                                      setShowEditWarning(true);
                                    } else {
                                      setExpandedCourseId(course.id);
                                      setEditingCourseId(course.id);
                                    }
                                  }}
                                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-sm font-semibold"
                                >
                                  Edit Course
                                </button>
                              )}
                              {(course.status === 'draft' || course.status === 'rejected') && (
                                <button
                                  onClick={() => submitCourseForReview(course.id)}
                                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 text-sm font-semibold"
                                >
                                  Submit
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                </div>
              )}
            </div>
          )}

          {/* EARNINGS TAB */}
          {providerTab === 'earnings' && (() => {
            const now = new Date();
            const currentYear = now.getFullYear();

            // All provider txns for YTD (6D)
            const ytdTxns = (allTransactions || []).filter(t =>
              t.providerId === currentUser.uid && !t.refunded &&
              new Date(t.purchaseDate).getFullYear() === currentYear
            );
            const ytdEarned  = ytdTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
            const ytdPaid    = ytdTxns.filter(t => t.providerPaid).reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
            const ytdOutstanding = ytdEarned - ytdPaid;

            // Month navigation bounds (6B)
            const providerTxns = (allTransactions || []).filter(t => t.providerId === currentUser.uid);
            let earliestMonth = earningsViewMonth.month;
            let earliestYear = earningsViewMonth.year;
            if (providerTxns.length > 0) {
              const oldest = providerTxns.reduce((a, b) =>
                new Date(a.purchaseDate) < new Date(b.purchaseDate) ? a : b
              );
              const oldDate = new Date(oldest.purchaseDate);
              earliestMonth = oldDate.getMonth();
              earliestYear = oldDate.getFullYear();
            }
            const atEarliest = earningsViewMonth.year < earliestYear ||
              (earningsViewMonth.year === earliestYear && earningsViewMonth.month <= earliestMonth);
            const atCurrent = earningsViewMonth.month === now.getMonth() && earningsViewMonth.year === now.getFullYear();
            const prevMonth = () => setEarningsViewMonth(prev =>
              prev.month === 0 ? { month: 11, year: prev.year - 1 } : { month: prev.month - 1, year: prev.year }
            );
            const nextMonth = () => setEarningsViewMonth(prev =>
              prev.month === 11 ? { month: 0, year: prev.year + 1 } : { month: prev.month + 1, year: prev.year }
            );

            // Month transactions (6A + 6B)
            const monthTxns = providerTxns.filter(t => {
              const tDate = new Date(t.purchaseDate);
              return tDate.getMonth() === earningsViewMonth.month &&
                     tDate.getFullYear() === earningsViewMonth.year &&
                     !t.refunded;
            });
            const pendingTxns   = monthTxns.filter(t => !t.processorCleared && !t.providerPaid);
            const availableTxns = monthTxns.filter(t => t.processorCleared === true && !t.providerPaid);
            const paidTxns      = monthTxns.filter(t => t.providerPaid);
            const pendingAmt   = pendingTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
            const availableAmt = availableTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
            const paidAmt      = paidTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);

            const monthLabel = new Date(earningsViewMonth.year, earningsViewMonth.month, 1)
              .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            const txStatus = t => {
              if (t.providerPaid) return 'paid';
              if (t.processorCleared === true) return 'available';
              return 'pending';
            };

            return (
              <div className="max-w-6xl mx-auto px-4 py-6">
                {/* 6D — YTD Summary Banner */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 grid grid-cols-3 gap-4">
                  <div className="text-center flex flex-col items-center">
                    <div className="text-xs text-gray-500 mb-1 min-h-[2rem] flex items-end justify-center text-center leading-tight">{currentYear} Total Earned</div>
                    <p className="text-lg font-bold text-purple-700">${ytdEarned.toFixed(2)}</p>
                  </div>
                  <div className="text-center border-x border-purple-200 flex flex-col items-center">
                    <div className="text-xs text-gray-500 mb-1 min-h-[2rem] flex items-end justify-center text-center leading-tight">{currentYear} Total Paid</div>
                    <p className="text-lg font-bold text-green-600">${ytdPaid.toFixed(2)}</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div className="text-xs text-gray-500 mb-1 min-h-[2rem] flex items-end justify-center text-center leading-tight">Outstanding Balance</div>
                    <p className="text-lg font-bold text-yellow-600">${ytdOutstanding.toFixed(2)}</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-1">
                      <h2 className="text-2xl font-bold">Your Earnings</h2>
                    </div>
                    {!currentUser?.w9Submitted && (
                      <button onClick={() => setShowW9Form(true)} className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 text-sm">
                        ⚠ Submit W-9 Required
                      </button>
                    )}
                    {currentUser?.w9Submitted && (
                      <span className="text-green-600 text-sm">✓ W-9 on file</span>
                    )}
                  </div>

                  {/* 6B — Month navigation */}
                  <div className="flex items-center justify-between mb-6 bg-gray-50 rounded-lg px-4 py-3">
                    <button
                      onClick={prevMonth}
                      disabled={atEarliest}
                      className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="font-semibold text-gray-800">{monthLabel}</span>
                    <button
                      onClick={nextMonth}
                      disabled={atCurrent}
                      className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  {/* 6A — Three-state summary cards */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
                      <p className="text-sm text-gray-600">Pending</p>
                      <p className="text-2xl font-bold text-yellow-700">${pendingAmt.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">Unconfirmed</p>
                      <p className="text-xs text-gray-400 mt-0.5">{pendingTxns.length} transaction{pendingTxns.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                      <p className="text-sm text-gray-600">Available</p>
                      <p className="text-2xl font-bold text-blue-700">${availableAmt.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">Confirmed</p>
                      <p className="text-xs text-gray-400 mt-0.5">{availableTxns.length} transaction{availableTxns.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                      <p className="text-sm text-gray-600">Paid</p>
                      <p className="text-2xl font-bold text-green-700">${paidAmt.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">Paid this period</p>
                      <p className="text-xs text-gray-400 mt-0.5">{paidTxns.length} transaction{paidTxns.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Transaction list */}
                  <h3 className="text-lg font-semibold mb-4">Transactions — {monthLabel}</h3>
                  {monthTxns.length > 0 ? (
                    <div className="space-y-3">
                      {monthTxns.map((transaction, index) => {
                        const status = txStatus(transaction);
                        const paymentSource = transaction.paymentSource || 'unknown';
                        const statusColors = {
                          pending:   'bg-yellow-500 text-white',
                          available: 'bg-blue-600 text-white',
                          paid:      'bg-green-600 text-white'
                        };
                        const statusLabels = {
                          pending:   'Pending',
                          available: 'Available',
                          paid:      'Paid'
                        };
                        const cardBg = status === 'paid' ? 'bg-green-50 border-green-200'
                                     : status === 'available' ? 'bg-blue-50 border-blue-200'
                                     : 'bg-yellow-50 border-yellow-200';
                        return (
                          <div key={index} className={`border rounded-lg p-4 ${cardBg}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <p className="font-semibold">{transaction.courseTitle}</p>
                                  <span className={`text-xs px-2 py-1 rounded ${paymentSource === 'apple' ? 'bg-gray-800 text-white' : 'bg-blue-600 text-white'}`}>
                                    {paymentSource === 'apple' ? 'Apple' : paymentSource === 'google' ? 'Google' : 'Unknown'}
                                  </span>
                                  <span className={`text-xs px-2 py-1 rounded ${statusColors[status]}`}>
                                    {statusLabels[status]}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {new Date(transaction.purchaseDate).toLocaleDateString()}
                                  {transaction.providerPaidDate && ` • Paid: ${new Date(transaction.providerPaidDate).toLocaleDateString()}`}
                                </p>
                                <div className="mt-1 text-sm">
                                  <span className="text-gray-600">Student paid: </span>
                                  <span className="font-semibold">${Number(transaction.amount).toFixed(2)}</span>
                                  {transaction.appStoreCutAmount > 0 && (
                                    <>
                                      <span className="text-gray-600 ml-3">App store cut: </span>
                                      <span className="font-semibold text-red-600">-${Number(transaction.appStoreCutAmount).toFixed(2)}</span>
                                    </>
                                  )}
                                  <span className="text-gray-600 ml-3">Platform fee: </span>
                                  <span className="font-semibold text-red-600">-${Number(transaction.platformCommission).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <p className="text-xl font-bold text-green-600">+${Number(transaction.providerEarnings).toFixed(2)}</p>
                                <p className="text-xs text-gray-500 mt-1">Your earnings</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No transactions in {monthLabel}</p>
                      <p className="text-sm mt-2">Use the arrows above to navigate to other months</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* PAYOUT HISTORY TAB */}
          {providerTab === 'payouts' && (() => {
            const now = new Date();
            const currentYear = now.getFullYear();
            const ytdTxns = (allTransactions || []).filter(t =>
              t.providerId === currentUser.uid && !t.refunded &&
              new Date(t.purchaseDate).getFullYear() === currentYear
            );
            const ytdEarned      = ytdTxns.reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
            const ytdPaid        = ytdTxns.filter(t => t.providerPaid).reduce((s, t) => s + (Number(t.providerEarnings) || 0), 0);
            const ytdOutstanding = ytdEarned - ytdPaid;

            const paidTxns = (allTransactions || []).filter(t =>
              t.providerId === currentUser.uid && t.providerPaid && !t.refunded
            );
            const batches = {};
            paidTxns.forEach(t => {
              const batchId = t.paymentBatchId || 'legacy';
              if (!batches[batchId]) {
                batches[batchId] = { date: t.providerPaidDate, total: 0, count: 0, transactions: [], method: t.paymentMethod || '—' };
              }
              batches[batchId].total += Number(t.providerEarnings) || 0;
              batches[batchId].count += 1;
              batches[batchId].transactions.push(t);
              if (t.providerPaidDate && (!batches[batchId].date || t.providerPaidDate > batches[batchId].date)) {
                batches[batchId].date = t.providerPaidDate;
              }
            });
            const sortedBatches = Object.entries(batches).sort((a, b) =>
              new Date(b[1].date || 0) - new Date(a[1].date || 0)
            );
            return (
              <div className="max-w-6xl mx-auto px-4 py-6">
                {/* YTD Banner */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 grid grid-cols-3 gap-4">
                  <div className="text-center flex flex-col items-center">
                    <div className="text-xs text-gray-500 mb-1 min-h-[2rem] flex items-end justify-center text-center leading-tight">{currentYear} Total Earned</div>
                    <p className="text-lg font-bold text-purple-700">${ytdEarned.toFixed(2)}</p>
                  </div>
                  <div className="text-center border-x border-purple-200 flex flex-col items-center">
                    <div className="text-xs text-gray-500 mb-1 min-h-[2rem] flex items-end justify-center text-center leading-tight">{currentYear} Total Paid</div>
                    <p className="text-lg font-bold text-green-600">${ytdPaid.toFixed(2)}</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div className="text-xs text-gray-500 mb-1 min-h-[2rem] flex items-end justify-center text-center leading-tight">Outstanding Balance</div>
                    <p className="text-lg font-bold text-yellow-600">${ytdOutstanding.toFixed(2)}</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold mb-6">Payout History</h2>
                  {sortedBatches.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No payouts yet</p>
                      <p className="text-sm mt-2">Completed payouts will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sortedBatches.map(([batchId, batch]) => {
                        const isExpanded = expandedPayoutBatch === batchId;
                        return (
                          <div key={batchId} className="border rounded-lg overflow-hidden">
                            <button
                              onClick={() => setExpandedPayoutBatch(isExpanded ? null : batchId)}
                              className="w-full p-4 flex items-center justify-between bg-green-50 hover:bg-green-100 transition text-left"
                            >
                              <div>
                                <p className="font-semibold text-green-800">
                                  ${batch.total.toFixed(2)} — {batch.count} transaction{batch.count !== 1 ? 's' : ''}
                                </p>
                                <p className="text-sm text-gray-600 mt-0.5">
                                  {batch.date ? new Date(batch.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}
                                  {batch.method !== '—' && ` • ${batch.method}`}
                                </p>
                              </div>
                              <ChevronRight size={20} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </button>
                            {isExpanded && (
                              <div className="divide-y">
                                {batch.transactions.map((t, i) => (
                                  <div key={i} className="p-4 flex items-start justify-between gap-4 bg-white">
                                    <div className="flex-1">
                                      <p className="font-medium">{t.courseTitle}</p>
                                      <p className="text-sm text-gray-600">Student: {t.studentName || '—'}</p>
                                      <p className="text-sm text-gray-500">{new Date(t.purchaseDate).toLocaleDateString()}</p>
                                    </div>
                                    <p className="font-bold text-green-600 flex-shrink-0">+${Number(t.providerEarnings).toFixed(2)}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* PROFILE TAB */}
          {providerTab === 'profile' && (
            <div className="max-w-md mx-auto px-4 py-6">
              <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <User size={32} className="text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{currentUser?.fullName}</h2>
                    <p className="text-sm text-gray-600">{currentUser?.email}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Profession</span>
                    <span className="font-semibold">
                      {professions.find(p => p.id === currentUser?.profession)?.name || 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Total Courses</span>
                    <span className="font-semibold">{providerCourses.length}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Total Students</span>
                    <span className="font-semibold">
                      {providerCourses.reduce((sum, course) => sum + (course.students || 0), 0)}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  // Populate fields from currentUser when opening
                  setProfession(currentUser?.profession || 'massage-therapy');
                  setGoverningBody(currentUser?.governingBody || '');
                  setProviderNumber(currentUser?.providerNumber || '');
                  setProviderExpiration(currentUser?.providerExpiration || '');
                  setCredentialNotes(currentUser?.credentialNotes || '');
                  setLicenseNumber(currentUser?.licenseNumber || '');
                  setShowProviderSettings(true);
                }}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 flex items-center justify-center mb-3"
              >
                <Settings size={20} className="mr-2" />
                Provider Settings
              </button>

              <button
                onClick={() => setShowDeleteAccountModal(true)}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 flex items-center justify-center mb-3"
              >
                Delete Account
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 flex items-center justify-center"
              >
                <LogOut size={20} className="mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>

        {/* BOTTOM TAB BAR - NATIVE STYLE */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom z-50">
          <div className="flex items-center justify-around h-16">
            <button
              onClick={() => setProviderTab('dashboard')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                providerTab === 'dashboard' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <Home size={24} />
              <span className="text-xs mt-1">Dashboard</span>
            </button>
            
            <button
              onClick={() => setProviderTab('courses')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                providerTab === 'courses' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <Book size={24} />
              <span className="text-xs mt-1">Courses</span>
            </button>
            
            <button
              onClick={() => setProviderTab('earnings')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                providerTab === 'earnings' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <DollarSign size={24} />
              <span className="text-xs mt-1">Earnings</span>
            </button>

            <button
              onClick={() => setProviderTab('payouts')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                providerTab === 'payouts' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <CreditCard size={24} />
              <span className="text-xs mt-1">Payouts</span>
            </button>

            <button
              onClick={() => setProviderTab('profile')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                providerTab === 'profile' ? 'text-purple-600' : 'text-gray-500'
              }`}
            >
              <User size={24} />
              <span className="text-xs mt-1">Profile</span>
            </button>
          </div>
        </div>

        {/* ALL MODALS */}
        {/* Provider Settings Modal */}
        {showProviderSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Provider Settings</h3>
                <button onClick={() => setShowProviderSettings(false)} className="touch-target">
  <X size={24} />
</button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Profession</label>
                  <select
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {professions.filter(p => p.active).map(prof => (
                      <option key={prof.id} value={prof.id}>
                        {prof.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Governing/Accrediting Body <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={governingBody}
                    onChange={(e) => setGoverningBody(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., NCBTMB, ABMP, State Board, NCCAOM"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the organization that issued your provider credentials
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Provider/Approval Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={providerNumber}
                    onChange={(e) => setProviderNumber(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Your provider or approval number"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Credential Expiration <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={providerExpiration}
                    onChange={(e) => setProviderExpiration(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">License Number (Optional)</label>
                  <input
                    type="text"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Additional Credentials/Notes (Optional)</label>
                  <textarea
                    value={credentialNotes}
                    onChange={(e) => setCredentialNotes(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    rows="3"
                    placeholder="e.g., Also ABMP certified, State-specific approvals, etc."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Any additional certifications or notes about your credentials
                  </p>
                </div>
                
                <div className="border-t pt-4 mt-6">
                  <h4 className="font-semibold mb-3">Payment Information</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Banking details are collected securely through our payment provider when your first payout is ready.
                  </p>
                </div>
                
                <button
                  onClick={updateProviderCredentials}
                  disabled={updatingCredentials}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {updatingCredentials ? 'Updating...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Approved Course Warning Modal */}
        {/* DELETE ACCOUNT MODAL */}
        {showDeleteAccountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              {currentUser?.userType === 'provider' ? (
                <>
                  <div className="flex items-center mb-4">
                    <AlertCircle size={28} className="text-yellow-500 mr-3 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">Close Your Account</h3>
                  </div>
                  <p className="text-sm text-gray-700 mb-6">
                    To close your provider account, email us at{' '}
                    <span className="font-medium text-purple-600">renewceuapp@gmail.com</span>{' '}
                    and we'll take care of everything, including your active courses.
                  </p>
                  <button
                    onClick={() => setShowDeleteAccountModal(false)}
                    className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 font-semibold"
                  >
                    Got it
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center mb-4">
                    <AlertCircle size={28} className="text-red-500 mr-3 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">Delete Account?</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    This will permanently delete your account and all associated data. This cannot be undone.
                  </p>
                  {currentUser?.authProvider !== 'google' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Enter your password to confirm</label>
                      <div className="flex items-center border rounded-lg px-3 py-2">
                        <Lock size={18} className="text-gray-400 mr-2" />
                        <input
                          type="password"
                          value={deleteAccountPassword}
                          onChange={(e) => setDeleteAccountPassword(e.target.value)}
                          className="flex-1 outline-none text-sm"
                          placeholder="Your password"
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowDeleteAccountModal(false); setDeleteAccountPassword(''); }}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deletingAccount}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 font-semibold disabled:bg-gray-400"
                    >
                      {deletingAccount ? 'Deleting...' : 'Delete Account'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showEditWarning && courseToEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-md my-8">
              <div className="flex items-center mb-4">
                <AlertCircle size={32} className="text-orange-500 mr-3" />
                <h3 className="text-xl font-bold">Edit Approved Course?</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  <strong>{courseToEdit.title}</strong> is currently live and visible to students.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 font-semibold mb-2">⚠️ Warning:</p>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Course will be removed from student browse</li>
                    <li>Status will change to "Draft"</li>
                    <li>You must re-submit for admin approval</li>
                    <li>Changes won't go live until approved</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowEditWarning(false);
                    setCourseToEdit(null);
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Change course status to draft
                      await window.firebaseDB.updateDoc(`courses/${courseToEdit.id}`, {
                        status: 'draft',
                        resubmittedAt: new Date().toISOString()
                      });
                      
                      // Close warning modal
                      setShowEditWarning(false);
                      setCourseToEdit(null);
                      
                      // Reload courses to update status badge
                      await loadProviderCourses(currentUser.uid);
                      await loadAllCourses();
                      
                      // Enter inline editing mode (accordion behavior)
                      setExpandedCourseId(courseToEdit.id);
                      setEditingCourseId(courseToEdit.id);
                      
                      showToast('Course moved to draft. Make your changes and submit for review when ready.', 'success');
                    } catch (error) {
                      showToast('Error updating course: ' + error.message, 'error');
                    }
                  }}
                  className="flex-1 bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700"
                >
                  Yes, Edit Course
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Create/Edit Course Modal */}
        {showCreateCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 overflow-x-hidden">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">
                  {editingCourse ? 'Edit Course' : 'Create New Course'}
                </h3>
                <button onClick={() => {
                  setShowCreateCourse(false);
                  setEditingCourse(null);
                  setSelectedFeaturedImage(null);
                }} className="touch-target">
  <X size={24} />
</button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Course Title</label>
                  <input
                    type="text"
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., Advanced Sports Massage Techniques"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Course Type</label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={newCourse.isCEU}
                        onChange={() => setNewCourse({...newCourse, isCEU: true})}
                        className="mr-2"
                      />
                      <span className="text-sm">CEU Course (Awards continuing education credits)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={!newCourse.isCEU}
                        onChange={() => setNewCourse({...newCourse, isCEU: false})}
                        className="mr-2"
                      />
                      <span className="text-sm">Educational Course (No CEU credits)</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Profession</label>
                  <select
                    value={newCourse.profession}
                    onChange={(e) => setNewCourse({...newCourse, profession: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {professions.filter(p => p.active).map(prof => (
                      <option key={prof.id} value={prof.id}>
                        {prof.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {newCourse.isCEU ? 'CEU Hours' : 'Course Hours'}
                  </label>
                  <input
                    type="number"
                    value={newCourse.hours}
                    onChange={(e) => setNewCourse({...newCourse, hours: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., 6"
                    step="0.5"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Approval Entries</label>
                  <div className="space-y-2">
                    {(newCourse.approvalEntries || [{ governingBody: '', approvalNumber: '', expirationDate: '' }]).map((entry, idx) => (
                      <div key={idx} className="flex gap-2 items-start">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={entry.governingBody}
                            onChange={(e) => {
                              const updated = newCourse.approvalEntries.map((en, i) => i === idx ? { ...en, governingBody: e.target.value } : en);
                              setNewCourse({ ...newCourse, approvalEntries: updated });
                            }}
                            className="w-full border rounded-lg px-2 py-2 text-sm min-w-0"
                            placeholder="e.g. NCBTMB, Florida Board, ABMP"
                          />
                          <input
                            type="text"
                            value={entry.approvalNumber}
                            onChange={(e) => {
                              const updated = newCourse.approvalEntries.map((en, i) => i === idx ? { ...en, approvalNumber: e.target.value } : en);
                              setNewCourse({ ...newCourse, approvalEntries: updated });
                            }}
                            className="w-full border rounded-lg px-2 py-2 text-sm min-w-0"
                            placeholder="Provider/approval number"
                          />
                          <input
                            type="date"
                            value={entry.expirationDate}
                            onChange={(e) => {
                              const updated = newCourse.approvalEntries.map((en, i) => i === idx ? { ...en, expirationDate: e.target.value } : en);
                              setNewCourse({ ...newCourse, approvalEntries: updated });
                            }}
                            className="w-full border rounded-lg px-2 py-2 text-sm min-w-0"
                          />
                        </div>
                        {newCourse.approvalEntries.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setNewCourse({ ...newCourse, approvalEntries: newCourse.approvalEntries.filter((_, i) => i !== idx) })}
                            className="flex-shrink-0 text-gray-400 hover:text-red-500 text-lg leading-none pt-2"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewCourse({ ...newCourse, approvalEntries: [...newCourse.approvalEntries, { governingBody: '', approvalNumber: '', expirationDate: '' }] })}
                    className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    + Add Another Approval
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Approved States — uncheck states where this course is not valid for CEU credit.
                  </label>
                  <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                      {ALL_US_STATES.map(state => (
                        <label key={state.code} className="flex items-center gap-2 py-0.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(newCourse.approvedStates || ALL_STATE_CODES).includes(state.code)}
                            onChange={(e) => {
                              const current = newCourse.approvedStates || ALL_STATE_CODES;
                              const next = e.target.checked
                                ? [...current, state.code]
                                : current.filter(c => c !== state.code);
                              setNewCourse({ ...newCourse, approvedStates: next });
                            }}
                            className="h-4 w-4 flex-shrink-0"
                          />
                          <span className="text-sm truncate">{state.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Price (USD)</label>
                  <input
                    type="number"
                    value={newCourse.price}
                    onChange={(e) => setNewCourse({...newCourse, price: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., 49.99"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={newCourse.description}
                    onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    rows="4"
                    placeholder="Describe what students will learn..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Featured Image (16:9 ratio recommended)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Recommended: 1280x720, 1920x1080, or any 16:9 image • Max 5MB
                  </p>
                  
                  {newCourse.featuredImage && (
                    <img
                      src={newCourse.featuredImage}
                      alt="Featured"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  )}
                </div>
                
                {newCourse.isCEU && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> CEU courses require admin approval before going live.
                    </p>
                  </div>
                )}
                
                <button
                  onClick={handleCreateCourse}
                  disabled={loading}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : (editingCourse ? 'Update Course' : 'Create Course')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Builder Modal */}
        {showQuizBuilder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Quiz Builder</h3>
                <button onClick={closeQuizBuilder} className="touch-target" >
                  <X size={24} />
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-3">Quiz Settings</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Passing Score (%)</label>
                    <input
                      type="number"
                      value={currentQuiz.passingScore}
                      onChange={(e) => setCurrentQuiz({...currentQuiz, passingScore: parseInt(e.target.value)})}
                      className="w-full border rounded-lg px-3 py-2"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Quiz Format</label>
                    <select
                      value={currentQuiz.quizFormat}
                      onChange={(e) => setCurrentQuiz({...currentQuiz, quizFormat: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="all-on-page">All questions on one page</option>
                      <option value="one-per-screen">One question per screen</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold mb-3">
                  {editingQuestionIndex !== null ? 'Edit Question' : 'Add New Question'}
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Question</label>
                    <textarea
                      value={questionForm.question}
                      onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      rows="2"
                      placeholder="Enter your question..."
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={questionForm.hasImage}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            removeQuestionImage();
                          }
                          setQuestionForm({...questionForm, hasImage: e.target.checked});
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">Add image to question</span>
                    </label>
                  </div>
                  
                  {questionForm.hasImage && (
                    <div>
                      {questionForm.imageURL ? (
                        <div className="relative">
                          <img
                            src={questionForm.imageURL}
                            alt="Question"
                            className="w-full max-w-md rounded-lg"
                          />
                          <button
                            onClick={removeQuestionImage}
                            className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleQuizImageSelect}
                            className="text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">Max 5MB - JPG, PNG, GIF</p>
                        </div>
                      )}
                      
                      {uploadingQuizImage && (
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full transition-all"
                              style={{ width: `${quizImageUploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 text-center">
                            Uploading... {quizImageUploadProgress.toFixed(0)}%
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Answer Options (2-6)</label>
                    <div className="space-y-2">
                      {questionForm.options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={questionForm.correctAnswer === index}
                            onChange={() => setQuestionForm({...questionForm, correctAnswer: index})}
                            className="mt-1"
                          />
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(index, e.target.value)}
                            className="flex-1 border rounded-lg px-3 py-2"
                            placeholder={`Option ${index + 1}`}
                          />
                          {questionForm.options.length > 2 && (
                            <button
                              onClick={() => removeOption(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X size={20} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {questionForm.options.length < 6 && (
                      <button
                        onClick={addOption}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Option
                      </button>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Select the radio button next to the correct answer
                    </p>
                  </div>
                  
                  <button
                    onClick={addOrUpdateQuestion}
                    disabled={uploadingQuizImage}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {editingQuestionIndex !== null ? 'Update Question' : 'Add Question'}
                  </button>
                  
                  {editingQuestionIndex !== null && (
                    <button
                      onClick={() => {
                        setEditingQuestionIndex(null);
                        resetQuestionForm();
                      }}
                      className="w-full bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold mb-3">
                  Questions ({currentQuiz.questions.length})
                </h4>
                
                {currentQuiz.questions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No questions added yet</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {currentQuiz.questions.map((question, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-medium flex-1">
                            {index + 1}. {question.question}
                          </p>
                          <div className="flex gap-2">
                            <button
  onClick={() => editQuestion(index)}
  className="text-blue-600 hover:text-blue-700 touch-target"
>
  <Edit size={18} />
</button>
                            <button
  onClick={() => deleteQuestion(index)}
  className="text-red-600 hover:text-red-700 touch-target"
>
  <Trash2 size={18} />
</button>
                          </div>
                        </div>
                        
                        {question.hasImage && (
                          <p className="text-xs text-gray-500 mb-2">📷 Has image</p>
                        )}
                        
                        <div className="text-sm space-y-1">
                          {question.options.map((option, optIndex) => (
                            <p key={optIndex} className={optIndex === question.correctAnswer ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                              {optIndex === question.correctAnswer ? '✓' : '○'} {option}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={closeQuizBuilder}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={saveQuiz}
                  disabled={loading || currentQuiz.questions.length === 0}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : 'Save Quiz'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Settings Modal */}
        {showCertificateSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 overflow-x-hidden">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Certificate Settings</h3>
                <button onClick={() => {
                  setShowCertificateSettings(null);
                  setSignatureFile(null);
                }} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Provider Credentials (shown on certificate)</label>
                  <input
                    type="text"
                    value={courseCredentials[showCertificateSettings]?.credentials || ''}
                    onChange={(e) => setCourseCredentials({
                      ...courseCredentials,
                      [showCertificateSettings]: {
                        ...courseCredentials[showCertificateSettings],
                        credentials: e.target.value
                      }
                    })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., LMT, NCBTMB Approved Provider #506789"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This will appear on the certificate under your name
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Certificate Expiration Type</label>
                  <select
                    value={courseCredentials[showCertificateSettings]?.expirationType || 'type2'}
                    onChange={(e) => setCourseCredentials({
                      ...courseCredentials,
                      [showCertificateSettings]: {
                        ...courseCredentials[showCertificateSettings],
                        expirationType: e.target.value,
                        // Clear conditional fields when type changes
                        expirationFixedDate: e.target.value === 'type1' ? courseCredentials[showCertificateSettings]?.expirationFixedDate : null,
                        expirationYears: e.target.value === 'type4' ? courseCredentials[showCertificateSettings]?.expirationYears : null
                      }
                    })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="type2">None - No expiration</option>
                    <option value="type1">Fixed Date - Everyone expires on same date</option>
                    <option value="type4">Years After Completion - Expires X years after completion</option>
                    <option value="type3">Tied to Provider Credential - Expires when provider credential expires</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose how certificates for this course should expire
                  </p>
                </div>
                
                {/* Type 1: Fixed Date - Show date picker */}
                {courseCredentials[showCertificateSettings]?.expirationType === 'type1' && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <label className="block text-sm font-medium mb-2">Fixed Expiration Date</label>
                    <input
                      type="date"
                      value={courseCredentials[showCertificateSettings]?.expirationFixedDate || ''}
                      onChange={(e) => setCourseCredentials({
                        ...courseCredentials,
                        [showCertificateSettings]: {
                          ...courseCredentials[showCertificateSettings],
                          expirationFixedDate: e.target.value
                        }
                      })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Example: "2024 OSHA Regulations" expires Dec 31, 2024
                    </p>
                  </div>
                )}
                
                {/* Type 4: Years After Completion - Show number input */}
                {courseCredentials[showCertificateSettings]?.expirationType === 'type4' && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <label className="block text-sm font-medium mb-2">Years Until Expiration</label>
                    <select
                      value={courseCredentials[showCertificateSettings]?.expirationYears || '2'}
                      onChange={(e) => setCourseCredentials({
                        ...courseCredentials,
                        [showCertificateSettings]: {
                          ...courseCredentials[showCertificateSettings],
                          expirationYears: e.target.value
                        }
                      })}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="1">1 year</option>
                      <option value="2">2 years</option>
                      <option value="3">3 years</option>
                      <option value="4">4 years</option>
                      <option value="5">5 years</option>
                    </select>
                    <p className="text-xs text-gray-600 mt-1">
                      Example: CPR expires 2 years after student completes the course
                    </p>
                  </div>
                )}
                
                {/* Type 3: Tied to Provider Credential - Show info */}
                {courseCredentials[showCertificateSettings]?.expirationType === 'type3' && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800 font-medium">
                      📋 Certificate expires when your provider credential expires
                    </p>
                    <p className="text-xs text-yellow-700 mt-2">
                      Your current credential expiration: {currentUser?.providerExpiration ? new Date(currentUser.providerExpiration).toLocaleDateString() : 'Not set'}
                    </p>
                    {!currentUser?.providerExpiration && (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ Please set your provider expiration in Provider Settings first
                      </p>
                    )}
                  </div>
                )}
                
                {/* Type 2: None - Show info */}
                {courseCredentials[showCertificateSettings]?.expirationType === 'type2' && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700">
                      Certificates will not have an expiration date. Course may become outdated over time but certificates remain valid.
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">Signature Image</label>
                  
                  {courseSignatures[showCertificateSettings] && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-green-600 mb-2">✓ Signature uploaded</p>
                      <img
                        src={courseSignatures[showCertificateSettings]}
                        alt="Signature"
                        className="max-w-xs border rounded"
                      />
                    </div>
                  )}
                  
                  <input
                    ref={signatureFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleSignatureSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => signatureFileInputRef.current.click()}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 bg-white hover:bg-gray-50 w-full text-left"
                  >
                    {signatureFile ? signatureFile.name : 'Choose File'}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload your signature (PNG with transparent background recommended, max 2MB)
                  </p>
                  
                  {uploadingSignature && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${signatureUploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 text-center">
                        Uploading... {signatureUploadProgress.toFixed(0)}%
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> These settings will be applied to all certificates generated for this course.
                  </p>
                </div>
                
                <button
                  onClick={() => saveCertificateSettings(
                    showCertificateSettings,
                    courseCredentials[showCertificateSettings] || {}
                  )}
                  disabled={uploadingSignature}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {uploadingSignature ? 'Uploading...' : 'Save Certificate Settings'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* W-9 Form Modal */}
        {showW9Form && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold">W-9 Tax Information</h3>
                  <button onClick={() => setShowW9Form(false)} className="touch-target">
                    <X size={24} />
                  </button>
                </div>
                
                <p className="text-gray-700 text-base leading-relaxed">
                  To receive payments, you'll receive a secure invite from our payment provider. Complete your banking details and W-9 through that link — nothing sensitive is stored in this app.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (currentScreen === 'admin-dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <ToastContainer />
        {/* Simple Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 shadow-lg safe-top">
          <div className="px-4 py-3 flex items-center justify-center">
            <h1 className="text-xl font-bold text-white">
  {adminTab === 'dashboard' ? 'Dashboard' : 
   adminTab === 'review' ? 'Review' : 
   adminTab === 'finance' ? 'Finance' : 
   adminTab === 'students' ? 'Students' :
   adminTab === 'analytics' ? 'Analytics' :
   'Profile'}
</h1>
          </div>
        </div>

        {/* Main Content - Flex Grow */}
        <div className="flex-1 overflow-y-auto pb-20">
          
          {/* DASHBOARD TAB */}
          {adminTab === 'dashboard' && (
            <div className="max-w-7xl mx-auto px-4 py-6">
              {adminDataLoading ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading admin data...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Pending Course Reviews - TOP PRIORITY */}
                  {pendingCourses.length > 0 && (
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <AlertCircle className="text-orange-500 mr-3" size={32} />
                          <div>
                            <p className="font-bold text-lg">Pending Course Approvals</p>
                            <p className="text-sm text-gray-600">{pendingCourses.length} course{pendingCourses.length !== 1 ? 's' : ''} awaiting review</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setAdminTab('review')}
                          className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 font-semibold"
                        >
                          Review Now →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Activity Log */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold">Recent Activity</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowAddNoteModal(true)}
                          className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700"
                        >
                          + Add Note
                        </button>
                        <button
                          onClick={() => loadActivityLog()}
                          className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-300"
                        >
                          Refresh
                        </button>
                        <button
                          onClick={() => setShowActivityLog(true)}
                          className="text-purple-600 hover:text-purple-700 text-sm font-semibold"
                        >
                          View All
                        </button>
                      </div>
                    </div>
                    
                    {(() => {
                      const filteredLogs = activityLog.filter(() => currentUser.userType === 'admin');

                      return filteredLogs.length === 0 ? (
                        <p className="text-gray-500 text-sm">No recent activity</p>
                      ) : (
                        <div className="space-y-3">
                          {filteredLogs.slice(0, 5).map((log, index) => {
                            const isGeneralNote = log.category === 'general';
                            const isSupport = log.category === 'support' && log.action === 'Help request submitted';
                            const isResolved = log.resolved === true;
                            const isExpanded = expandedLogId === (log.id || index);

                            return (
                              <div
                                key={log.id || index}
                                className={`rounded-lg p-3 ${
                                  isSupport
                                    ? isResolved
                                      ? 'border border-gray-300 bg-gray-50'
                                      : 'border-2 border-orange-400 bg-orange-50'
                                    : isGeneralNote
                                      ? 'border-2 border-blue-400 bg-blue-50'
                                      : 'border-b pb-3'
                                }`}
                              >
                                <div
                                  className={isSupport ? 'cursor-pointer' : ''}
                                  onClick={() => isSupport && setExpandedLogId(isExpanded ? null : (log.id || index))}
                                >
                                  <p className={`font-semibold ${isSupport ? (isResolved ? 'text-gray-500' : 'text-orange-800') : isGeneralNote ? 'text-base text-blue-800' : 'text-sm'}`}>
                                    {isSupport ? (isResolved ? '✅ ' : '🆘 ') : isGeneralNote ? '📝 ' : ''}{log.action}
                                    {isSupport && <span className="text-xs font-normal ml-2 text-gray-500">{isExpanded ? '▲ collapse' : '▼ expand'}</span>}
                                  </p>
                                  <p className={`text-gray-600 text-sm mt-1 ${isResolved ? 'line-through' : ''}`}>
                                    {log.details}
                                  </p>
                                </div>
                                {isSupport && isExpanded && (
                                  <div className="mt-2 pt-2 border-t border-orange-200">
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{log.fullMessage || log.details}</p>
                                    {!isResolved && log.action === 'Help request submitted' && (
                                      <button
                                        disabled={resolvingLogId === log.id}
                                        onClick={async () => {
                                          setResolvingLogId(log.id);
                                          try {
                                            await window.firebaseFunctions.call('resolveHelpRequest', {
                                              activityLogId: log.id,
                                              helpRequestId: log.helpRequestId || null,
                                              resolvedBy: currentUser.fullName,
                                              resolvedById: currentUser.uid
                                            });
                                            const resolvedAt = new Date().toISOString();
                                            setActivityLog(prev => prev.map(l => l.id === log.id ? { ...l, resolved: true, resolvedBy: currentUser.fullName, resolvedAt } : l));
                                            setExpandedLogId(null);
                                          } catch (err) {
                                            console.error('Error resolving help request:', err);
                                            showToast('Failed to resolve help request', 'error');
                                          }
                                          setResolvingLogId(null);
                                        }}
                                        className="mt-2 bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                                      >
                                        {resolvingLogId === log.id ? 'Resolving...' : 'Mark Resolved'}
                                      </button>
                                    )}
                                    {isResolved && <p className="text-xs text-gray-500 mt-1">Resolved by {log.resolvedBy} · {log.resolvedAt ? new Date(log.resolvedAt).toLocaleString() : ''}</p>}
                                  </div>
                                )}
                                {log.notes && (
                                  <p className="text-sm text-blue-600 mt-1 italic">Note: {log.notes}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                  by {log.adminName} • {new Date(log.timestamp).toLocaleString()}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Export Transactions - Owner & Financial Only */}
                  {(currentUser.userType === 'admin') && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-xl font-bold mb-4">Export Reports</h3>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <label className="block text-sm font-semibold mb-1">Provider</label>
                          <select value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
                            <option value="all">All Providers</option>
                            {allProviders.map(p => (
                              <option key={p.id} value={p.id}>{p.fullName}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-1">Course</label>
                          <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
                            <option value="all">All Courses</option>
                            {allCourses.map(c => (
                              <option key={c.id} value={c.id}>{c.title}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-1">Status</label>
                          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
                            <option value="all">All Status</option>
                            <option value="paid">Paid</option>
                            <option value="pending">Pending</option>
                            <option value="refunded">Refunded</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-1">Payment Source</label>
                          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
                            <option value="all">All Sources</option>
                            <option value="apple">Apple</option>
                            <option value="google">Google</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-1">From Date</label>
                          <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-1">To Date</label>
                          <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                        </div>
                      </div>
                      
                      <button
                        onClick={exportTransactionsCSV}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <Download size={20} />
                        Export Transactions CSV
                      </button>
                    </div>
                  )}

                  {/* Stats Cards - MOVED TO BOTTOM */}
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Total Students</h3>
                      <User className="text-blue-600" size={24} />
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{allStudents.length}</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Total Providers</h3>
                      <Upload className="text-purple-600" size={24} />
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{allProviders.length}</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-600 font-semibold">Total Courses</h3>
                      <Book className="text-green-600" size={24} />
                    </div>
                    <p className="text-3xl font-bold text-gray-800">
                      {courses.filter(c => c.status === 'approved').length}
                    </p>
                  </div>
                  
                  {(currentUser.userType === 'admin') && (
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-gray-600 font-semibold">Revenue</h3>
                        <DollarSign className="text-yellow-600" size={24} />
                      </div>
                      {(() => {
                        const transactions = (allTransactions || []).filter(t => !t.refunded);
                        const gross = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
                        const platformCommission = transactions.reduce((sum, t) => sum + (Number(t.platformCommission) || 0), 0);
                        
                        return (
                          <div className="space-y-1">
                            <p className="text-2xl font-bold text-gray-800">${gross.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">Commission: ${platformCommission.toFixed(2)}</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* REVIEW TAB */}
          {adminTab === 'review' && (
            <div className="max-w-7xl mx-auto px-4 py-6">
              {/* Internal Tabs */}
              <div className="flex gap-3 mb-6">
                <button
                  onClick={() => setAdminReviewTab('courses')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition ${
                    adminReviewTab === 'courses'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border'
                  }`}
                >
                  Courses
                </button>
                <button
                  onClick={() => setAdminReviewTab('providers')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition ${
                    adminReviewTab === 'providers'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border'
                  }`}
                >
                  Providers
                </button>
              </div>

              {/* COURSES REVIEW */}
              {adminReviewTab === 'courses' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">All Courses</h2>
                  
                  <div className="mb-6 space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-1 flex items-center border rounded-lg px-3 py-2">
                        <Search size={20} className="text-gray-400 mr-2" />
                        <input
                          type="text"
                          value={adminCourseSearch}
                          onChange={(e) => setAdminCourseSearch(e.target.value)}
                          placeholder="Search courses..."
                          className="flex-1 outline-none"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <select
                        value={adminCourseProfessionFilter}
                        onChange={(e) => setAdminCourseProfessionFilter(e.target.value)}
                        className="border rounded-lg px-3 py-2"
                      >
                        <option value="all">All Professions</option>
                        {professions.filter(p => p.active).map(prof => (
                          <option key={prof.id} value={prof.id}>{prof.name}</option>
                        ))}
                      </select>
                      
                      <select
                        value={adminCourseStatusFilter}
                        onChange={(e) => setAdminCourseStatusFilter(e.target.value)}
                        className="border rounded-lg px-3 py-2"
                      >
                        <option value="all">All Statuses</option>
                        <option value="draft">Draft</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      
                      <select
                        value={adminCourseSort}
                        onChange={(e) => setAdminCourseSort(e.target.value)}
                        className="border rounded-lg px-3 py-2"
                      >
                        <option value="recent">Most Recent</option>
                        <option value="oldest">Oldest First</option>
                        <option value="title">Title A-Z</option>
                        <option value="price-high">Price High-Low</option>
                        <option value="price-low">Price Low-High</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {courses
                      .filter(course => {
                        const matchesSearch = 
                          adminCourseSearch === '' ||
                          course.title.toLowerCase().includes(adminCourseSearch.toLowerCase()) ||
                          course.provider.toLowerCase().includes(adminCourseSearch.toLowerCase());
                        
                        const matchesProfession = 
                          adminCourseProfessionFilter === 'all' || 
                          course.profession === adminCourseProfessionFilter ||
                          (() => {
                            const prof = professions.find(p => p.id === adminCourseProfessionFilter);
                            if (!prof) return false;
                            if (prof.name === course.profession) return true;
                            const profWords = prof.name.toLowerCase().split(/\s+/).filter(w => w.length > 3);
                            const courseWords = course.profession.toLowerCase().split(/\s+/).filter(w => w.length > 3);
                            return profWords.some(pw => courseWords.some(cw => pw.substring(0, 6) === cw.substring(0, 6))) && profWords.length > 0;
                          })();
                        
                        const matchesStatus = 
                          adminCourseStatusFilter === 'all' || 
                          course.status === adminCourseStatusFilter;
                        
                        return matchesSearch && matchesProfession && matchesStatus;
                      })
                      .sort((a, b) => {
                        const statusOrder = { pending: 0, draft: 1, rejected: 2, approved: 3 };
                        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
                        if (statusDiff !== 0) return statusDiff;
                        if (adminCourseSort === 'recent') return new Date(b.createdAt) - new Date(a.createdAt);
                        if (adminCourseSort === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
                        if (adminCourseSort === 'title') return a.title.localeCompare(b.title);
                        if (adminCourseSort === 'price-high') return b.price - a.price;
                        if (adminCourseSort === 'price-low') return a.price - b.price;
                        return 0;
                      })
                      .map(course => (
                        <div key={course.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <h3 className="font-bold text-lg">{course.title}</h3>
                                {course.status === 'draft' && (
                                  <span className="text-xs bg-gray-200 text-gray-800 font-medium px-2 py-1 rounded">
                                    Draft
                                  </span>
                                )}
                                {course.status === 'pending' && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                    Pending
                                  </span>
                                )}
                                {course.status === 'approved' && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                    Approved
                                  </span>
                                )}
                                {course.status === 'rejected' && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                    Rejected
                                  </span>
                                )}
                                {course.isCEU ? (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    CEU
                                  </span>
                                ) : (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    Educational
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{course.description}</p>
                              <div className="flex flex-wrap gap-2 text-sm">
                                <span className="text-gray-600">
                                  Provider: <strong>{course.provider}</strong>
                                </span>
                                <span className="text-gray-600">•</span>
                                <span className="text-gray-600">
                                  {professions.find(p => p.id === course.profession)?.name || course.profession}
                                </span>
                                <span className="text-gray-600">•</span>
                                <span className="text-gray-600">{course.hours} Hours</span>
                                <span className="text-gray-600">•</span>
                                <span className="text-gray-600">${course.price}</span>
                              </div>
                            </div>
                            
                            <div className="text-right ml-4">
                              <p className="text-xl font-bold text-purple-600">{course.students || 0}</p>
                              <p className="text-xs text-gray-600">Students</p>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-3 flex-wrap">
                            <button
                              onClick={async () => {
                                const safeCourse = {
                                  ...course,
                                  videos: course.videos || [],
                                  materials: course.materials || [],
                                  quiz: course.quiz || { questions: [] },
                                  learningObjectives: course.learningObjectives || []
                                };
                                setInspectingCourse(safeCourse);
                                await loadCourseFiles(course.id);
                                await loadCourseVideos(course.id);
                                await loadCourseQuiz(course.id);
                              }}
                              className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm"
                            >
                              Inspect
                            </button>
                            
                            {course.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    setApprovingCourse(course);
                                    setApprovalCommission(20);
                                    setShowApprovalModal(true);
                                  }}
                                  className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => setRejectingCourse(course)}
                                  className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 text-sm"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* PROVIDERS REVIEW */}
              {adminReviewTab === 'providers' && (
                <div>
                  {selectedProvider ? (
                    // Provider Detail View
                    (() => {
                      const providerTxns = allTransactions.filter(t => t.providerId === selectedProvider.id && !t.refunded);
                      const totalEarned = providerTxns.reduce((sum, t) => sum + (t.providerEarnings || 0), 0);
                      const totalPaid = providerTxns.filter(t => t.providerPaid === true).reduce((sum, t) => sum + (t.providerEarnings || 0), 0);
                      const outstanding = totalEarned - totalPaid;
                      const providerCourses = allCourses.filter(c => c.providerId === selectedProvider.id);

                      return (
                        <div>
                          <button
                            onClick={() => setSelectedProvider(null)}
                            className="mb-4 text-purple-600 hover:text-purple-700 flex items-center font-medium"
                          >
                            ← Back to Providers
                          </button>

                          <div className="flex items-center gap-3 mb-6 flex-wrap">
                            <h2 className="text-2xl font-bold text-gray-800 truncate">{selectedProvider.fullName}</h2>
                            {selectedProvider.status === 'pending' && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Pending</span>
                            )}
                            {selectedProvider.status === 'active' && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active</span>
                            )}
                            {selectedProvider.status === 'suspended' && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Suspended</span>
                            )}
                          </div>

                          {/* Profile */}
                          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                            <h3 className="text-lg font-bold mb-4">Profile</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-xs text-gray-500 mb-1">Email</p>
                                <p className="font-medium text-sm break-all">{selectedProvider.email}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-xs text-gray-500 mb-1">Profession</p>
                                <p className="font-medium text-sm">{professions.find(p => p.id === selectedProvider.profession)?.name || selectedProvider.profession || '—'}</p>
                              </div>
                              {selectedProvider.providerNumber && (
                                <div className="bg-gray-50 p-3 rounded">
                                  <p className="text-xs text-gray-500 mb-1">{professions.find(p => p.id === selectedProvider.profession)?.providerNumberLabel || 'Provider Number'}</p>
                                  <p className="font-medium text-sm">{selectedProvider.providerNumber}</p>
                                </div>
                              )}
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-xs text-gray-500 mb-1">Joined</p>
                                <p className="font-medium text-sm">{selectedProvider.createdAt ? new Date(selectedProvider.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-xs text-gray-500 mb-1">Last Active</p>
                                <p className="font-medium text-sm">{selectedProvider.lastActiveAt ? new Date(selectedProvider.lastActiveAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-xs text-gray-500 mb-1">W-9 Status</p>
                                <p className={`font-medium text-sm ${selectedProvider.w9Submitted ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {selectedProvider.w9Submitted ? 'Submitted' : 'Not submitted'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Earnings */}
                          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                            <h3 className="text-lg font-bold mb-4">Earnings</h3>
                            <div className="grid grid-cols-3 gap-4">
                              <div className="bg-gray-50 p-4 rounded text-center">
                                <p className="text-xs text-gray-500 mb-1">Total Earned</p>
                                <p className="text-xl font-bold text-purple-700">${totalEarned.toFixed(2)}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded text-center">
                                <p className="text-xs text-gray-500 mb-1">Total Paid Out</p>
                                <p className="text-xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded text-center">
                                <p className="text-xs text-gray-500 mb-1">Outstanding</p>
                                <p className="text-xl font-bold text-yellow-600">${outstanding.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>

                          {/* Courses */}
                          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                            <h3 className="text-lg font-bold mb-4">Courses ({providerCourses.length})</h3>
                            {providerCourses.length === 0 ? (
                              <p className="text-gray-500 text-center py-6">No courses yet</p>
                            ) : (
                              <div className="space-y-3">
                                {providerCourses.map(course => {
                                  const enrollmentCount = allEnrollments.filter(e => e.courseId === course.id).length;
                                  return (
                                    <div key={course.id} className="border rounded-lg p-4">
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <p className="font-semibold truncate">{course.title}</p>
                                          <p className="text-sm text-gray-500 mt-1">{enrollmentCount} enrollment{enrollmentCount !== 1 ? 's' : ''}</p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <span className="font-semibold">${Number(course.price || 0).toFixed(2)}</span>
                                          {course.status === 'approved' && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Approved</span>}
                                          {course.status === 'pending' && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Pending</span>}
                                          {course.status === 'draft' && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Draft</span>}
                                          {course.status === 'rejected' && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Rejected</span>}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold mb-4">Actions</h3>
                            {selectedProvider.status !== 'suspended' ? (
                              <button
                                onClick={() => { setSuspendProviderReason(''); setShowSuspendProviderModal(true); }}
                                className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition"
                              >
                                Suspend Provider
                              </button>
                            ) : (
                              <button
                                onClick={() => setShowReinstateProviderModal(true)}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition"
                              >
                                Reinstate Provider
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    // Provider List
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h2 className="text-2xl font-bold text-gray-800 mb-6">All Providers</h2>

                      <div className="mb-6 space-y-4">
                        <div className="flex gap-3">
                          <div className="flex-1 flex items-center border rounded-lg px-3 py-2">
                            <Search size={20} className="text-gray-400 mr-2" />
                            <input
                              type="text"
                              value={adminProviderSearch}
                              onChange={(e) => setAdminProviderSearch(e.target.value)}
                              placeholder="Search providers..."
                              className="flex-1 outline-none"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <select
                            value={adminProviderProfessionFilter}
                            onChange={(e) => setAdminProviderProfessionFilter(e.target.value)}
                            className="border rounded-lg px-3 py-2"
                          >
                            <option value="all">All Professions</option>
                            {professions.filter(p => p.active).map(prof => (
                              <option key={prof.id} value={prof.id}>{prof.name}</option>
                            ))}
                          </select>

                          <select
                            value={adminProviderStatusFilter}
                            onChange={(e) => setAdminProviderStatusFilter(e.target.value)}
                            className="border rounded-lg px-3 py-2"
                          >
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="suspended">Suspended</option>
                          </select>

                          <select
                            value={adminProviderSort}
                            onChange={(e) => setAdminProviderSort(e.target.value)}
                            className="border rounded-lg px-3 py-2"
                          >
                            <option value="recent">Most Recent</option>
                            <option value="oldest">Oldest First</option>
                            <option value="name">Name A-Z</option>
                            <option value="courses">Most Courses</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {allProviders
                          .filter(provider => {
                            const matchesSearch =
                              adminProviderSearch === '' ||
                              (provider.fullName && provider.fullName.toLowerCase().includes(adminProviderSearch.toLowerCase())) ||
                              (provider.email && provider.email.toLowerCase().includes(adminProviderSearch.toLowerCase()));

                            const matchesProfession =
                              adminProviderProfessionFilter === 'all' ||
                              provider.profession === adminProviderProfessionFilter;

                            const matchesStatus =
                              adminProviderStatusFilter === 'all' ||
                              provider.status === adminProviderStatusFilter;

                            return matchesSearch && matchesProfession && matchesStatus;
                          })
                          .sort((a, b) => {
                            if (adminProviderSort === 'recent') return new Date(b.createdAt) - new Date(a.createdAt);
                            if (adminProviderSort === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
                            if (adminProviderSort === 'name') return (a.fullName || '').localeCompare(b.fullName || '');
                            if (adminProviderSort === 'courses') {
                              const aCount = allCourses.filter(c => c.providerId === a.id).length;
                              const bCount = allCourses.filter(c => c.providerId === b.id).length;
                              return bCount - aCount;
                            }
                            return 0;
                          })
                          .map(provider => {
                            const providerCourses = allCourses.filter(c => c.providerId === provider.id);
                            const providerEarnings = allTransactions
                              .filter(t => t.providerId === provider.id && !t.refunded)
                              .reduce((sum, t) => sum + (t.providerEarnings || 0), 0);

                            return (
                              <div
                                key={provider.id}
                                className="border rounded-lg p-4 cursor-pointer hover:border-purple-400 hover:shadow-sm transition"
                                onClick={() => setSelectedProvider(provider)}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <h3 className="font-bold text-lg">{provider.fullName}</h3>
                                      {provider.status === 'pending' && (
                                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                          Pending
                                        </span>
                                      )}
                                      {provider.status === 'active' && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                          Active
                                        </span>
                                      )}
                                      {provider.status === 'suspended' && (
                                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                          Suspended
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600">{provider.email}</p>
                                    <p className="text-sm text-gray-600">
                                      {professions.find(p => p.id === provider.profession)?.name || provider.profession}
                                    </p>
                                    {provider.providerNumber && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {professions.find(p => p.id === provider.profession)?.providerNumberLabel || 'Provider Number'}: {provider.providerNumber}
                                      </p>
                                    )}
                                    <div className="flex gap-4 mt-2 text-sm">
                                      <span className="text-gray-600">
                                        <strong>{providerCourses.length}</strong> courses
                                      </span>
                                      <span className="text-gray-600">
                                        Earnings: <strong>${providerEarnings.toFixed(2)}</strong>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* FINANCE TAB */}
          {adminTab === 'finance' && (currentUser.userType === 'admin') && (
            <div className="max-w-7xl mx-auto px-4 py-6">
              {/* Finance Sub-tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto">
                <button
                  onClick={() => setAdminFinanceTab('payouts')}
                  className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition ${
                    adminFinanceTab === 'payouts'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border'
                  }`}
                >
                  Payouts
                </button>
                <button
                  onClick={() => setAdminFinanceTab('refunds')}
                  className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition ${
                    adminFinanceTab === 'refunds'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border'
                  }`}
                >
                  Refunds
                </button>
                <button
                  onClick={() => setAdminFinanceTab('1099')}
                  className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition ${
                    adminFinanceTab === '1099'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border'
                  }`}
                >
                  1099
                </button>
                <button
                  onClick={() => setAdminFinanceTab('reconciliation')}
                  className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition ${
                    adminFinanceTab === 'reconciliation'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border'
                  }`}
                >
                  Reconciliation
                </button>
              </div>

              {/* PAYOUTS CONTENT */}
              {adminFinanceTab === 'payouts' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold mb-4">Pending Payouts</h3>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3">
                    <span className="text-blue-600 text-lg">📅</span>
                    <p className="text-sm text-blue-800">
                      <strong>Scheduled payout day: 15th of each month.</strong> Process all cleared transactions by the 15th.
                    </p>
                  </div>

                  {/* Filter & Export */}
                  <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                      <label className="font-semibold">Filter:</label>
                      <button
                        onClick={() => setFilterSource('all')}
                        className={`px-4 py-2 rounded ${filterSource === 'all' ? 'bg-purple-600 text-white' : 'bg-white border'}`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setFilterSource('apple')}
                        className={`px-4 py-2 rounded ${filterSource === 'apple' ? 'bg-purple-600 text-white' : 'bg-white border'}`}
                      >
                        Apple
                      </button>
                      <button
                        onClick={() => setFilterSource('google')}
                        className={`px-4 py-2 rounded ${filterSource === 'google' ? 'bg-purple-600 text-white' : 'bg-white border'}`}
                      >
                        Google
                      </button>
                    </div>
                    
                    <button
                      onClick={exportPayoutCSV}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download size={20} />
                      Export Pending Payouts CSV
                    </button>
                  </div>
                  
                  {/* Filters */}
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Search</label>
                      <input
                        type="text"
                        placeholder="Course or provider name..."
                        className="w-full border rounded-lg px-3 py-2"
                        onChange={(e) => setPayoutSearch(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Payment Source</label>
                      <select className="w-full border rounded-lg px-3 py-2" onChange={(e) => setPayoutSourceFilter(e.target.value)}>
                        <option value="all">All Sources</option>
                        <option value="apple">Apple Only</option>
                        <option value="google">Google Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Month</label>
                      <select value={payoutDateFilter} className="w-full border rounded-lg px-3 py-2" onChange={(e) => setPayoutDateFilter(e.target.value)}>
                        <option value="all">All Time</option>
                        <option value="current">Current Month</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Calculate totals */}
                  {(() => {
                    const currentMonth = new Date().getMonth();
                    const currentYear = new Date().getFullYear();
                    
                    let filteredTransactions = (allTransactions || []).filter(t => {
                      if (t.processorCleared !== true) return false;
                      if (t.providerPaid !== false) return false;
                      if (t.refunded !== false) return false;

                      if (payoutDateFilter === 'current') {
                        const tDate = new Date(t.purchaseDate);
                        if (tDate.getMonth() !== currentMonth || tDate.getFullYear() !== currentYear) return false;
                      }

                      if (payoutSourceFilter === 'apple' || payoutSourceFilter === 'google') {
                        if (t.paymentSource !== payoutSourceFilter) return false;
                      }

                      if (payoutSearch) {
                        const searchLower = payoutSearch.toLowerCase();
                        const matchesCourse = (t.courseTitle || '').toLowerCase().includes(searchLower);
                        const matchesProvider = (t.providerName || '').toLowerCase().includes(searchLower);
                        if (!matchesCourse && !matchesProvider) return false;
                      }

                      return true;
                    });
                    
                    filteredTransactions = filteredTransactions.sort((a, b) => {
                      return new Date(a.purchaseDate) - new Date(b.purchaseDate);
                    });
                    
                    const totalOwed = filteredTransactions.reduce((sum, t) => 
                      sum + (Number(t.providerEarnings) || 0), 0
                    );
                    
                    const providerSummary = {};
                    filteredTransactions.forEach(t => {
                      if (!providerSummary[t.providerId]) {
                        providerSummary[t.providerId] = {
                          name: t.providerName,
                          total: 0,
                          count: 0,
                          transactions: []
                        };
                      }
                      providerSummary[t.providerId].total += Number(t.providerEarnings) || 0;
                      providerSummary[t.providerId].count += 1;
                      providerSummary[t.providerId].transactions.push(t);
                    });
                    
                    return (
                      <>
                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Total Pending</p>
                            <p className="text-2xl font-bold text-purple-600">${totalOwed.toFixed(2)}</p>
                          </div>
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Providers</p>
                            <p className="text-2xl font-bold text-blue-600">{Object.keys(providerSummary).length}</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Transactions</p>
                            <p className="text-2xl font-bold text-green-600">{filteredTransactions.length}</p>
                          </div>
                        </div>
                        
                        {/* Transaction List */}
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {filteredTransactions.map((transaction, index) => {
                            const daysPending = Math.floor((new Date() - new Date(transaction.purchaseDate)) / (1000 * 60 * 60 * 24));
                            const isSelected = selectedTransactions.includes(transaction.id);
                            
                            return (
                              <div key={index} className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedTransactions([...selectedTransactions, transaction.id]);
                                      } else {
                                        setSelectedTransactions(selectedTransactions.filter(id => id !== transaction.id));
                                      }
                                    }}
                                    className="mt-1 w-5 h-5"
                                  />
                                  
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <p className="font-semibold">{transaction.courseTitle}</p>
                                      <span className={`text-xs px-2 py-1 rounded ${transaction.paymentSource === 'apple' ? 'bg-gray-800 text-white' : 'bg-blue-600 text-white'}`}>
                                        {transaction.paymentSource === 'apple' ? 'Apple' : transaction.paymentSource === 'google' ? 'Google' : 'Unknown'}
                                      </span>
                                      <span className="text-xs px-2 py-1 rounded bg-yellow-500 text-white">
                                        {daysPending} days pending
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                      Provider: {transaction.providerName} • 
                                      Purchase: {new Date(transaction.purchaseDate).toLocaleDateString()}
                                    </p>
                                    <div className="mt-2 text-sm">
                                      <span className="text-gray-600">Gross: </span>
                                      <span className="font-semibold">${Number(transaction.amount).toFixed(2)}</span>
                                      {transaction.appStoreCutAmount > 0 && (
                                        <>
                                          <span className="text-gray-600 ml-2">• App Store: </span>
                                          <span className="text-red-600">-${Number(transaction.appStoreCutAmount).toFixed(2)}</span>
                                        </>
                                      )}
                                      <span className="text-gray-600 ml-2">• Platform: </span>
                                      <span className="text-red-600">-${Number(transaction.platformCommission).toFixed(2)}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="text-right">
                                    <p className="text-xl font-bold text-green-600">${Number(transaction.providerEarnings).toFixed(2)}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          
                          {filteredTransactions.length === 0 && (
                            <p className="text-gray-500 text-center py-8">No pending transactions match your filters</p>
                          )}
                        </div>
                        
                        {/* Mark Selected Button */}
                        {filteredTransactions.length > 0 && (
                          <div className="mt-6 pt-6 border-t">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                              <div>
                                <p className="text-sm text-gray-600">
                                  {selectedTransactions.length} transaction{selectedTransactions.length !== 1 ? 's' : ''} selected
                                </p>
                                {selectedTransactions.length > 0 && (
                                  <p className="text-lg font-bold text-green-600">
                                    Total: ${filteredTransactions
                                      .filter(t => selectedTransactions.includes(t.id))
                                      .reduce((sum, t) => sum + (Number(t.providerEarnings) || 0), 0)
                                      .toFixed(2)}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex gap-3">
                                <button
                                  onClick={() => setSelectedTransactions(filteredTransactions.map(t => t.id))}
                                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                  Select All
                                </button>
                                <button
                                  onClick={() => setSelectedTransactions([])}
                                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                  disabled={selectedTransactions.length === 0}
                                >
                                  Clear
                                </button>
                                <button
                                  onClick={() => {
                                    if (selectedTransactions.length === 0) {
                                      showToast('Please select at least one transaction', 'error');
                                      return;
                                    }
                                    setPayoutMethod('ACH');
                                    setPayoutReference('');
                                    setPayoutNotes('');
                                    setShowPayoutModal(true);
                                  }}
                                  disabled={selectedTransactions.length === 0}
                                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Mark Selected as Paid
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* PAYOUT CONFIRMATION MODAL */}
              {showPayoutModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
                  <div className="bg-white rounded-lg p-6 w-full max-w-md">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold">Confirm Payout</h3>
                      <button onClick={() => setShowPayoutModal(false)} className="touch-target">
                        <X size={24} />
                      </button>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                      {selectedTransactions.length} transaction{selectedTransactions.length !== 1 ? 's' : ''} selected — total:{' '}
                      <strong className="text-green-600">
                        ${(allTransactions || [])
                          .filter(t => selectedTransactions.includes(t.id))
                          .reduce((sum, t) => sum + (Number(t.providerEarnings) || 0), 0)
                          .toFixed(2)}
                      </strong>
                    </p>

                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium mb-1">Payment Method</label>
                        <select
                          value={payoutMethod}
                          onChange={e => setPayoutMethod(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="ACH">ACH</option>
                          <option value="Zelle">Zelle</option>
                          <option value="Check">Check</option>
                          <option value="Wire">Wire</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Reference / Confirmation Number</label>
                        <input
                          type="text"
                          value={payoutReference}
                          onChange={e => setPayoutReference(e.target.value)}
                          placeholder="e.g. ACH batch ID, check number..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                        <input
                          type="text"
                          value={payoutNotes}
                          onChange={e => setPayoutNotes(e.target.value)}
                          placeholder="Optional notes..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowPayoutModal(false)}
                        className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={!payoutReference.trim()}
                        onClick={async () => {
                          try {
                            const paymentBatchId = `payout_${new Date().toISOString().replace(/[:.]/g, '-')}`;
                            const now = new Date().toISOString();
                            for (const transactionId of selectedTransactions) {
                              await window.firebaseDB.updateDoc(`transactions/${transactionId}`, {
                                providerPaid: true,
                                providerPaidDate: now,
                                paymentBatchId: paymentBatchId,
                                paymentMethod: payoutMethod,
                                paymentReference: payoutReference.trim(),
                                paymentNotes: payoutNotes.trim(),
                                status: 'paid'
                              });
                            }
                            const selectedAmount = (allTransactions || [])
                              .filter(t => selectedTransactions.includes(t.id))
                              .reduce((sum, t) => sum + (Number(t.providerEarnings) || 0), 0);
                            await logActivity(
                              'Batch Payment Processed',
                              `Marked ${selectedTransactions.length} transactions as paid ($${selectedAmount.toFixed(2)} total) via ${payoutMethod} — ref: ${payoutReference.trim()}`,
                              'financial'
                            );
                            setShowPayoutModal(false);
                            setSelectedTransactions([]);
                            await loadAllTransactions();
                            showToast(`${selectedTransactions.length} transaction(s) marked as paid!`, 'success');
                          } catch (error) {
                            showToast('Error: ' + error.message, 'error');
                          }
                        }}
                        className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-300 disabled:text-gray-500"
                      >
                        Confirm Payout
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* REFUNDS CONTENT */}
              {adminFinanceTab === 'refunds' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Refunds Policy</h2>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                    <div className="flex items-start">
                      <AlertCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" size={24} />
                      <div>
                        <h3 className="font-bold text-blue-900 mb-2">App Store Refund Policy</h3>
                        <p className="text-blue-800 mb-3">
                          All course purchases made through the mobile app are processed through Apple App Store or Google Play Store. 
                          Refunds for these purchases must be requested directly through the respective app store.
                        </p>
                        <div className="space-y-2 text-sm text-blue-700">
                          <p><strong>Apple App Store:</strong> Students can request refunds at reportaproblem.apple.com</p>
                          <p><strong>Google Play Store:</strong> Students can request refunds in the Play Store app under Order History</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-800 mb-3">Historical Refunds</h3>
                    <p className="text-gray-600 mb-4">
                      This section previously managed in-app refund requests. All new refunds are now handled by Apple/Google.
                    </p>
                    
                    {allEnrollments.filter(e => e.refunded).length > 0 ? (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-gray-700">Past Refunded Enrollments:</p>
                        {allEnrollments
                          .filter(e => e.refunded)
                          .slice(0, 10)
                          .map(enrollment => {
                            const course = courses.find(c => c.id === enrollment.courseId);
                            const student = allStudents.find(s => s.id === enrollment.userId);
                            return (
                              <div key={enrollment.id} className="bg-white border rounded-lg p-3 text-sm">
                                <p className="font-semibold">{course?.title}</p>
                                <p className="text-gray-600">Student: {student?.fullName}</p>
                                <p className="text-gray-600">Amount: ${enrollment.amount}</p>
                                <p className="text-gray-600">
                                  Refunded: {new Date(enrollment.refundDate).toLocaleDateString()}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No historical refunds found.</p>
                    )}
                  </div>
                </div>
              )}

              {/* 1099 CONTENT */}
              {adminFinanceTab === '1099' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                    <h3 className="text-xl font-bold">1099-NEC Management - {selected1099Year}</h3>
                    <select
                      value={selected1099Year}
                      onChange={e => setSelected1099Year(Number(e.target.value))}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      {Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => new Date().getFullYear() - i).map(yr => (
                        <option key={yr} value={yr}>{yr}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-gray-600 mb-6">Providers who received $600 or more in payouts in {selected1099Year}</p>

                  <div className="mb-6">
                    <button
                      onClick={() => export1099CSV(selected1099Year)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download size={20} />
                      Export 1099 Data CSV ({selected1099Year})
                    </button>
                  </div>
                  
                  {/* Calculate year totals */}
                  {(() => {
                    const currentYear = selected1099Year;
                    
                    const yearTransactions = (allTransactions || []).filter(t => {
                      if (!t.providerPaid || t.refunded) return false;
                      if (!t.providerPaidDate) return false;
                      const paidDate = new Date(t.providerPaidDate);
                      return paidDate.getFullYear() === currentYear;
                    });
                    
                    const providerYearTotals = {};
                    yearTransactions.forEach(t => {
                      if (!providerYearTotals[t.providerId]) {
                        providerYearTotals[t.providerId] = {
                          name: t.providerName,
                          total: 0,
                          count: 0
                        };
                      }
                      providerYearTotals[t.providerId].total += Number(t.providerEarnings) || 0;
                      providerYearTotals[t.providerId].count += 1;
                    });
                    
                    const providers1099 = Object.entries(providerYearTotals)
                      .filter(([_, data]) => data.total >= 600)
                      .sort((a, b) => b[1].total - a[1].total);
                    
                    const totalToReport = providers1099.reduce((sum, [_, data]) => sum + data.total, 0);
                    
                    return (
                      <>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                          <p className="text-sm text-blue-800">
                            <strong>IRS Requirement:</strong> 1099s track payments MADE, not earnings ACCUMULATED. 
                            This report shows providers who received $600+ in actual payouts during {currentYear}.
                          </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                          <div className="bg-yellow-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Providers Requiring 1099</p>
                            <p className="text-2xl font-bold text-yellow-600">{providers1099.length}</p>
                          </div>
                          <div className="bg-red-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Total to Report</p>
                            <p className="text-2xl font-bold text-red-600">${totalToReport.toFixed(2)}</p>
                          </div>
                        </div>
                        
                        {providers1099.length > 0 ? (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {providers1099.map(([providerId, data]) => {
                              const provider = allProviders.find(p => p.id === providerId);
                              
                              const w9On = provider?.w9Submitted === true;
                              return (
                                <div key={providerId} className="border rounded-lg p-4">
                                  <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                      <p className="font-semibold">{data.name}</p>
                                      <p className="text-sm text-gray-600">
                                        {data.count} payment{data.count !== 1 ? 's' : ''} received in {currentYear} •
                                        ${data.total.toFixed(2)} total paid
                                      </p>
                                      {w9On ? (
                                        <p className="text-xs text-green-600 mt-1">✓ W-9 on file</p>
                                      ) : (
                                        <p className="text-xs text-red-600 mt-1">⚠ W-9 missing — required for 1099</p>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                      <p className="text-xl font-bold">${data.total.toFixed(2)}</p>
                                      <button
                                        onClick={async () => {
                                          const next = !w9On;
                                          try {
                                            await window.firebaseDB.updateDoc(`users/${providerId}`, {
                                              w9Submitted: next,
                                              w9SubmittedAt: next ? new Date().toISOString() : null
                                            });
                                            setAllProviders(prev => prev.map(p =>
                                              p.id === providerId ? { ...p, w9Submitted: next } : p
                                            ));
                                            showToast(next ? 'W-9 marked as received.' : 'W-9 mark removed.', 'success');
                                          } catch (err) {
                                            showToast('Error updating W-9 status.', 'error');
                                          }
                                        }}
                                        className={`text-xs px-3 py-1 rounded border font-medium transition ${w9On ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-400 text-green-700 hover:bg-green-50'}`}
                                      >
                                        {w9On ? 'Unmark W-9' : 'Mark W-9 Received'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-8">No providers received $600+ in payouts yet this year</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* RECONCILIATION CONTENT */}
              {adminFinanceTab === 'reconciliation' && (() => {
                const appleUncleared = (allTransactions || []).filter(t =>
                  t.processorCleared === false && !t.providerPaid && !t.refunded && t.paymentSource === 'apple'
                );
                const googleUncleared = (allTransactions || []).filter(t =>
                  t.processorCleared === false && !t.providerPaid && !t.refunded && t.paymentSource === 'google'
                );

                let filtered = (allTransactions || []).filter(t =>
                  t.processorCleared === false && !t.providerPaid && !t.refunded
                );
                if (reconciliationSourceFilter !== 'all') {
                  filtered = filtered.filter(t => t.paymentSource === reconciliationSourceFilter);
                }
                if (reconciliationDateFrom) {
                  filtered = filtered.filter(t => new Date(t.purchaseDate) >= new Date(reconciliationDateFrom));
                }
                if (reconciliationDateTo) {
                  const to = new Date(reconciliationDateTo);
                  to.setHours(23, 59, 59);
                  filtered = filtered.filter(t => new Date(t.purchaseDate) <= to);
                }
                filtered = filtered.sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));

                const markCleared = async (transactionId, paymentSource) => {
                  const processor = paymentSource === 'apple' ? 'Apple' : 'Google';
                  if (!window.confirm(`Confirm ${processor} has deposited this transaction to your Mercury account?`)) return;
                  try {
                    const now = new Date().toISOString();
                    await window.firebaseDB.updateDoc(`transactions/${transactionId}`, {
                      processorCleared: true,
                      processorClearedAt: now
                    });
                    setAllTransactions(prev => prev.map(t =>
                      t.id === transactionId ? { ...t, processorCleared: true, processorClearedAt: now } : t
                    ));
                  } catch (err) {
                    showToast('Error updating transaction: ' + err.message, 'error');
                  }
                };

                const markAllCleared = async (source) => {
                  const sourceName = source === 'apple' ? 'Apple' : 'Google';
                  const targets = (allTransactions || []).filter(t =>
                    t.processorCleared === false && !t.providerPaid && !t.refunded && t.paymentSource === source
                  );
                  if (targets.length === 0) {
                    showToast(`No uncleared ${sourceName} transactions.`, 'info');
                    return;
                  }
                  if (!window.confirm(`Mark all ${targets.length} ${sourceName} transactions as cleared?`)) return;
                  try {
                    const now = new Date().toISOString();
                    for (const t of targets) {
                      await window.firebaseDB.updateDoc(`transactions/${t.id}`, {
                        processorCleared: true,
                        processorClearedAt: now
                      });
                    }
                    const targetIds = new Set(targets.map(t => t.id));
                    setAllTransactions(prev => prev.map(t =>
                      targetIds.has(t.id) ? { ...t, processorCleared: true, processorClearedAt: now } : t
                    ));
                    showToast(`Marked ${targets.length} ${sourceName} transactions as cleared.`, 'success');
                  } catch (err) {
                    showToast('Error updating transactions: ' + err.message, 'error');
                  }
                };

                return (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-bold mb-1">Processor Reconciliation</h3>
                    <p className="text-sm text-gray-600 mb-6">Transactions awaiting confirmation that Apple or Google has deposited funds to your Mercury account.</p>

                    {/* Bulk clear buttons */}
                    <div className="flex gap-3 mb-6 flex-wrap">
                      <button
                        onClick={() => markAllCleared('apple')}
                        className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm font-medium"
                      >
                        Mark All Apple as Cleared ({appleUncleared.length})
                      </button>
                      <button
                        onClick={() => markAllCleared('google')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                      >
                        Mark All Google as Cleared ({googleUncleared.length})
                      </button>
                    </div>

                    {/* Filters */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-6 flex gap-4 flex-wrap items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">Payment Source</label>
                        <select
                          value={reconciliationSourceFilter}
                          onChange={e => setReconciliationSourceFilter(e.target.value)}
                          className="border rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="all">All Sources</option>
                          <option value="apple">Apple</option>
                          <option value="google">Google</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">From</label>
                        <input
                          type="date"
                          value={reconciliationDateFrom}
                          onChange={e => setReconciliationDateFrom(e.target.value)}
                          className="border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">To</label>
                        <input
                          type="date"
                          value={reconciliationDateTo}
                          onChange={e => setReconciliationDateTo(e.target.value)}
                          className="border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <button
                        onClick={() => { setReconciliationSourceFilter('all'); setReconciliationDateFrom(''); setReconciliationDateTo(''); }}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg bg-white"
                      >
                        Clear Filters
                      </button>
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Uncleared (filtered)</p>
                        <p className="text-2xl font-bold text-yellow-600">{filtered.length}</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-300 mb-1">Apple Total</p>
                        <p className="text-2xl font-bold text-white">{appleUncleared.length}</p>
                      </div>
                      <div className="bg-blue-600 p-4 rounded-lg">
                        <p className="text-sm text-blue-100 mb-1">Google Total</p>
                        <p className="text-2xl font-bold text-white">{googleUncleared.length}</p>
                      </div>
                    </div>

                    {/* Transaction rows */}
                    {filtered.length === 0 ? (
                      <p className="text-gray-500 text-center py-12">No uncleared transactions match your filters</p>
                    ) : (
                      <div className="space-y-3">
                        {filtered.map((t, index) => {
                          const daysSince = Math.floor((new Date() - new Date(t.purchaseDate)) / (1000 * 60 * 60 * 24));
                          const processor = t.paymentSource === 'apple' ? 'Apple' : 'Google';
                          return (
                            <div key={t.id || index} className="border rounded-lg p-4 flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <p className="font-semibold">{t.courseTitle}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded text-white flex-shrink-0 ${t.paymentSource === 'apple' ? 'bg-gray-800' : 'bg-blue-600'}`}>
                                    {processor}
                                  </span>
                                  {daysSince > 30 ? (
                                    <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 flex-shrink-0">{daysSince}d — overdue</span>
                                  ) : (
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 flex-shrink-0">{daysSince}d</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">Provider: {t.providerName}</p>
                                <p className="text-sm text-gray-600">Student: {t.studentName || '—'}</p>
                                <p className="text-sm text-gray-500">{new Date(t.purchaseDate).toLocaleDateString()}</p>
                                <div className="mt-1 text-sm">
                                  <span className="text-gray-600">Gross: <span className="font-medium">${Number(t.amount).toFixed(2)}</span></span>
                                  <span className="ml-3 text-green-700">Provider earns: <span className="font-medium">${Number(t.providerEarnings).toFixed(2)}</span></span>
                                </div>
                              </div>
                              <button
                                onClick={() => markCleared(t.id, t.paymentSource)}
                                className="flex-shrink-0 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                              >
                                Mark Cleared
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* STUDENTS TAB */}
          {adminTab === 'students' && (
            <div className="max-w-7xl mx-auto px-4 py-6">
              {selectedStudent ? (
                // Student Detail View
                <div>
                  <button
                    onClick={() => setSelectedStudent(null)}
                    className="mb-4 text-red-600 hover:text-red-700 flex items-center"
                  >
                    ← Back to Students
                  </button>

                  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center mb-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <User size={32} className="text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">{selectedStudent.fullName}</h2>
                        <p className="text-sm text-gray-600">{selectedStudent.email}</p>
                        <p className="text-xs text-gray-500 mt-1">Joined: {selectedStudent.createdAt ? new Date(selectedStudent.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="text-sm text-gray-600">Profession</p>
                        <p className="font-semibold">{selectedStudent.profession || 'Not specified'}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="text-sm text-gray-600">Total Enrollments</p>
                        <p className="font-semibold">{allEnrollments.filter(e => e.userId === selectedStudent.id).length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-bold mb-4">Enrollments</h3>
                    {(() => {
                      const studentEnrollments = allEnrollments.filter(e => e.userId === selectedStudent.id);
                      
                      if (studentEnrollments.length === 0) {
                        return <p className="text-gray-500 text-center py-8">No enrollments yet</p>;
                      }

                      return (
                        <div className="space-y-3">
                          {studentEnrollments.map(enrollment => {
                            const course = courses.find(c => c.id === enrollment.courseId);
                            if (!course) return null;

                            return (
                              <div key={enrollment.id} className="border rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <h4 className="font-semibold">{course.title}</h4>
                                    <p className="text-sm text-gray-600">{course.provider}</p>
                                  </div>
                                  <div className="text-right">
                                    {enrollment.completed ? (
                                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                        Completed
                                      </span>
                                    ) : (
                                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        In Progress
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="text-sm text-gray-600 space-y-1">
                                  <p>Enrolled: {enrollment.enrolledAt ? new Date(enrollment.enrolledAt.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
                                  {enrollment.completed && (
                                    <p>Completed: {enrollment.completedAt ? new Date(enrollment.completedAt.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
                                  )}
                                  <p>Progress: {enrollment.videosWatched || 0} of {course.videos?.length || 0} videos</p>
                                  {enrollment.quizScore !== undefined && (
                                    <p>Quiz Score: {enrollment.quizScore}%</p>
                                  )}
                                </div>

                                {enrollment.completed && (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Re-issue certificate for this course?')) return;
                                      
                                      try {
                                        // Generate new certificate
                                        const pdf = new jsPDF();
                                        
                                        // Certificate border
                                        pdf.setLineWidth(2);
                                        pdf.setDrawColor(139, 0, 0);
                                        pdf.rect(10, 10, 190, 277);
                                        
                                        // Title
                                        pdf.setFontSize(32);
                                        pdf.setFont(undefined, 'bold');
                                        pdf.text('Certificate of Completion', 105, 50, { align: 'center' });
                                        
                                        // Student name
                                        pdf.setFontSize(24);
                                        pdf.text(selectedStudent.fullName, 105, 90, { align: 'center' });
                                        
                                        // Course title
                                        pdf.setFontSize(16);
                                        pdf.setFont(undefined, 'normal');
                                        pdf.text('has successfully completed', 105, 120, { align: 'center' });
                                        pdf.setFont(undefined, 'bold');
                                        
                                        const courseTitleLines = pdf.splitTextToSize(course.title, 160);
                                        let currentY = 140;
                                        courseTitleLines.forEach(line => {
                                          pdf.text(line, 105, currentY, { align: 'center' });
                                          currentY += 10;
                                        });
                                        
                                        // CEU credits
                                        pdf.setFont(undefined, 'normal');
                                        pdf.setFontSize(14);
                                        pdf.text(`${course.ceuCredits} CEU Credits`, 105, currentY + 20, { align: 'center' });
                                        
                                        // Date
                                        const completionDate = enrollment.completedAt 
                                          ? new Date(enrollment.completedAt.seconds * 1000).toLocaleDateString()
                                          : new Date().toLocaleDateString();
                                        pdf.text(`Completion Date: ${completionDate}`, 105, currentY + 40, { align: 'center' });
                                        
                                        // Certificate ID
                                        const certId = `CERT-${enrollment.id.slice(0, 8).toUpperCase()}`;
                                        pdf.setFontSize(10);
                                        pdf.text(`Certificate ID: ${certId}`, 105, 270, { align: 'center' });
                                        
                                        // Save
                                        pdf.save(`certificate-${course.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`);
                                        
                                        showToast('Certificate re-issued successfully!', 'success');
                                      } catch (error) {
                                        console.error('Error re-issuing certificate:', error);
                                        showToast('Failed to re-issue certificate', 'error');
                                      }
                                    }}
                                    className="mt-3 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 flex items-center justify-center"
                                  >
                                    <Download size={16} className="mr-2" />
                                    Re-issue Certificate
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Student Actions */}
                  <div className="bg-white rounded-lg shadow-md p-6 mt-4">
                    <h3 className="text-lg font-bold mb-4">Actions</h3>
                    {selectedStudent.status === 'banned' && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-red-700 mb-1">This student is banned</p>
                        {selectedStudent.bannedAt && (
                          <p className="text-xs text-gray-600">Banned on: {new Date(selectedStudent.bannedAt).toLocaleDateString()}</p>
                        )}
                        {selectedStudent.banReason && (
                          <p className="text-xs text-gray-600 mt-1">Reason: {selectedStudent.banReason}</p>
                        )}
                      </div>
                    )}
                    {selectedStudent.status !== 'banned' ? (
                      <button
                        onClick={() => { setBanStudentReason(''); setShowBanStudentModal(true); }}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition"
                      >
                        Ban Student
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowUnbanStudentModal(true)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition"
                      >
                        Unban Student
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                // Students List View
                <div>
                  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h3 className="text-lg font-bold mb-4">All Students ({allStudents.length})</h3>
                    
                    <div className="mb-4 space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          placeholder="Search by name or email..."
                          value={adminStudentSearch}
                          onChange={(e) => setAdminStudentSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        />
                      </div>
                      <select
                        value={adminStudentStatusFilter}
                        onChange={(e) => setAdminStudentStatusFilter(e.target.value)}
                        className="border rounded-lg px-3 py-2 w-full"
                      >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="banned">Banned</option>
                      </select>
                    </div>

                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {(() => {
                        const filtered = allStudents.filter(student => {
                          const searchLower = adminStudentSearch.toLowerCase();
                          const matchesSearch = (
                            student.fullName?.toLowerCase().includes(searchLower) ||
                            student.email?.toLowerCase().includes(searchLower)
                          );
                          const matchesStatus =
                            adminStudentStatusFilter === 'all' ||
                            (adminStudentStatusFilter === 'banned' ? student.status === 'banned' : student.status !== 'banned');
                          return matchesSearch && matchesStatus;
                        });

                        if (filtered.length === 0) {
                          return <p className="text-gray-500 text-center py-8">No students found</p>;
                        }

                        return filtered.map(student => {
                          const enrollmentCount = allEnrollments.filter(e => e.userId === student.id).length;
                          const completedCount = allEnrollments.filter(e => e.userId === student.id && e.completed).length;

                          return (
                            <div
                              key={student.id}
                              onClick={() => setSelectedStudent(student)}
                              className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center flex-1">
                                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <User size={20} className="text-blue-600" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-semibold">{student.fullName}</h4>
                                      {student.status === 'banned' && (
                                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Banned</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600">{student.email}</p>
                                    {student.profession && (
                                      <p className="text-xs text-gray-500 mt-1">{student.profession}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right text-sm">
                                  <p className="text-gray-600">{enrollmentCount} enrollments</p>
                                  <p className="text-green-600">{completedCount} completed</p>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* ANALYTICS TAB */}
          {adminTab === 'analytics' && (currentUser.userType === 'admin') && (
            <div className="max-w-7xl mx-auto px-4 py-6 overflow-x-hidden">
              <div className="space-y-6">
                
                {/* Header with Export Button */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Platform Metrics</h2>
                  <button
                    onClick={exportAnalyticsToCSV}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download size={20} />
                    Download Report
                  </button>
                </div>
                
                {/* Platform Metrics */}
                <div>
                  <h2 className="text-xl font-bold mb-4">Platform Metrics</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg shadow p-4">
                      <p className="text-sm text-gray-600 mb-1">Total Users</p>
                      <p className="text-2xl font-bold">{analyticsData.totalUsers}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                      <p className="text-sm text-gray-600 mb-1">MAU</p>
                      <p className="text-2xl font-bold">{analyticsData.mau}</p>
                      <p className="text-xs text-gray-500">Monthly Active</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                      <p className="text-sm text-gray-600 mb-1">DAU</p>
                      <p className="text-2xl font-bold">{analyticsData.dau}</p>
                      <p className="text-xs text-gray-500">Daily Active</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                      <p className="text-sm text-gray-600 mb-1">Revenue</p>
                      <p className="text-2xl font-bold">${Number(analyticsData.totalRevenue || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                      <p className="text-sm text-gray-600 mb-1">Courses</p>
                      <p className="text-2xl font-bold">{analyticsData.totalCourses}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                      <p className="text-sm text-gray-600 mb-1">Enrollments</p>
                      <p className="text-2xl font-bold">{analyticsData.totalEnrollments}</p>
                    </div>
                  </div>
                </div>

                {/* Top Performing Courses */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold mb-4">Top Performing Courses</h3>
                  {(analyticsData.topCourses || []).length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No course data yet</p>
                  ) : (
                    <div className="space-y-3">
                      {(analyticsData.topCourses || []).map((course, index) => (
                        <div key={course.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-gray-400">#{index + 1}</span>
                                <h4 className="font-semibold">{course.title}</h4>
                              </div>
                              <p className="text-sm text-gray-600">{course.provider}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-green-600">
                                ${Number((course.price || 0) * (course.enrollments || 0)).toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Views</p>
                              <p className="font-semibold">{course.views}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Enrollments</p>
                              <p className="font-semibold">{course.enrollments}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Conversion</p>
                              <p className="font-semibold">
                                {course.views > 0 ? ((course.enrollments / course.views) * 100).toFixed(1) : 0}%
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Conversion Metrics */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold mb-4">Platform Conversion</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Overall Conversion Rate</span>
                        <span className="font-semibold">{Number(analyticsData.conversionRate || 0).toFixed(2)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(analyticsData.conversionRate, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold mb-4">Recent Activity</h3>
                  {(analyticsData.recentActivity || []).length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No activity yet</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {(analyticsData.recentActivity || []).map((event, index) => {
                        const course = allCourses.find(c => c.id === event.courseId);
                        if (!course) return null;
                        
                        const timeAgo = () => {
                          const diff = Date.now() - new Date(event.timestamp).getTime();
                          const minutes = Math.floor(diff / 60000);
                          const hours = Math.floor(diff / 3600000);
                          const days = Math.floor(diff / 86400000);
                          
                          if (minutes < 60) return `${minutes}m ago`;
                          if (hours < 24) return `${hours}h ago`;
                          return `${days}d ago`;
                        };
                        
                        return (
                          <div key={index} className="flex items-center gap-3 text-sm py-2 border-b last:border-0">
                            <div className="flex-shrink-0">
                              {event.type === 'course_viewed' && <Eye size={16} className="text-blue-500" />}
                              {event.type === 'course_bookmarked' && <Bookmark size={16} className="text-yellow-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-700 truncate">
                                {event.type === 'course_viewed' && 'Viewed'}
                                {event.type === 'course_bookmarked' && 'Bookmarked'}
                                {' '}<span className="font-semibold">{course.title}</span>
                              </p>
                            </div>
                            <span className="text-gray-400 text-xs whitespace-nowrap">{timeAgo()}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}
          
          {/* PROFILE TAB */}
          {adminTab === 'profile' && (
            <div className="max-w-md mx-auto px-4 py-6">
              <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <User size={32} className="text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{currentUser?.fullName}</h2>
                    <p className="text-sm text-gray-600">{currentUser?.email}</p>
                    {currentUser?.isOwner ? (
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded mt-1 inline-block">
                        Owner
                      </span>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mt-1 inline-block">
                        Admin
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Total Students</span>
                    <span className="font-semibold">{allStudents.length}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Total Providers</span>
                    <span className="font-semibold">{allProviders.length}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-gray-600">Total Courses</span>
                    <span className="font-semibold">{courses.length}</span>
                  </div>
                </div>
              </div>
              
              {/* Notification Preferences */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-4">
                <h3 className="text-lg font-bold mb-4">Notification Settings</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">Push Notifications</p>
                      <p className="text-sm text-gray-600">
                        {notificationPermission === 'granted' ? 'Enabled' : 
                         notificationPermission === 'denied' ? 'Notifications are off — enable in device settings.' : 'Not enabled'}
                      </p>
                    </div>
                    {notificationPermission !== 'granted' && (
                      <button
                        onClick={() => {
                          if (notificationPermission === 'denied') {
                            Browser.open({ url: 'app-settings:' });
                          } else {
                            registerDeviceToken(currentUser.uid);
                          }
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm"
                      >
                        Enable
                      </button>
                    )}
                    {notificationPermission === 'granted' && (
                      <CheckCircle className="text-green-600" size={24} />
                    )}
                  </div>
                  
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-500">
                      Get notified when courses need review, refunds are requested, or important platform events occur
                    </p>
                  </div>
                </div>
              </div>
              
              {currentUser.isOwner && (
                <>
                  <button
                    onClick={() => setShowProfessionManager(true)}
                    className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 flex items-center justify-center mb-3"
                  >
                    <Settings size={20} className="mr-2" />
                    Manage Professions
                  </button>
                  
                  <button
                    onClick={() => setShowOrgManager(true)}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center mb-3"
                  >
                    <Settings size={20} className="mr-2" />
                    Manage Organizations
                  </button>
                  
                  <button
                    onClick={() => setShowAdminSettings(true)}
                    className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 flex items-center justify-center mb-3"
                  >
                    <Settings size={20} className="mr-2" />
                    Platform Settings
                  </button>
                </>
              )}
              
              <button
                onClick={handleLogout}
                className="w-full bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 flex items-center justify-center"
              >
                <LogOut size={20} className="mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>

        {/* BOTTOM TAB BAR - NATIVE STYLE */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom z-50">
          <div className="flex items-center justify-around h-16">
            <button
              onClick={() => setAdminTab('dashboard')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                adminTab === 'dashboard' ? 'text-red-600' : 'text-gray-500'
              }`}
            >
              <Home size={24} />
              <span className="text-xs mt-1">Dashboard</span>
            </button>
            
            {(currentUser.userType === 'admin') && (
              <button
                onClick={() => setAdminTab('review')}
                className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                  adminTab === 'review' ? 'text-red-600' : 'text-gray-500'
                }`}
              >
                <CheckCircle size={24} />
                <span className="text-xs mt-1">Review</span>
              </button>
            )}
            
            {(currentUser.userType === 'admin') && (
              <button
                onClick={() => setAdminTab('finance')}
                className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                  adminTab === 'finance' ? 'text-red-600' : 'text-gray-500'
                }`}
              >
                <DollarSign size={24} />
                <span className="text-xs mt-1">Finance</span>
              </button>
            )}
            
            <button
              onClick={() => setAdminTab('students')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                adminTab === 'students' ? 'text-red-600' : 'text-gray-500'
              }`}
            >
              <GraduationCap size={24} />
              <span className="text-xs mt-1">Students</span>
            </button>
            
            {(currentUser.userType === 'admin') && (
              <button
                onClick={() => setAdminTab('analytics')}
                className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                  adminTab === 'analytics' ? 'text-red-600' : 'text-gray-500'
                }`}
              >
                <BarChart3 size={24} />
                <span className="text-xs mt-1">Analytics</span>
              </button>
            )}

            <button
              onClick={() => setAdminTab('profile')}
              className={`flex-1 flex flex-col items-center justify-center h-full transition ${
                adminTab === 'profile' ? 'text-red-600' : 'text-gray-500'
              }`}
            >
              <User size={24} />
              <span className="text-xs mt-1">Profile</span>
            </button>
          </div>
        </div>

        {/* ALL MODALS - Keep all existing modals here */}
  {/* COURSE INSPECTION MODAL */}
        {inspectingCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8 overflow-x-hidden">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Course Inspection</h3>
                <button onClick={() => { setInspectingCourse(null); setEditingCommission(false); }} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">

                {/* Price change alert for resubmissions */}
                {inspectingCourse.lastApprovedPrice &&
                  Number(inspectingCourse.lastApprovedPrice) !== Number(inspectingCourse.price) && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <p className="font-semibold text-yellow-800 mb-1">⚠️ Price changed since last approval</p>
                    <p className="text-sm text-yellow-700">
                      ${Number(inspectingCourse.lastApprovedPrice).toFixed(2)} → ${Number(inspectingCourse.price).toFixed(2)} — update store listing
                    </p>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold mb-2">Course Details</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p><strong>Title:</strong> {inspectingCourse.title}</p>
                    <p><strong>Provider:</strong> {inspectingCourse.provider}</p>
                    <p><strong>Profession:</strong> {professions.find(p => p.id === inspectingCourse.profession)?.name || inspectingCourse.profession}</p>
                    <p><strong>Hours:</strong> {inspectingCourse.hours}</p>
                    <p><strong>Price:</strong> ${inspectingCourse.price}</p>
                    {(currentUser.userType === 'admin') && inspectingCourse.status === 'approved' && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <strong>Commission:</strong>
                          {editingCommission ? (
                            <>
                              <input
                                type="number"
                                value={commissionInput}
                                onChange={e => setCommissionInput(e.target.value)}
                                className="border rounded px-2 py-0.5 w-20 text-sm"
                                min="0"
                                max="100"
                              />
                              <span className="text-sm">%</span>
                              <button
                                disabled={savingCommission}
                                onClick={async () => {
                                  setSavingCommission(true);
                                  try {
                                    const newCommission = parseFloat(commissionInput) || 0;
                                    const rate = adminSettings.appStoreCutRate || 15;
                                    const gross = Number(inspectingCourse.price) || 0;
                                    const appCut = gross * (rate / 100);
                                    const net = gross - appCut;
                                    const platFee = net * (newCommission / 100);
                                    const providerEarns = net - platFee;
                                    await window.firebaseDB.updateDoc(`courses/${inspectingCourse.id}`, {
                                      commission: newCommission,
                                      providerEarnings: providerEarns,
                                      platformCommission: platFee,
                                      commissionUpdatedAt: new Date().toISOString()
                                    });
                                    setInspectingCourse(prev => ({ ...prev, commission: newCommission }));
                                    setEditingCommission(false);
                                    showToast('Commission updated.', 'success');
                                  } catch (err) {
                                    showToast('Error saving commission: ' + err.message, 'error');
                                  } finally {
                                    setSavingCommission(false);
                                  }
                                }}
                                className="text-sm bg-purple-600 text-white px-2 py-0.5 rounded hover:bg-purple-700"
                              >
                                {savingCommission ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEditingCommission(false)}
                                className="text-sm text-gray-500 hover:text-gray-700"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <span>{inspectingCourse.commission ?? '—'}%</span>
                              <button
                                onClick={() => {
                                  setCommissionInput(String(inspectingCourse.commission ?? ''));
                                  setEditingCommission(true);
                                }}
                                className="text-sm text-purple-600 hover:text-purple-800 underline ml-1"
                              >
                                Edit
                              </button>
                            </>
                          )}
                        </div>
                        {editingCommission && (() => {
                          const rate = adminSettings.appStoreCutRate || 15;
                          const gross = Number(inspectingCourse.price) || 0;
                          const appCut = gross * (rate / 100);
                          const net = gross - appCut;
                          const comm = Number(commissionInput) || 0;
                          const platFee = net * (comm / 100);
                          const providerGets = net - platFee;
                          return (
                            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm ml-2">
                              <p className="text-gray-600">App store cut: <span className="font-medium text-red-600">{rate}% = -${appCut.toFixed(2)}</span></p>
                              <p className="text-gray-600">Platform fee: <span className="font-medium text-red-600">{comm}% = -${platFee.toFixed(2)}</span></p>
                              <p className="text-gray-700 font-semibold">Provider receives: <span className="text-green-600">${providerGets.toFixed(2)} per sale</span></p>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {/* Featured toggle — Change 4 */}
                    <div className="flex items-center gap-3">
                      <strong>Featured Course:</strong>
                      <button
                        onClick={async () => {
                          const newVal = !inspectingCourse.isFeatured;
                          try {
                            await window.firebaseDB.updateDoc(`courses/${inspectingCourse.id}`, { isFeatured: newVal });
                            setInspectingCourse(prev => ({ ...prev, isFeatured: newVal }));
                            // Also update the courses list so browse tab reflects immediately
                            setCourses(prev => prev.map(c => c.id === inspectingCourse.id ? { ...c, isFeatured: newVal } : c));
                            showToast(newVal ? 'Course marked as featured' : 'Course removed from featured', 'success');
                          } catch (err) {
                            showToast('Error updating featured status: ' + err.message, 'error');
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${inspectingCourse.isFeatured ? 'bg-yellow-400' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${inspectingCourse.isFeatured ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className="text-sm text-gray-500">{inspectingCourse.isFeatured ? 'Featured' : 'Not featured'}</span>
                    </div>
                    <p><strong>Type:</strong> {inspectingCourse.isCEU ? 'CEU Course' : 'Educational Course'}</p>
                    {inspectingCourse.accreditationNumber && (
                      <p><strong>Accreditation:</strong> {inspectingCourse.accreditationNumber}</p>
                    )}
                    {inspectingCourse.courseApprovalNumber && (
                      <p><strong>Course Approval:</strong> {inspectingCourse.courseApprovalNumber}</p>
                    )}
                    {inspectingCourse.courseExpiration && (
                      <p><strong>Approval Expires:</strong> {new Date(inspectingCourse.courseExpiration).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Compliance</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Approval Entries</p>
                      {inspectingCourse.approvalEntries?.length > 0 ? (
                        <div className="space-y-2">
                          {inspectingCourse.approvalEntries.map((entry, i) => {
                            const isExpired = entry.expirationDate && new Date(entry.expirationDate) < new Date();
                            return (
                              <div key={i} className={`rounded-lg p-3 text-sm ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                                <p><strong>Governing Body:</strong> {entry.governingBody || '—'}</p>
                                <p><strong>Approval #:</strong> {entry.approvalNumber || '—'}</p>
                                <p className={isExpired ? 'text-red-600 font-medium' : ''}>
                                  <strong>Expires:</strong> {entry.expirationDate ? new Date(entry.expirationDate).toLocaleDateString() : '—'}
                                  {isExpired && <span className="ml-2 text-xs bg-red-600 text-white px-1.5 py-0.5 rounded">Expired</span>}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No approval information provided</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {inspectingCourse.approvedStates?.length > 0
                          ? `Approved in ${inspectingCourse.approvedStates.length} state${inspectingCourse.approvedStates.length !== 1 ? 's' : ''}`
                          : 'No states declared'}
                      </p>
                      {inspectingCourse.approvedStates?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {inspectingCourse.approvedStates.map(code => (
                            <span key={code} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">{code}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Certificate Settings</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    {inspectingCertSettings === undefined ? (
                      <p className="text-sm text-gray-500 italic">Loading…</p>
                    ) : inspectingCertSettings === null ? (
                      <p className="text-sm text-gray-500 italic">Certificate settings not configured.</p>
                    ) : (
                      <>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Provider Credentials</p>
                          <p className="text-sm">{inspectingCertSettings.providerCredentials || '—'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Expiration</p>
                          <p className="text-sm">
                            {(() => {
                              switch (inspectingCertSettings.expirationType) {
                                case 'type1':
                                  return inspectingCertSettings.expirationFixedDate
                                    ? `Expires on ${new Date(inspectingCertSettings.expirationFixedDate).toLocaleDateString()}`
                                    : 'Expires on a fixed date (not set)';
                                case 'type3':
                                  return 'Expires with provider credential';
                                case 'type4':
                                  return `Expires ${inspectingCertSettings.expirationYears || '—'} years after completion`;
                                case 'type2':
                                default:
                                  return 'No expiration';
                              }
                            })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Signature</p>
                          {inspectingCertSettings.signatureURL ? (
                            <img
                              src={inspectingCertSettings.signatureURL}
                              alt="Provider signature"
                              className="max-w-xs border rounded bg-white"
                            />
                          ) : (
                            <p className="text-sm text-gray-500 italic">No signature uploaded</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm">{inspectingCourse.description}</p>
                  </div>
                </div>
                
                {inspectingCourse.featuredImage && (
                  <div>
                    <h4 className="font-semibold mb-2">Course Banner Image</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <img 
                        src={inspectingCourse.featuredImage} 
                        alt="Course banner" 
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {courseFiles[inspectingCourse.id]?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Course Materials ({courseFiles[inspectingCourse.id].length})</h4>
                    <div className="space-y-2">
                      {courseFiles[inspectingCourse.id].map(file => (
                        <div key={file.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between gap-3 overflow-x-hidden">
                          <div className="flex items-center min-w-0 flex-1">
                            <FileText size={20} className="text-gray-600 mr-3 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm break-words overflow-hidden">{file.fileName}</p>
                              <p className="text-xs text-gray-500">{file.fileSize}</p>
                            </div>
                          </div>
                          <button
  onClick={async () => await Browser.open({ url: file.downloadURL })}
  className="text-blue-600 hover:text-blue-700 touch-target"
>
  <Eye size={20} />
</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {courseVideos[inspectingCourse.id]?.length > 0 && (
  <div>
    <h4 className="font-semibold mb-2">Course Videos ({courseVideos[inspectingCourse.id].length})</h4>
    <div className="space-y-2">
      {courseVideos[inspectingCourse.id].map((video, index) => {
        const isExpanded = inspectingExpandedVideo === video.id;
        
        return (
          <div key={video.id} className="border rounded-lg overflow-hidden">
            {/* Video Header */}
            <button
              onClick={() => setInspectingExpandedVideo(isExpanded ? null : video.id)}
              className="w-full p-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition text-left"
            >
              <div className="flex items-center flex-1 min-w-0">
                <Video size={20} className="text-blue-600 mr-3 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{video.videoName || `Video ${index + 1}`}</p>
                  {video.videoSize && (
                    <p className="text-xs text-gray-500">{video.videoSize}</p>
                  )}
                </div>
              </div>
              <div className="ml-2 flex-shrink-0">
                {isExpanded ? <X size={20} /> : <Play size={20} />}
              </div>
            </button>
            
            {/* Video Player (Expanded) */}
            {isExpanded && (
              <div className="p-4 bg-white">
                {video.videoType === 'youtube' ? (
                  <YouTubePlayer
                    video={video}
                    course={inspectingCourse}
                    onClose={() => setInspectingExpandedVideo(null)}
                  />
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <p>Video type not supported for preview</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}
                
                {courseQuizzes[inspectingCourse.id]?.questions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Course Quiz</h4>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm mb-2">
                        <strong>Questions:</strong> {courseQuizzes[inspectingCourse.id].questions.length}
                      </p>
                      <p className="text-sm mb-3">
                        <strong>Passing Score:</strong> {courseQuizzes[inspectingCourse.id].passingScore}%
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPreviewingQuiz(courseQuizzes[inspectingCourse.id])}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                        >
                          Preview Quiz
                        </button>
                        <button
                          onClick={() => {
                            setTakingQuizAsAdmin(courseQuizzes[inspectingCourse.id]);
                            setAdminQuizAnswers({});
                            setAdminQuizResults(null);
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm"
                        >
                          Test Quiz
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ADMIN COURSE CONTENT EDITOR */}
                {currentUser?.userType === 'admin' && (
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-xl font-bold mb-4">📝 Edit Course Content (Admin)</h3>
                    
                    {/* ENROLLMENT WARNING */}
                    {(() => {
                      const enrolledCount = allEnrollments?.filter(e => e.courseId === inspectingCourse.id)?.length || 0;
                      if (enrolledCount > 0) {
                        return (
                          <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                            <div className="flex items-center">
                              <AlertCircle size={24} className="text-yellow-600 mr-3" />
                              <div>
                                <p className="font-semibold text-yellow-900">
                                  ⚠️ {enrolledCount} student{enrolledCount !== 1 ? 's' : ''} enrolled
                                </p>
                                <p className="text-sm text-yellow-800">
                                  Editing content may affect student progress and completion status
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* YOUTUBE VIDEO SECTION */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold mb-3 flex items-center">
                        <Video size={20} className="mr-2 text-blue-600" />
                        Course Videos
                      </h4>
                      
                      {courseVideos[inspectingCourse.id]?.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {courseVideos[inspectingCourse.id].map(video => (
                            <div key={video.id} className="flex items-center justify-between overflow-x-hidden p-3 bg-white rounded-lg">
                              <div className="flex items-center min-w-0 flex-1">
                                <Video size={20} className="text-blue-600 mr-3" />
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm break-words overflow-hidden">{video.videoName || 'YouTube Video'}</p>
                                  {video.videoType !== 'youtube' && video.videoSize && (
                                    <p className="text-xs text-gray-500">{video.videoSize}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  if (!confirm('Remove this video from the course?')) return;
                                  
                                  if (video.videoType === 'youtube') {
                                    await window.firebaseDB.deleteDoc(`course_videos/${video.id}`);
                                  } else {
                                    await window.firebaseStorage.deleteFile(video.storagePath);
                                    await window.firebaseDB.deleteDoc(`course_videos/${video.id}`);
                                  }
                                  
                                  setCourseVideos(prev => ({
                                    ...prev,
                                    [inspectingCourse.id]: (prev[inspectingCourse.id] || []).filter(v => v.id !== video.id)
                                  }));
                                  
                                  showToast('Video removed successfully', 'success');
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={20} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mb-3">
                        <p className="text-sm text-blue-900 mb-2">
                          <strong>📹 Add Video from YouTube:</strong>
                        </p>
                        <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
                          <li>Upload video to YouTube</li>
                          <li>Set privacy to <strong>"Unlisted"</strong></li>
                          <li>Copy the video URL</li>
                          <li>Paste it below</li>
                        </ol>
                      </div>
                      
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={videoTitle}
                          onChange={(e) => setVideoTitle(e.target.value)}
                          placeholder="Video title (e.g., 'Safety Protocol Overview')"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        />
                        <div className="space-y-3">
  <input
    type="text"
    value={youtubeUrl}
    onChange={(e) => setYoutubeUrl(e.target.value)}
    placeholder="https://youtube.com/watch?v=..."
    className="w-full border rounded-lg px-3 py-2 text-sm"
  />
  <button
                            onClick={() => addYoutubeVideo(inspectingCourse.id)}
                            disabled={!youtubeUrl.trim() || addingYoutubeVideo}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center text-sm"
                          >
                            <Upload size={16} className="mr-2 flex-shrink-0" />
                            <span>{addingYoutubeVideo ? 'Adding...' : 'Add Video'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* MATERIALS SECTION */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="font-semibold mb-3 flex items-center">
                        <FileText size={20} className="mr-2 text-gray-600" />
                        Course Materials
                      </h4>
                      
                      {courseFiles[inspectingCourse.id]?.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {courseFiles[inspectingCourse.id].map(file => (
                            <div key={file.id} className="flex items-center justify-between overflow-x-hidden p-3 bg-white rounded-lg">
                              <div className="flex items-center min-w-0 flex-1">
                                <FileText size={20} className="text-gray-600 mr-3" />
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm break-words overflow-hidden">{file.fileName}</p>
                                  <p className="text-xs text-gray-500">{file.fileSize} • {file.fileType}</p>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  if (!confirm('Delete this file?')) return;
                                  
                                  await window.firebaseStorage.deleteFile(file.storagePath);
                                  await window.firebaseDB.deleteDoc(`course_files/${file.id}`);
                                  
                                  setCourseFiles(prev => ({
                                    ...prev,
                                    [inspectingCourse.id]: (prev[inspectingCourse.id] || []).filter(f => f.id !== file.id)
                                  }));
                                  
                                  showToast('File deleted successfully', 'success');
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={20} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="space-y-3">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileSelect}
                          className="w-full text-sm"
                        />
                        <button
                          onClick={() => uploadFile(inspectingCourse.id)}
                          disabled={!selectedFile || uploading}
                          className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center"
                        >
                          <Upload size={16} className="mr-2" />
                          {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                      </div>
                      
                      {uploading && (
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full transition-all"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 text-center">
                            {uploadProgress.toFixed(0)}%
                          </p>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-2">
                        Supported: PDF, DOC, DOCX (Max 500MB)
                      </p>
                    </div>
                    
                    {/* QUIZ SECTION */}
                    <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold mb-3 flex items-center">
                        <ClipboardList size={20} className="mr-2 text-green-600" />
                        Course Quiz
                      </h4>
                      
                      {courseQuizzes[inspectingCourse.id] ? (
                        <div className="bg-white rounded-lg p-3 mb-3">
                          <p className="text-sm text-gray-700 mb-1">
                            ✓ Quiz configured with {courseQuizzes[inspectingCourse.id].questions.length} questions
                          </p>
                          <p className="text-xs text-gray-500">
                            Passing score: {courseQuizzes[inspectingCourse.id].passingScore}%
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 mb-3">
                          No quiz created yet. Add a quiz to test student knowledge.
                        </p>
                      )}
                      
                      <button 
                        onClick={() => openQuizBuilder(inspectingCourse.id)}
                        className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 flex items-center justify-center"
                      >
                        <Edit size={16} className="mr-2" />
                        {courseQuizzes[inspectingCourse.id] ? 'Edit Quiz' : 'Create Quiz'}
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4 border-t">
                  {inspectingCourse.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          setApprovingCourse(inspectingCourse);
                          setApprovalCommission(20);
                          setShowApprovalModal(true);
                        }}
                        className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700"
                      >
                        Approve Course
                      </button>
                      <button
                        onClick={() => {
                          setRejectingCourse(inspectingCourse);
                          setInspectingCourse(null);
                        }}
                        className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700"
                      >
                        Reject Course
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setInspectingCourse(null)}
                    className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* COURSE APPROVAL MODAL */}
        {showApprovalModal && approvingCourse && (() => {
          const rate = adminSettings.appStoreCutRate || 15;
          const gross = Number(approvingCourse.price) || 0;
          const appCut = gross * (rate / 100);
          const net = gross - appCut;
          const comm = Number(approvalCommission);
          const platFee = net * (comm / 100);
          const providerGets = net - platFee;
          const commissionValid = approvalCommission !== '' && !isNaN(comm) && comm >= 0 && comm <= 100;
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Approve Course</h3>
                  <button onClick={() => { setShowApprovalModal(false); setApprovingCourse(null); }} className="touch-target">
                    <X size={24} />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-1">
                  <p className="font-semibold text-gray-800">{approvingCourse.title}</p>
                  <p className="text-sm text-gray-600">Provider: {approvingCourse.provider}</p>
                  <p className="text-sm text-gray-600">Price: <span className="font-medium">${Number(approvingCourse.price).toFixed(2)}</span></p>
                </div>

                <div className="mb-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Approval Entries</p>
                    {approvingCourse.approvalEntries?.length > 0 ? (
                      <div className="space-y-2">
                        {approvingCourse.approvalEntries.map((entry, i) => {
                          const isExpired = entry.expirationDate && new Date(entry.expirationDate) < new Date();
                          return (
                            <div key={i} className={`rounded-lg p-3 text-sm ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                              <p><strong>Governing Body:</strong> {entry.governingBody || '—'}</p>
                              <p><strong>Approval #:</strong> {entry.approvalNumber || '—'}</p>
                              <p className={isExpired ? 'text-red-600 font-medium' : ''}>
                                <strong>Expires:</strong> {entry.expirationDate ? new Date(entry.expirationDate).toLocaleDateString() : '—'}
                                {isExpired && <span className="ml-2 text-xs bg-red-600 text-white px-1.5 py-0.5 rounded">Expired</span>}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No approval information provided</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {approvingCourse.approvedStates?.length > 0
                        ? `Approved in ${approvingCourse.approvedStates.length} state${approvingCourse.approvedStates.length !== 1 ? 's' : ''}`
                        : 'No states declared'}
                    </p>
                    {approvingCourse.approvedStates?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {approvingCourse.approvedStates.map(code => (
                          <span key={code} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">{code}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 italic border-t pt-2">Verify approval numbers against the governing body's public directory before approving.</p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Platform Commission %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={approvalCommission}
                    onChange={e => setApprovalCommission(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-1 text-sm">
                  <p className="text-gray-600">App store cut: <span className="font-medium text-red-600">{rate}% = -${appCut.toFixed(2)}</span></p>
                  <p className="text-gray-600">Platform fee: <span className="font-medium text-red-600">{comm}% = -${platFee.toFixed(2)}</span></p>
                  <p className="text-gray-800 font-semibold">Provider receives: <span className="text-green-600">${providerGets.toFixed(2)} per sale</span></p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowApprovalModal(false); setApprovingCourse(null); }}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={!commissionValid}
                    onClick={() => {
                      approveCourse(approvingCourse.id, comm);
                      setShowApprovalModal(false);
                      setApprovingCourse(null);
                      setInspectingCourse(null);
                    }}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-300 disabled:text-gray-500"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* QUIZ PREVIEW MODAL */}
        {previewingQuiz && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Quiz Preview</h3>
                <button onClick={() => setPreviewingQuiz(null)} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-6">
                {previewingQuiz.questions.map((question, qIndex) => (
                  <div key={qIndex} className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium mb-3">
                      {qIndex + 1}. {question.question}
                    </p>
                    
                    {question.hasImage && question.imageURL && (
                      <img
                        src={question.imageURL}
                        alt="Question"
                        className="w-full max-w-md mx-auto rounded-lg mb-4"
                      />
                    )}
                    
                    <div className="space-y-2">
                      {question.options.map((option, optionIndex) => (
                        <div
                          key={optionIndex}
                          className={`p-3 rounded-lg border-2 ${
                            optionIndex === question.correctAnswer
                              ? 'border-green-600 bg-green-50'
                              : 'border-gray-300'
                          }`}
                        >
                          {optionIndex === question.correctAnswer && (
                            <CheckCircle size={16} className="inline text-green-600 mr-2" />
                          )}
                          {option}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setPreviewingQuiz(null)}
                className="w-full mt-6 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
              >
                Close Preview
              </button>
            </div>
          </div>
        )}
        
        {/* ADMIN QUIZ TAKING MODAL */}
        {takingQuizAsAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8">
              {!adminQuizResults ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold">Test Quiz</h3>
                    <button onClick={() => {
                      setTakingQuizAsAdmin(null);
                      setAdminQuizAnswers({});
                    }} className="touch-target">
                      <X size={24} />
                    </button>
                  </div>
                  
                  <div className="space-y-6 mb-6">
                    {takingQuizAsAdmin.questions.map((question, qIndex) => (
                      <div key={qIndex} className="bg-gray-50 rounded-lg p-4">
                        <p className="font-medium mb-3">
                          {qIndex + 1}. {question.question}
                        </p>
                        
                        {question.hasImage && question.imageURL && (
                          <img
                            src={question.imageURL}
                            alt="Question"
                            className="w-full max-w-md mx-auto rounded-lg mb-4"
                          />
                        )}
                        
                        <div className="space-y-2">
                          {question.options.map((option, optionIndex) => (
                            <button
                              key={optionIndex}
                              onClick={() => setAdminQuizAnswers({
                                ...adminQuizAnswers,
                                [qIndex]: optionIndex
                              })}
                              className={`w-full text-left p-3 rounded-lg border-2 transition ${
                                adminQuizAnswers[qIndex] === optionIndex
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-300 hover:border-blue-400'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={submitAdminQuiz}
                    disabled={Object.keys(adminQuizAnswers).length !== takingQuizAsAdmin.questions.length}
                    className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
                  >
                    Submit Quiz
                  </button>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    {adminQuizResults.passed ? (
                      <div className="bg-green-50 p-6 rounded-lg">
                        <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-green-800 mb-2">Quiz Passed!</h3>
                        <p className="text-3xl font-bold text-green-600 mb-2">
                          {adminQuizResults.score}%
                        </p>
                        <p className="text-sm text-gray-600">
                          {adminQuizResults.correctCount} correct out of {adminQuizResults.totalQuestions}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-red-50 p-6 rounded-lg">
                        <X size={64} className="text-red-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-red-800 mb-2">Did Not Pass</h3>
                        <p className="text-3xl font-bold text-red-600 mb-2">
                          {adminQuizResults.score}%
                        </p>
                        <p className="text-sm text-gray-600">
                          {adminQuizResults.correctCount} correct out of {adminQuizResults.totalQuestions}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={retakeAdminQuiz}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
                    >
                      Retake Quiz
                    </button>
                    <button
                      onClick={() => {
                        setTakingQuizAsAdmin(null);
                        setAdminQuizAnswers({});
                        setAdminQuizResults(null);
                      }}
                      className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* COURSE REJECTION MODAL */}
        {rejectingCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Reject Course</h3>
                <button onClick={() => {
                  setRejectingCourse(null);
                  setRejectionNotes('');
                }} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <p className="text-gray-700 mb-4">
                Course: <strong>{rejectingCourse.title}</strong>
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Rejection Reason</label>
                <textarea
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 outline-none"
                  rows="4"
                  placeholder="Explain why this course is being rejected..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setRejectingCourse(null);
                    setRejectionNotes('');
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => rejectCourse(rejectingCourse.id, rejectionNotes)}
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* ADMIN SETTINGS MODAL */}
        {showAdminSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8" style={{position: 'relative', zIndex: 9999}}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Platform Settings</h3>
                <button onClick={() => setShowAdminSettings(false)} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3">Notification Settings</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">Admin Email for Notifications</label>
                      <input
                        type="email"
                        value={adminSettings.notificationEmail}
                        onChange={(e) => setAdminSettings({...adminSettings, notificationEmail: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={adminSettings.notifyNewProvider}
                          onChange={(e) => setAdminSettings({...adminSettings, notifyNewProvider: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm">Email when new provider registers</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={adminSettings.notifyNewCEUCourse}
                          onChange={(e) => setAdminSettings({...adminSettings, notifyNewCEUCourse: e.target.checked})}
                          className="mr-2"
                        />
                        <span className="text-sm">Email when CEU course submitted for approval</span>
                      </label>
                      
                    </div>
                  </div>
                </div>
                
                
                {/* Transaction Fee Settings */}
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-semibold mb-3">Transaction Fees</h4>
                  <div>
                    <label className="block text-sm font-medium mb-2">App Store Fee %</label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={adminSettings.appStoreCutRate}
                      onChange={(e) => setAdminSettings({...adminSettings, appStoreCutRate: Number(e.target.value)})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Applied to all new transactions. Existing transactions are unaffected.</p>
                  </div>
                </div>

                {/* Provider Enrollment Settings - Owner Only */}
                {currentUser.isOwner && (
                  <div className="border-t pt-6 mt-6">
                    <h4 className="font-semibold mb-3">Provider Enrollment</h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          checked={adminSettings.providerEnrollmentOpen === true}
                          onChange={() => setAdminSettings({...adminSettings, providerEnrollmentOpen: true})}
                          className="w-4 h-4"
                        />
                        <div>
                          <div className="font-medium">Open Enrollment</div>
                          <div className="text-xs text-gray-500">Anyone can sign up as a course provider</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          checked={adminSettings.providerEnrollmentOpen === false}
                          onChange={() => setAdminSettings({...adminSettings, providerEnrollmentOpen: false})}
                          className="w-4 h-4"
                        />
                        <div>
                          <div className="font-medium">Invitation Only</div>
                          <div className="text-xs text-gray-500">New providers must request an invitation</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowAdminSettings(false);
                    setShowAdminManager(true);
                  }}
                  className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 mb-4"
                  style={{position: 'relative', zIndex: 99999, pointerEvents: 'auto'}}
                >
                  Manage Admin Users
                </button>
                
                <button
                  onClick={async () => {
                    try {
                      await window.firebaseDB.setDoc('admin_settings/main', adminSettings);
                      showToast('Settings saved successfully!', 'success');
                      setShowAdminSettings(false);
                    } catch (error) {
                      showToast('Error saving settings: ' + error.message, 'error');
                    }
                  }}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        )}
               
        {/* Admin Users Management */}
        {showAdminManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold">Admin User Management</h3>
                  <button onClick={() => setShowAdminManager(false)} className="text-gray-500 hover:text-gray-700 touch-target">
                    <X size={24} />
                  </button>
                </div>
                
                {/* Current Admins List */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold mb-4">Current Admins ({allAdmins.length})</h4>
                  <div className="space-y-3">
                    {allAdmins.map(admin => (
                      <div key={admin.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{admin.fullName}</p>
                            <p className="text-sm text-gray-600">{admin.email}</p>
                            {admin.isOwner && (
                              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded mt-1 inline-block">
                                Owner
                              </span>
                            )}
                            {!admin.isOwner && admin.permissions && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {Object.keys(admin.permissions).filter(k => admin.permissions[k]).map(perm => (
                                  <span key={perm} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    {perm}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {!admin.isOwner && currentUser.isOwner && (
                              <button
                                onClick={() => removeAdmin(admin.id, admin.fullName, admin.email)}
                                className="text-red-600 hover:text-red-800 text-sm font-semibold"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Add New Admin */}
                {hasPermission('manageAdmins') && (
                  <div className="border-t pt-6">
                    <h4 className="text-lg font-semibold mb-4">Invite New Admin</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Email *</label>
                        <input
                          type="email"
                          value={newAdminEmail}
                          onChange={(e) => setNewAdminEmail(e.target.value)}
                          placeholder="admin@example.com"
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold mb-2">Full Name *</label>
                        <input
                          type="text"
                          value={newAdminName}
                          onChange={(e) => setNewAdminName(e.target.value)}
                          placeholder="John Smith"
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <p className="text-xs text-gray-500 mb-4">
                          Admins can review courses and providers, manage students, and handle
                          payouts and refunds. Only the owner can manage platform settings and
                          other admins.
                        </p>
                      </div>
                      
                      <button
                        onClick={inviteAdmin}
                        disabled={invitingAdmin}
                        className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {invitingAdmin ? 'Sending Invitation...' : 'Send Invitation'}
                      </button>
                    </div>
                  </div>
                )}
                
                {!hasPermission('manageAdmins') && (
                  <div className="border-t pt-6">
                    <p className="text-sm text-gray-600">
                      You do not have permission to add new admins. Contact the platform owner.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Activity Log Full View */}
        {showActivityLog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold">Activity Log</h3>
                  <div className="flex items-center gap-3">
                    <button onClick={() => loadActivityLog()} className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-300">
                      Refresh
                    </button>
                    <button onClick={() => setShowActivityLog(false)} className="text-gray-500 hover:text-gray-700 touch-target">
                      <X size={24} />
                    </button>
                  </div>
                </div>
                
                {(() => {
                  const filteredLogs = activityLog.filter(() => currentUser.userType === 'admin');

                  return filteredLogs.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No activity to display</p>
                  ) : (
                    <div className="space-y-4">
                      {filteredLogs.map((log, index) => {
                        const isGeneralNote = log.category === 'general';
                        const isSupport = log.category === 'support' && log.action === 'Help request submitted';
                        const isResolved = log.resolved === true;
                        const isExpanded = expandedLogId === (log.id || index);

                        return (
                          <div
                            key={log.id || index}
                            className={`p-4 rounded-lg ${
                              isSupport
                                ? isResolved
                                  ? 'border border-gray-300 bg-gray-50'
                                  : 'border-2 border-orange-400 bg-orange-50'
                                : isGeneralNote
                                  ? 'border-2 border-blue-400 bg-blue-50'
                                  : 'border'
                            }`}
                          >
                            <div
                              className={isSupport ? 'cursor-pointer' : ''}
                              onClick={() => isSupport && setExpandedLogId(isExpanded ? null : (log.id || index))}
                            >
                              <p className={`font-semibold ${isSupport ? (isResolved ? 'text-gray-500' : 'text-orange-800 text-lg') : isGeneralNote ? 'text-lg text-blue-800' : ''}`}>
                                {isSupport ? (isResolved ? '✅ ' : '🆘 ') : isGeneralNote ? '📝 ' : ''}{log.action}
                                {isSupport && <span className="text-xs font-normal ml-2 text-gray-500">{isExpanded ? '▲ collapse' : '▼ expand'}</span>}
                              </p>
                              <p className={`text-gray-600 mt-1 text-sm ${isResolved ? 'line-through' : ''}`}>
                                {log.details}
                              </p>
                            </div>
                            {isSupport && isExpanded && (
                              <div className="mt-3 pt-3 border-t border-orange-200">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{log.fullMessage || log.details}</p>
                                {!isResolved && (
                                  <button
                                    disabled={resolvingLogId === log.id}
                                    onClick={async () => {
                                      setResolvingLogId(log.id);
                                      try {
                                        await window.firebaseFunctions.call('resolveHelpRequest', {
                                          activityLogId: log.id,
                                          helpRequestId: log.helpRequestId || null,
                                          resolvedBy: currentUser.fullName,
                                          resolvedById: currentUser.uid
                                        });
                                        setActivityLog(prev => prev.map(l => l.id === log.id ? { ...l, resolved: true, resolvedBy: currentUser.fullName } : l));
                                        await logActivity('Help request resolved', `Resolved request from ${log.requesterName || 'unknown'} (${log.requesterEmail || ''})`, 'support');
                                        setExpandedLogId(null);
                                      } catch (err) {
                                        console.error('Error resolving help request:', err);
                                        showToast('Failed to resolve help request', 'error');
                                      }
                                      setResolvingLogId(null);
                                    }}
                                    className="mt-2 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                                  >
                                    {resolvingLogId === log.id ? 'Resolving...' : 'Mark Resolved'}
                                  </button>
                                )}
                                {isResolved && <p className="text-xs text-gray-500 mt-2">Resolved by {log.resolvedBy} · {log.resolvedAt ? new Date(log.resolvedAt).toLocaleString() : ''}</p>}
                              </div>
                            )}
                            {log.notes && (
                              <p className="text-sm text-blue-600 mt-2 italic">Note: {log.notes}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              {log.adminName} • {new Date(log.timestamp).toLocaleString()}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* ADD GENERAL NOTE MODAL */}
        {showAddNoteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold">Add General Note</h3>
                  <button onClick={() => {
                    setShowAddNoteModal(false);
                    setGeneralNote('');
                    setGeneralNoteTitle('');
                  }} className="touch-target">
                    <X size={24} />
                  </button>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold mb-2">Title:</label>
                  <input
                    type="text"
                    value={generalNoteTitle}
                    onChange={(e) => setGeneralNoteTitle(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 mb-4"
                    placeholder="e.g., Server Maintenance, Holiday Hours, etc."
                  />
                  
                  <label className="block text-sm font-semibold mb-2">Note:</label>
                  <textarea
                    value={generalNote}
                    onChange={(e) => setGeneralNote(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 h-32"
                    placeholder="Additional details..."
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddNoteModal(false);
                      setGeneralNote('');
                      setGeneralNoteTitle('');
                    }}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!generalNoteTitle.trim()) {
                        showToast('Please enter a title', 'error');
                        return;
                      }
                      
                      try {
                        await logActivity(generalNoteTitle.trim(), generalNote.trim() || 'No additional details', 'general');
                        setShowAddNoteModal(false);
                        setGeneralNote('');
                        setGeneralNoteTitle('');
                        showToast('Note added successfully!', 'success');
                      } catch (error) {
                        showToast('Error adding note: ' + error.message, 'error');
                      }
                    }}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* PROFESSION MANAGER MODAL */}
        {showProfessionManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Manage Professions</h3>
                <button onClick={() => setShowProfessionManager(false)} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {professions.map(profession => (
                  <div key={profession.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg">{profession.name}</h4>
                        <p className="text-sm text-gray-600">Accrediting Body: {profession.accreditingBody}</p>
                        <p className="text-xs text-gray-500 mt-1">Provider Label: {profession.providerNumberLabel}</p>
                        <p className="text-xs text-gray-500">Credit Label: {profession.creditLabel}</p>
                      </div>
                      
                      <button
                        onClick={() => toggleProfessionActive(profession.id, profession.active)}
                        className={`px-4 py-2 rounded-lg font-semibold ${
                          profession.active
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {profession.active ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setShowProfessionManager(false)}
                className="w-full mt-6 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        )}
   {/* ORGANIZATION MANAGER MODAL */}
        {showOrgManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
              
              {!selectedOrg ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold">Manage Organizations</h3>
                    <button onClick={() => setShowOrgManager(false)} className="touch-target">
                      <X size={24} />
                    </button>
                  </div>
                  
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold mb-4">Organizations ({organizations.length})</h4>
                    {organizations.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No organizations yet. Create one below.</p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {organizations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(org => ( 
                          <div 
                            key={org.id} 
                            onClick={() => setSelectedOrg(org)}
                            className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h5 className="font-bold text-lg">{org.name}</h5>
                                {org.contactName && (
                                  <p className="text-sm text-gray-600">Contact: {org.contactName}</p>
                                )}
                                {org.contactEmail && (
                                  <p className="text-sm text-gray-600">Email: {org.contactEmail}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  Created: {new Date(org.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded text-sm font-semibold ${
                                org.status === 'active' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {org.status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="text-lg font-semibold mb-4">Create New Organization</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Organization Name *</label>
                        <input
                          type="text"
                          value={newOrgForm.name}
                          onChange={(e) => setNewOrgForm({...newOrgForm, name: e.target.value})}
                          placeholder="Massage Envy"
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">Contact Name (Optional)</label>
                          <input
                            type="text"
                            value={newOrgForm.contactName}
                            onChange={(e) => setNewOrgForm({...newOrgForm, contactName: e.target.value})}
                            placeholder="John Smith"
                            className="w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-2">Contact Email (Optional)</label>
                          <input
                            type="email"
                            value={newOrgForm.contactEmail}
                            onChange={(e) => setNewOrgForm({...newOrgForm, contactEmail: e.target.value})}
                            placeholder="john@massageenvy.com"
                            className="w-full border rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>
                      
                      <button
                        onClick={createOrganization}
                        disabled={creatingOrg}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-400"
                      >
                        {creatingOrg ? 'Creating...' : 'Create Organization'}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowOrgManager(false)}
                    className="w-full mt-6 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => {
                          setSelectedOrg(null);
                          setOrgDetailTab('codes');
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        ← Back to Organizations
                      </button>
                    </div>
                    <button onClick={() => {
                      setShowOrgManager(false);
                      setSelectedOrg(null);
                      setOrgDetailTab('codes');
                    }} className="touch-target">
                      <X size={24} />
                    </button>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-6">{selectedOrg.name}</h3>
                  
                  <div className="flex gap-2 mb-6 border-b">
                    <button
                      onClick={() => setOrgDetailTab('codes')}
                      className={`px-4 py-2 font-semibold transition-colors ${
                        orgDetailTab === 'codes'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Codes
                    </button>
                    <button
                      onClick={() => setOrgDetailTab('courses')}
                      className={`px-4 py-2 font-semibold transition-colors ${
                        orgDetailTab === 'courses'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Courses
                    </button>
                    <button
                      onClick={() => setOrgDetailTab('settings')}
                      className={`px-4 py-2 font-semibold transition-colors ${
                        orgDetailTab === 'settings'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Settings
                    </button>
                  </div>
                  
                  <div className="min-h-[400px]">
                    {orgDetailTab === 'codes' && (
                      <div>
                        <div className="bg-blue-50 rounded-lg p-4 mb-6">
                          <h4 className="font-semibold mb-2">Code Statistics</h4>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-2xl font-bold text-blue-600">{orgCodes.length}</p>
                              <p className="text-sm text-gray-600">Total Codes</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-green-600">
                                {orgCodes.filter(c => c.usedCount >= c.maxUses).length}
                              </p>
                              <p className="text-sm text-gray-600">Redeemed</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-600">
                                {orgCodes.filter(c => c.usedCount < c.maxUses).length}
                              </p>
                              <p className="text-sm text-gray-600">Available</p>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            setCodeGeneration({
                              ...codeGeneration,
                              organizationId: selectedOrg.id,
                              organizationName: selectedOrg.name
                            });
                            setShowGenerateCodes(true);
                          }}
                          className="w-full mb-6 bg-yellow-600 text-white py-3 rounded-lg hover:bg-yellow-700 font-semibold"
                        >
                          Generate New Codes
                        </button>

                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-4 py-3 border-b">
                            <h4 className="font-semibold">Redemption Codes</h4>
                          </div>

                          {orgCodes.length === 0 ? (
                            <div className="p-4">
                              <p className="text-gray-500 text-center py-8">No codes generated yet.</p>
                            </div>
                          ) : (
                            <div className="divide-y">
                              {(() => {
                                // Group codes by batchPrefix + courseId
                                const groups = {};
                                orgCodes.forEach(code => {
                                  const key = `${code.batchPrefix || 'NO_BATCH'}__${code.courseId}`;
                                  if (!groups[key]) {
                                    groups[key] = {
                                      batchPrefix: code.batchPrefix || 'NO_BATCH',
                                      courseId: code.courseId,
                                      courseTitle: allCourses.find(c => c.id === code.courseId)?.title || code.courseId,
                                      codes: []
                                    };
                                  }
                                  groups[key].codes.push(code);
                                });

                                return Object.entries(groups).map(([key, group]) => {
                                  const totalCodes = group.codes.length;
                                  const redeemedCount = group.codes.filter(c => c.usedCount >= c.maxUses).length;
                                  const isBatchExpanded = !!expandedBatches[key];

                                  return (
                                    <div key={key}>
                                      {/* Batch header row */}
                                      <div
                                        className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                                        onClick={() => setExpandedBatches(prev => ({ ...prev, [key]: !prev[key] }))}
                                      >
                                        <div>
                                          <span className="font-mono font-semibold text-sm">{group.batchPrefix}</span>
                                          <span className="text-gray-400 mx-2">·</span>
                                          <span className="text-sm text-gray-700">{group.courseTitle}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">{totalCodes} codes, {redeemedCount} redeemed</span>
                                          <span className="text-gray-400">{isBatchExpanded ? '▲' : '▼'}</span>
                                        </div>
                                      </div>

                                      {/* Individual codes */}
                                      {isBatchExpanded && (
                                        <div className="divide-y border-t">
                                          {group.codes.map(code => {
                                            const redeemed = code.usedCount >= code.maxUses;
                                            const isExpanded = selectedCode?.id === code.id;
                                            return (
                                              <div key={code.id}>
                                                <div
                                                  className="flex items-center justify-between px-6 py-3 hover:bg-gray-50 cursor-pointer"
                                                  onClick={() => {
                                                    if (isExpanded) {
                                                      setSelectedCode(null);
                                                      setSelectedCodeRedemptions([]);
                                                    } else {
                                                      setSelectedCode(code);
                                                      loadCodeRedemptions(code.id, code.courseId);
                                                    }
                                                  }}
                                                >
                                                  <span className="font-mono text-sm">{code.code}</span>
                                                  <span>{redeemed ? '✅' : '⭕'}</span>
                                                </div>
                                                {isExpanded && (
                                                  <div className="bg-gray-50 px-6 py-3 border-t text-sm">
                                                    {loadingCodeRedemptions ? (
                                                      <p className="text-gray-400 text-xs">Loading...</p>
                                                    ) : selectedCodeRedemptions.length === 0 ? (
                                                      <p className="text-gray-400 text-xs">Not yet redeemed.</p>
                                                    ) : (
                                                      <div className="space-y-2">
                                                        {selectedCodeRedemptions.map(r => (
                                                          <div key={r.id} className="bg-white rounded p-2 border text-xs space-y-1">
                                                            <p className="font-semibold">{r.userName}</p>
                                                            <p className="text-gray-500">{r.userEmail}</p>
                                                            <p className="text-gray-400">Redeemed: {r.redeemedAt ? new Date(r.redeemedAt).toLocaleDateString() : '—'}</p>
                                                            <p className={r.completed ? 'text-green-600' : 'text-orange-500'}>
                                                              Quiz: {r.completed ? `✅ ${new Date(r.completedAt).toLocaleDateString()}` : '⏳ Pending'}
                                                            </p>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {orgDetailTab === 'courses' && (
                      <div>
                        <button
                          onClick={() => {
                            setOrgCourseForm({
                              title: '',
                              description: '',
                              organizationId: selectedOrg.id,
                              hours: 0,
                              isCEU: true
                            });
                            setShowOrgManager(false);
                            setShowCreateOrgCourse(true);
                          }}
                          className="w-full mb-6 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold"
                        >
                          Create Organization Course
                        </button>
                        
                        <div className="space-y-4">
                          <h4 className="font-semibold">
                            Organization Courses ({orgCourses.length})
                          </h4>
                          
                          {orgCourses.length === 0 ? (
                            <div className="border rounded-lg p-8 text-center">
                              <p className="text-gray-500">No courses created yet.</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {orgCourses.map(course => (
                                <div key={course.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                  <div className="flex items-start justify-between mb-2">
                                    <h5 className="font-bold text-lg">{course.title}</h5>
                                    <span className={`px-3 py-1 rounded text-xs font-semibold ${
                                      course.isCEU 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {course.isCEU ? 'CEU' : 'Educational'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">{course.description}</p>
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <span>{course.hours} {course.isCEU ? 'CEU' : ''} Hours</span>
                                    <span>Created: {new Date(course.createdAt).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {orgDetailTab === 'settings' && (
                      <div>
                        <div className="space-y-6">
                          <div>
                            <h4 className="font-semibold mb-4">Organization Information</h4>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-semibold mb-2">Organization Name *</label>
                                <input
                                  type="text"
                                  value={editingOrgForm.name}
                                  onChange={(e) => setEditingOrgForm({...editingOrgForm, name: e.target.value})}
                                  className="w-full border rounded-lg px-3 py-2"
                                />
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-semibold mb-2">Contact Name</label>
                                  <input
                                    type="text"
                                    value={editingOrgForm.contactName}
                                    onChange={(e) => setEditingOrgForm({...editingOrgForm, contactName: e.target.value})}
                                    className="w-full border rounded-lg px-3 py-2"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-semibold mb-2">Contact Email</label>
                                  <input
                                    type="email"
                                    value={editingOrgForm.contactEmail}
                                    onChange={(e) => setEditingOrgForm({...editingOrgForm, contactEmail: e.target.value})}
                                    className="w-full border rounded-lg px-3 py-2"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="border-t pt-6">
                            <h4 className="font-semibold mb-4">Organization Status</h4>
                            <div className="flex items-center gap-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  checked={editingOrgForm.status === 'active'}
                                  onChange={() => setEditingOrgForm({...editingOrgForm, status: 'active'})}
                                  className="mr-2"
                                />
                                <span className="text-sm">Active (codes work, courses accessible)</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  checked={editingOrgForm.status === 'inactive'}
                                  onChange={() => setEditingOrgForm({...editingOrgForm, status: 'inactive'})}
                                  className="mr-2"
                                />
                                <span className="text-sm">Inactive (codes disabled, courses hidden)</span>
                              </label>
                            </div>
                          </div>
                          
                          <div className="border-t pt-6">
                            <h4 className="font-semibold mb-4">Organization Statistics</h4>
                            <div className="grid md:grid-cols-3 gap-4 text-sm">
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-gray-600">Created</p>
                                <p className="font-semibold">{new Date(selectedOrg.createdAt).toLocaleDateString()}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-gray-600">Total Codes</p>
                                <p className="font-semibold">{orgCodes.length}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <p className="text-gray-600">Total Courses</p>
                                <p className="font-semibold">{orgCourses.length}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="border-t pt-6">
                            <button
                              onClick={saveOrgSettings}
                              disabled={savingOrgSettings}
                              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-400"
                            >
                              {savingOrgSettings ? 'Saving...' : 'Save Changes'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              
            </div>
          </div>
        )}

        {/* CREATE ORGANIZATION COURSE MODAL */}
        {showCreateOrgCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Create Organization Course</h3>
                <button onClick={() => setShowCreateOrgCourse(false)} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Course Title *</label>
                  <input
                    type="text"
                    value={orgCourseForm.title}
                    onChange={(e) => setOrgCourseForm({...orgCourseForm, title: e.target.value})}
                    placeholder="Safety Protocols Training"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Description *</label>
                  <textarea
                    value={orgCourseForm.description}
                    onChange={(e) => setOrgCourseForm({...orgCourseForm, description: e.target.value})}
                    placeholder="Comprehensive safety training for all employees..."
                    rows={4}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Organization *</label>
                    <select 
                      value={orgCourseForm.organizationId}
                      onChange={(e) => setOrgCourseForm({...orgCourseForm, organizationId: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select Organization</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Course will only be accessible to this organization</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold mb-2">Course Type *</label>
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          checked={orgCourseForm.isCEU === true}
                          onChange={() => setOrgCourseForm({...orgCourseForm, isCEU: true})}
                          className="mr-2"
                        />
                        <span className="text-sm">CEU Course</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          checked={orgCourseForm.isCEU === false}
                          onChange={() => setOrgCourseForm({...orgCourseForm, isCEU: false})}
                          className="mr-2"
                        />
                        <span className="text-sm">Educational</span>
                      </label>
                    </div>
                    <label className="block text-sm font-semibold mb-2">
                      {orgCourseForm.isCEU ? 'CEU Hours *' : 'Course Hours *'}
                    </label>
                    <input
                      type="number"
                      value={orgCourseForm.hours}
                      onChange={(e) => setOrgCourseForm({...orgCourseForm, hours: e.target.value})}
                      placeholder="3"
                      min="0"
                      step="0.5"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold mb-2">⚠️ Organization Course Settings</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Not visible in public browse</li>
                    <li>• Accessible only via redemption codes</li>
                    <li>• No payment required (B2B arrangement)</li>
                    <li>• Auto-approved (no review needed)</li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateOrgCourse(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createOrgCourse}
                    disabled={creatingOrgCourse}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold disabled:bg-gray-400"
                  >
                    {creatingOrgCourse ? 'Creating...' : 'Create Course'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* GENERATE REDEMPTION CODES MODAL */}
        {showGenerateCodes && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Generate Redemption Codes</h3>
                <button onClick={() => setShowGenerateCodes(false)} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Select Organization Course *</label>
                  <select 
                    value={codeGeneration.courseId}
                    onChange={(e) => setCodeGeneration({...codeGeneration, courseId: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select Course</option>
                    {courses.filter(c => c.organizationId).map(course => {
                      const org = organizations.find(o => o.id === course.organizationId);
                      return (
                        <option key={course.id} value={course.id}>
                          {course.title} ({org?.name})
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Only organization courses are shown</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Batch Prefix (1-6 chars) *</label>
                    <input
                      type="text"
                      value={codeGeneration.prefix}
                      onChange={(e) => setCodeGeneration({...codeGeneration, prefix: e.target.value.toUpperCase()})}
                      placeholder="MENV"
                      maxLength="6"
                      className="w-full border rounded-lg px-3 py-2 uppercase"
                    />
                    <p className="text-xs text-gray-500 mt-1">Example: ME, MENV, SPRING24</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold mb-2">Quantity (1-500) *</label>
                    <input
                      type="number"
                      value={codeGeneration.quantity}
                      onChange={(e) => setCodeGeneration({...codeGeneration, quantity: e.target.value})}
                      placeholder="50"
                      min="1"
                      max="500"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of codes to generate</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Expiration Date *</label>
                  <input
                    type="date"
                    value={codeGeneration.expiresAt}
                    onChange={(e) => setCodeGeneration({...codeGeneration, expiresAt: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Codes will expire after this date</p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">📋 Code Format Preview</p>
                  <p className="text-lg font-mono bg-white px-3 py-2 rounded border">
                    {codeGeneration.prefix ? `${codeGeneration.prefix.toUpperCase()}-` : '[PREFIX]-'}X7K9P2
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    Total length: {(codeGeneration.prefix.length || 0) + 7} characters
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold mb-2">⚙️ Code Settings</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Each code is single-use (1 redemption per code)</li>
                    <li>• Codes are case-insensitive for user input</li>
                    <li>• Excludes confusing characters (0/O, 1/I/l)</li>
                    <li>• Collision-proof generation with 50 attempts</li>
                    <li>• CSV file downloads automatically after generation</li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowGenerateCodes(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={generateRedemptionCodes}
                    disabled={loading}
                    className="flex-1 bg-yellow-600 text-white py-3 rounded-lg hover:bg-yellow-700 font-semibold disabled:bg-gray-400"
                  >
                    {loading ? 'Generating...' : `Generate ${codeGeneration.quantity} Codes`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QUIZ BUILDER MODAL - Same as provider */}
        {showQuizBuilder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[9999] overflow-y-auto pt-8">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Quiz Builder</h3>
                <button onClick={closeQuizBuilder} className="touch-target">
                  <X size={24} />
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-3">Quiz Settings</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Passing Score (%)</label>
                    <input
                      type="number"
                      value={currentQuiz.passingScore}
                      onChange={(e) => setCurrentQuiz({...currentQuiz, passingScore: parseInt(e.target.value)})}
                      className="w-full border rounded-lg px-3 py-2"
                      min="0"
                      max="100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Quiz Format</label>
                    <select
                      value={currentQuiz.quizFormat}
                      onChange={(e) => setCurrentQuiz({...currentQuiz, quizFormat: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="all-on-page">All questions on one page</option>
                      <option value="one-per-screen">One question per screen</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold mb-3">
                  {editingQuestionIndex !== null ? 'Edit Question' : 'Add New Question'}
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Question</label>
                    <textarea
                      value={questionForm.question}
                      onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      rows="2"
                      placeholder="Enter your question..."
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={questionForm.hasImage}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            removeQuestionImage();
                          }
                          setQuestionForm({...questionForm, hasImage: e.target.checked});
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">Add image to question</span>
                    </label>
                  </div>
                  
                  {questionForm.hasImage && (
                    <div>
                      {questionForm.imageURL ? (
                        <div className="relative">
                          <img
                            src={questionForm.imageURL}
                            alt="Question"
                            className="w-full max-w-md rounded-lg"
                          />
                          <button
                            onClick={removeQuestionImage}
                            className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleQuizImageSelect}
                            className="text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">Max 5MB - JPG, PNG, GIF</p>
                        </div>
                      )}
                      
                      {uploadingQuizImage && (
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full transition-all"
                              style={{ width: `${quizImageUploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 text-center">
                            Uploading... {quizImageUploadProgress.toFixed(0)}%
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Answer Options (2-6)</label>
                    <div className="space-y-2">
                      {questionForm.options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={questionForm.correctAnswer === index}
                            onChange={() => setQuestionForm({...questionForm, correctAnswer: index})}
                            className="mt-1"
                          />
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(index, e.target.value)}
                            className="flex-1 border rounded-lg px-3 py-2"
                            placeholder={`Option ${index + 1}`}
                          />
                          {questionForm.options.length > 2 && (
                            <button
                              onClick={() => removeOption(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X size={20} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {questionForm.options.length < 6 && (
                      <button
                        onClick={addOption}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Option
                      </button>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Select the radio button next to the correct answer
                    </p>
                  </div>
                  
                  <button
                    onClick={addOrUpdateQuestion}
                    disabled={uploadingQuizImage}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {editingQuestionIndex !== null ? 'Update Question' : 'Add Question'}
                  </button>
                  
                  {editingQuestionIndex !== null && (
                    <button
                      onClick={() => {
                        setEditingQuestionIndex(null);
                        resetQuestionForm();
                      }}
                      className="w-full bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold mb-3">
                  Questions ({currentQuiz.questions.length})
                </h4>
                
                {currentQuiz.questions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No questions added yet</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {currentQuiz.questions.map((question, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-medium flex-1">
                            {index + 1}. {question.question}
                          </p>
                          <div className="flex gap-2">
                            <button
  onClick={() => editQuestion(index)}
  className="text-blue-600 hover:text-blue-700 touch-target"
>
  <Edit size={18} />
</button>
                            <button
  onClick={() => deleteQuestion(index)}
  className="text-red-600 hover:text-red-700 touch-target"
>
  <Trash2 size={18} />
</button>
                          </div>
                        </div>
                        
                        {question.hasImage && (
                          <p className="text-xs text-gray-500 mb-2">📷 Has image</p>
                        )}
                        
                        <div className="text-sm space-y-1">
                          {question.options.map((option, optIndex) => (
                            <p key={optIndex} className={optIndex === question.correctAnswer ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                              {optIndex === question.correctAnswer ? '✓' : '○'} {option}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={closeQuizBuilder}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={saveQuiz}
                  disabled={loading || currentQuiz.questions.length === 0}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : 'Save Quiz'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SUSPEND PROVIDER MODAL */}
        {showSuspendProviderModal && selectedProvider && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={28} className="text-red-500 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-900">Suspend Provider</h3>
              </div>
              <p className="font-semibold text-gray-800 mb-2">{selectedProvider.fullName}</p>
              <p className="text-sm text-gray-600 mb-4">
                This provider will lose access immediately and be notified by email. Their courses will remain visible until manually unpublished.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for suspension *</label>
                <textarea
                  value={suspendProviderReason}
                  onChange={(e) => setSuspendProviderReason(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 h-24 resize-none"
                  placeholder="Enter reason..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowSuspendProviderModal(false); setSuspendProviderReason(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={suspendProvider}
                  disabled={!suspendProviderReason.trim() || suspendingProvider}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {suspendingProvider ? 'Suspending...' : 'Suspend Provider'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BAN STUDENT MODAL */}
        {showBanStudentModal && selectedStudent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={28} className="text-red-500 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-900">Ban Student</h3>
              </div>
              <p className="font-semibold text-gray-800">{selectedStudent.fullName}</p>
              <p className="text-sm text-gray-500 mb-2">{selectedStudent.email}</p>
              <p className="text-sm text-gray-600 mb-4">This student will lose access immediately.</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for ban *</label>
                <textarea
                  value={banStudentReason}
                  onChange={(e) => setBanStudentReason(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 h-24 resize-none"
                  placeholder="Enter reason..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowBanStudentModal(false); setBanStudentReason(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={banStudent}
                  disabled={!banStudentReason.trim() || banningStudent}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {banningStudent ? 'Banning...' : 'Ban Student'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REINSTATE PROVIDER MODAL */}
        {showReinstateProviderModal && selectedProvider && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={28} className="text-green-500 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-900">Reinstate Provider</h3>
              </div>
              <p className="text-sm text-gray-700 mb-6">
                Reinstate <span className="font-semibold">{selectedProvider.fullName}</span>? They will regain full access immediately.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReinstateProviderModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => { setShowReinstateProviderModal(false); await reinstateProvider(); }}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700"
                >
                  Reinstate Provider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* UNBAN STUDENT MODAL */}
        {showUnbanStudentModal && selectedStudent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <AlertCircle size={28} className="text-green-500 mr-3 flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-900">Unban Student</h3>
              </div>
              <p className="text-sm text-gray-700 mb-6">
                Unban <span className="font-semibold">{selectedStudent.fullName}</span>? They will regain full access immediately.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUnbanStudentModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => { setShowUnbanStudentModal(false); await unbanStudent(); }}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700"
                >
                  Unban Student
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

// Simple wrapper - no error boundary for now
function App() {
  return <CEUPlatform />;
}

export default App;